import React, { useEffect, useState, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { Navigate } from 'react-router-dom';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/Button';
import { useAuth } from '../contexts/AuthContext';
import { toDownloadUrl, isRegenAdmin } from '../lib/utils';
import { regenApi, verticalsApi } from '../lib/api';
import { Loader2, Wand2, AlertTriangle, CheckCircle2, RefreshCw, Sparkles, Clapperboard,
  PencilRuler, Video, GitBranch, Users, BarChart3,
  DollarSign, FlaskConical, Search, MessageSquare, FileText, Building2, Image as ImageIcon,
  ChevronDown, ChevronUp, ChevronLeft, ChevronRight, Film, Zap, Download, Upload, Play, X, Check, Plus, Settings, RotateCw,
  Brain, PenLine, Megaphone, UserCircle, Camera, GraduationCap, MoreVertical, Pencil, Trash2, Mic } from 'lucide-react';
import { PixelOffice } from '../components/PixelOffice';
import { TeamRoomPanel } from '../components/TeamRoomPanel';
import { AutoStudio } from './AutoStudio';
import { OrbitEditor } from './OrbitEditor';
import { CreativeLab } from './CreativeLab';
import { StoryboardImages } from '../components/StoryboardImages';
import { MediaThumb } from '../components/MediaThumb';
import { ThinkingAvatar } from '../components/ThinkingAvatar';
import { CoverflowGallery, type CoverflowItem } from '../components/ui/CoverflowGallery';
import { cached, invalidate, hasCached, peek, put } from '../lib/cache';
import { downscaleImage } from '../lib/downscaleImage';
import { GenerationPreview, type DryRunPreview } from '../components/GenerationPreview';
import { PreflightCard } from '../components/PreflightCard';



const VARIATION_TYPES = [
  'Full Ad', 'Hook Change Only', 'Caption Change Only', 'Reclean/Minor Mod', 'Script',
  'Broll', 'Stock Video', 'Avatar/UGC', 'map + ugc', 'Image', 'Image + Voiceover',
  'Special Request',
];

const PRESERVE_OPTIONS = ['spokesperson', 'voice', 'script', 'style', 'captions'];

// ── Reference tray (videos the user marked "use as reference"), shared across tabs ─────
type Ref = { id: string; filename: string };
const REFS_KEY = 'regen_refs';
function getRefs(): Ref[] { try { return JSON.parse(localStorage.getItem(REFS_KEY) || '[]'); } catch { return []; } }
function setRefs(refs: Ref[]) { localStorage.setItem(REFS_KEY, JSON.stringify(refs)); window.dispatchEvent(new Event('regen-refs')); }
function toggleRef(r: Ref) {
  const cur = getRefs();
  const next = cur.some(x => x.id === r.id) ? cur.filter(x => x.id !== r.id) : [...cur, r];
  setRefs(next); return next;
}
function useRefs(): [Ref[], (r: Ref[]) => void] {
  const [refs, setLocal] = useState<Ref[]>(getRefs());
  useEffect(() => {
    const h = () => setLocal(getRefs());
    window.addEventListener('regen-refs', h); window.addEventListener('storage', h);
    return () => { window.removeEventListener('regen-refs', h); window.removeEventListener('storage', h); };
  }, []);
  return [refs, (r) => { setRefs(r); setLocal(r); }];
}

// Generation models the engine supports (ids MUST match the engine's model registry).
// '' = Auto (engine default routing). Seedance routes via the Kie.ai lane.
// Provider-EXPLICIT ids so the choice is unambiguous (which gateway/credits run).
const GEN_MODELS: { id: string; label: string }[] = [
  { id: '', label: 'Auto (best available for the job)' },
  { id: 'seedance-2', label: 'Seedance 2.0 — reference-to-video (via Kie)' },
  { id: 'veo3-kie', label: 'Veo 3 (via Kie — your Kie credits)' },
  { id: 'veo3-google', label: 'Veo 3 (via Google — Gemini key)' },
  { id: 'higgsfield-v1', label: 'Higgsfield (its own API)' },
  { id: 'runway-gen4', label: 'Runway Gen-4 (via Kie)' },
  { id: 'kling-v3', label: 'Kling v3' },
  { id: 'hailuo-02', label: 'Hailuo 02' },
  { id: 'wan-2.2', label: 'Wan 2.2' },
];

interface Loser {
  creative_id: string; filename: string; spend: number; roas: number;
  hook_rate: number; hold_rate: number; editor_name: string;
  ctr: number | null; cpc: number | null; cpa: number | null; epc: number | null;
  conversions: number | null; clicks: number | null; impressions: number | null; revenue: number | null;
  download_url?: string | null;
  diagnosis: { summary: string; recommended_type: string; lagging_metric: string; segment: string;
    value?: number; target?: number; directive_hint?: string;
    // PHASE 1 smart loser-redraw: judge → suggest → user-decides payload from diagnose().
    suggestion?: {
      lagging: { metric: string; label: string; value: number | null; target: number | null };
      why: string;
      recommended: FixOption;
      alternatives: FixOption[];
    } | null };
}
type FixOption = { type: string; title: string; does: string };

const fmtPct = (v: number | null | undefined) => v == null ? '—' : `${(v * 100).toFixed(1)}%`;
// diagnosis.suggestion values: 'roi' is already a whole-number percent (e.g. -30 → "-30%");
// every other lagging metric (hook_rate/hold_rate/ctr) is a 0-1 fraction.
const fmtSuggestVal = (metric: string, v: number | null | undefined) =>
  v == null ? 'n/a' : metric === 'roi' ? `${v}%` : `${Math.round(v * 100)}%`;
// ROI (not ROAS): profit per $ spent = (revenue - spend)/spend = ROAS - 1, shown as a %
const fmtRoi = (roas: number | null | undefined) => roas == null ? '—' : `${roas >= 1 ? '+' : ''}${((roas - 1) * 100).toFixed(0)}%`;
const fmtUsd = (v: number | null | undefined) => v == null ? '—' : `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
const fmtNum = (v: number | null | undefined) => v == null ? '—' : Number(v).toLocaleString();
// Small, muted timestamp for chat messages + threads — e.g. "Aug 5, 8:56 PM" in the browser locale.
const fmtStamp = (ts?: string | number | null) => {
  if (!ts) return '';
  const d = new Date(ts);
  return isNaN(d.getTime()) ? '' : d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};
const Stamp = ({ at }: { at?: string | number | null }) => {
  const s = fmtStamp(at);
  return s ? <div className="mt-1 px-1 text-[10px] text-gray-500 select-none">{s}</div> : null;
};

// Minimal **bold** renderer for the redraw-chat bubbles (mirrors OrbitModal.renderBold).
function renderBold(text: string): React.ReactNode {
  return String(text).split('**').map((seg, i) => (i % 2 ? <strong key={i}>{seg}</strong> : <span key={i}>{seg}</span>));
}
// One turn of the redraw-chat conversation (same shape as a studio_messages row).
type RedrawMsg = { id: number | string; role: string; kind: string; text: string | null; data?: any; created_at?: string };

function MetricPanel({ l }: { l: Loser }) {
  const lag = l.diagnosis?.lagging_metric;
  const cell = (key: string, label: string, val: string) => (
    <div className={`rounded-lg border p-2 ${lag === key ? 'border-red-400 bg-red-50 dark:bg-red-900/20' : 'border-border'}`}>
      <div className="text-[10px] uppercase tracking-wide text-gray-500 flex items-center gap-0.5">{label}{lag === key && <AlertTriangle className="w-3 h-3 text-red-400" />}</div>
      <div className="text-sm font-semibold text-gray-900 dark:text-white">{val}</div>
    </div>
  );
  return (
    <div className="grid grid-cols-4 gap-2 mb-3">
      {cell('roas', 'ROI', fmtRoi(l.roas))}
      {cell('', 'Spend', fmtUsd(l.spend))}
      {cell('hook_rate', 'Hook', fmtPct(l.hook_rate))}
      {cell('hold_rate', 'Hold', fmtPct(l.hold_rate))}
      {cell('ctr', 'CTR', fmtPct(l.ctr))}
      {cell('', 'CPC', fmtUsd(l.cpc))}
      {cell('', 'CPA', fmtUsd(l.cpa))}
      {cell('', 'Conv', fmtNum(l.conversions))}
    </div>
  );
}

export function VariationStudioPage() {
  const { user } = useAuth();
  const _tab0 = (typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('tab')) as any;
  const [tab, setTab] = useState<'studio' | 'auto' | 'new' | 'create' | 'images' | 'library' | 'requests' | 'results' | 'trees' | 'room' | 'orbit' | 'editor' | 'learning'>(_tab0 || 'studio');

  // Hard gate: the email allow-list OR a per-user 'variation_studio' role (granted from the admin
  // panel) — MUST mirror the backend canUseStudio gate, else a role-granted user is bounced here even
  // though the API would allow them. This is the page-level block (the nav link is gated separately).
  const _canUseStudio = isRegenAdmin(user?.email)
    || (Array.isArray((user as any)?.additional_roles) && (user as any).additional_roles.includes('variation_studio'));
  if (!_canUseStudio) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    // ORBIT theme scope: the whole Variation Studio page adopts the affiliate-engine
    // Apple-dark look via a token swap (see .theme-orbit in index.css). Scoped here so
    // the rest of Creative Library keeps its current theme until we roll this out app-wide.
    <div className="dark theme-orbit" style={{
      backgroundImage: `linear-gradient(rgba(6,6,10,0.55), rgba(6,6,10,0.62)), url(${process.env.PUBLIC_URL}/orbit-herobg.jpg)`,
      backgroundSize: 'cover', backgroundPosition: 'center', backgroundAttachment: 'fixed',
    }}>
    <DashboardLayout flush title="Variation Studio" badge={<span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">admin only</span>}>
      {/* wider work area — max-w-7xl (1280px) left big empty margins on wide monitors ('too compact');
          give the Studio real room to visualise. Still centered + padded on ultra-wide. */}
      {/* Full-height flex column. Studio needs a DEFINITE, viewport-bounded height (h + overflow-hidden)
          so its inner thread (flex-1 min-h-0 overflow-y-auto) scrolls INTERNALLY and the composer pins
          at the bottom — a min-h ancestor grows with content, so the thread never scrolls and the page
          runs off-screen. Other tabs keep min-h so they grow + scroll via DashboardLayout's <main>.
          53px ≈ the slim sticky topbar only (py-2 = 16px + a 36px control + 1px border-b); `flush`
          drops the shell's body padding and the page H1 now lives in that topbar. */}
      <div className={`max-w-[1760px] mx-auto px-2 sm:px-4 flex flex-col ${tab === 'studio' ? 'h-[calc(100vh-53px)] overflow-hidden' : 'min-h-[calc(100vh-53px)]'}`}>
        {/* horizontal-scroll on narrow screens so the 11 tabs never overflow the viewport */}
        <div className="flex flex-nowrap items-stretch gap-1 mb-6 border-b border-border overflow-x-auto whitespace-nowrap scrollbar-thin shrink-0">
          {TABS.map(({ key, label, accent }) => (
            <TabBtn key={key} active={tab === key || (key === 'studio' && (MAKER_TABS as readonly string[]).includes(tab))} accent={accent} onClick={() => setTab(key)}>
              {label}
            </TabBtn>
          ))}
        </div>

        {/* ONE DOOR: under "Studio", the maker modes as chips. Keeps each view's own layout (we only
            switch the tab) so nothing about the child components or their dispatch changes. */}
        {(MAKER_TABS as readonly string[]).includes(tab) && (
          <div className="flex flex-wrap items-center gap-1.5 mb-4 shrink-0">
            <span className="text-[11px] text-gray-500 mr-1">Make a video:</span>
            {MAKER_MODES.map(({ key, label, icon: Icon }) => (
              <button key={key} type="button" onClick={() => setTab(key)}
                className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs border transition ${tab === key ? 'border-primary/60 text-primary bg-primary/10' : 'border-border text-gray-400 hover:text-gray-200 hover:bg-accent'}`}>
                <Icon className="w-3.5 h-3.5" /> {label}
              </button>
            ))}
          </div>
        )}

        {tab === 'images' ? <CreativeLab />
          : tab === 'studio' ? <StudioChat />
          : tab === 'auto' ? <AutoStudio />
          : tab === 'new' ? <NewVariation onSubmitted={() => setTab('requests')} onTree={() => setTab('trees')} />
          : tab === 'create' ? <CreateVideo onSubmitted={() => setTab('requests')} />
          : tab === 'library' ? <CreativeLibrary onRegenerate={(id) => {
              // was a full page reload to ?creative=<id>, which landed on the DEFAULT tab — the
              // redraw-losers view never mounted, so the deep-link was never read and nothing
              // appeared to happen. Set the param, then switch to the tab that consumes it.
              const u = new URL(window.location.href);
              u.searchParams.set('creative', id);
              window.history.replaceState({}, '', u);
              setTab('new');   // 'new' is the redraw-losers view
            }} />
          : tab === 'requests' ? <RequestsList />
          : tab === 'orbit' ? <OrbitGallery />
          : tab === 'editor' ? <OrbitEditor />
          : tab === 'learning' ? <LearningTab />
          : tab === 'trees' ? <TreesList />
          : tab === 'room' ? <CreativeTeamRoom />
          : <ResultsList />}
      </div>
    </DashboardLayout>
    </div>
  );
}

// Color-coded tab bar — one accent per tab so it reads as interactive. Text-only
// at 15px. (P0.4: labels are plain-language; the deeper entry-point merge — folding
// Avtar Studio + Create Video sub-modes into the one Studio composer — is a tracked
// follow-up in ORBIT_UX_OVERHAUL_PLAN.md and is NOT done here.)
// ONE DOOR (P0.4): the maker entries (Studio chat / Avatar / Create Video / Redraw) are no longer
// separate top-level tabs — they live under the single "Studio" door as mode chips (MAKER_MODES below).
// Their `tab` keys + switch cases are KEPT so deep-links (?tab=), onSubmitted/onTree, and the
// Library→Redraw (?creative=) coupling all still work unchanged.
const TABS = [
  { key: 'studio',   label: 'Studio',             accent: '#2997ff' },
  { key: 'images',   label: 'Creative Lab',       accent: '#ff9f0a' },
  { key: 'library',  label: 'Creative Library',   accent: '#30d158' },
  { key: 'requests', label: 'Requests',           accent: '#64d2ff' },
  { key: 'orbit',    label: 'ORBIT Generated',    accent: '#40c8e0' },
  { key: 'editor',   label: 'ORBIT Editor',       accent: '#ff9f0a' },
  { key: 'learning', label: 'Learning',           accent: '#66d19e' },
  { key: 'trees',    label: 'Framework Trees',    accent: '#ffd60a' },
  { key: 'room',     label: 'Creative Team Room', accent: '#5e5ce6' },
  { key: 'results',  label: 'Results',            accent: '#ff6b6b' },
] as const;

// The maker modes shown as chips under the single "Studio" door. Each navigates to the existing
// view via its tab key (components, dispatch and layout unchanged).
const MAKER_MODES = [
  { key: 'studio', label: 'Chat',          icon: MessageSquare },
  { key: 'auto',   label: 'Avatar',        icon: UserCircle },
  { key: 'create', label: 'Create Video',  icon: Clapperboard },
  { key: 'new',    label: 'Redraw Losers', icon: RotateCw },
] as const;
const MAKER_TABS = ['studio', 'auto', 'create', 'new'] as const;

// ── Models manifest (which models made this video) — travels wherever a video is shown ──
// One-line summary for compact cards: "seedance-2 · f5-tts (clone) · veed · whisper+ass".
function modelsLine(models: any): string {
  if (!models || typeof models !== 'object') return '';
  return [models.video, models.voice, models.lipsync, models.captions].filter(Boolean).join(' · ');
}

// Full read-only manifest + collapsible per-model input log, for the ORBIT accordion.
function ModelsPanel({ models, calls }: { models?: any; calls?: any }) {
  const [openCalls, setOpenCalls] = useState(false);
  const m = models && typeof models === 'object' ? models : null;
  const c = Array.isArray(calls) ? calls : [];
  if (!m && !c.length) return null;
  const rows: [string, any][] = m
    ? [['Video', m.video], ['Voice', m.voice], ['Lip-sync', m.lipsync], ['Captions', m.captions], ['Recipe', m.recipe]]
    : [];
  return (
    <div className="mt-2 pt-2 border-t border-border/50 space-y-1">
      {rows.some(([, val]) => val) && (
        <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">Models used</div>
      )}
      {rows.filter(([, val]) => val).map(([label, val]) => (
        <div key={label} className="flex gap-1.5">
          <span className="text-gray-500 shrink-0">{label}:</span>
          <span className="text-gray-300 break-words">
            {String(val)}
            {label === 'Voice' && m?.voice_cloned && (
              <span className="ml-1 inline-flex items-center rounded-full bg-emerald-500/15 px-1.5 py-px text-[9px] font-semibold uppercase tracking-wide text-emerald-300 ring-1 ring-emerald-400/30">cloned</span>
            )}
          </span>
        </div>
      ))}
      {c.length > 0 && (
        <div className="pt-1">
          <button onClick={() => setOpenCalls(o => !o)} className="inline-flex items-center gap-1 text-gray-400 hover:text-gray-200">
            <ChevronDown className={`w-3.5 h-3.5 transition ${openCalls ? 'rotate-180' : ''}`} /> Model inputs ({c.length})
          </button>
          {openCalls && (
            <div className="mt-1 space-y-1.5 max-h-52 overflow-auto">
              {c.map((call: any, i: number) => (
                <div key={i} className="rounded-md bg-black/30 ring-1 ring-border/50 p-1.5">
                  <div className="text-gray-400">
                    <span className="text-gray-300">{call?.stage || '—'}</span>
                    {call?.model && <span className="text-gray-500"> → {call.model}</span>}
                  </div>
                  {call?.input_summary && (
                    <pre className="mt-0.5 whitespace-pre-wrap break-words font-mono text-[10px] text-gray-400">{call.input_summary}</pre>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// SPEED EXPORT — per finished video: pick a fine speed step (0.8–1.5, 0.1 increments; default 1.0) and
// export the re-timed file. Calls the ffmpeg re-time endpoint (setpts + atempo) and downloads the result.
// Cheap post-process only — no re-generation, no model call, no spend.
const SPEED_STEPS = [0.8, 0.9, 1.0, 1.1, 1.2, 1.3, 1.4, 1.5];
function SpeedExport({ variantId, filename }: { variantId: string; filename?: string }) {
  const [speed, setSpeed] = useState(1.0);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const run = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await regenApi.exportSpeed(variantId, speed);
      const url = URL.createObjectURL(r.data as Blob);
      const a = document.createElement('a');
      const base = (filename || `orbit-${variantId}.mp4`).replace(/\.[^.]+$/, '');
      a.href = url; a.download = `${base}__${speed.toFixed(1)}x.mp4`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch { setErr('Export failed'); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-2 flex items-center gap-1.5 flex-wrap">
      <span className="text-[10px] uppercase tracking-wide text-gray-500">Speed</span>
      <select value={speed} onChange={(e) => setSpeed(Number(e.target.value))} disabled={busy}
        className="text-[11px] px-1.5 py-1 rounded-lg bg-black/30 border border-border text-gray-100 focus:outline-none disabled:opacity-50">
        {SPEED_STEPS.map((s) => <option key={s} value={s}>{s.toFixed(1)}x</option>)}
      </select>
      <button type="button" onClick={run} disabled={busy}
        className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-lg border border-primary/40 text-primary hover:bg-primary/10 disabled:opacity-50">
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
        Export at {speed.toFixed(1)}x
      </button>
      {err && <span className="text-[10px] text-red-400">{err}</span>}
    </div>
  );
}

// ORBIT Generated — one gallery of every generation, with the prompt/message that made it
// tucked into an accordion under each card.
// REVIEW-FIRST: a gen in status 'clips_ready' shows its staged clips here — approve to combine, or
// select the bad ones + a reason and regenerate only those (no whole re-render, saves credits).
function ClipReview({ g, onChange }: { g: any; onChange: () => void }) {
  const clips: any[] = ((g.directive && g.directive.review_clips) || []).slice().sort((a: any, b: any) => (a.index || 0) - (b.index || 0));
  const [reasons, setReasons] = useState<Record<number, string>>({});
  const [sel, setSel] = useState<Record<number, boolean>>({});
  const [busy, setBusy] = useState<'' | 'regen' | 'stitch'>('');
  if (!clips.length) return null;
  const anySel = clips.some((c: any) => sel[c.index]);
  const regen = async () => {
    const picks = clips.filter((c: any) => sel[c.index]).map((c: any) => ({ index: c.index, reason: (reasons[c.index] || '').trim() }));
    if (!picks.length) return;
    if (picks.some((p) => !p.reason)) { alert('Add a reason for each selected clip (what to fix).'); return; }
    setBusy('regen');
    try { await regenApi.regenClips(g.id, picks); setSel({}); setReasons({}); onChange(); }
    catch (e: any) { alert('Regenerate failed: ' + (e?.response?.data?.error || e?.message)); }
    finally { setBusy(''); }
  };
  const combine = async () => {
    setBusy('stitch');
    try { await regenApi.stitchClips(g.id); onChange(); }
    catch (e: any) { alert('Combine failed: ' + (e?.response?.data?.error || e?.message)); }
    finally { setBusy(''); }
  };
  const builtCount = clips.filter((c: any) => c.url).length;
  const allBuilt = builtCount === clips.length;
  return (
    <div className="p-3 space-y-3">
      <div className="text-xs text-amber-300 font-medium">
        {builtCount} of {clips.length} clips built — {allBuilt
          ? 'approve to combine, or pick the bad ones + a reason and regenerate just those.'
          : 'build the rest one at a time (say “now clip N”), or regenerate a built one.'}
      </div>
      {/* Responsive grid so clips are large and wrap instead of a cramped tiny row. */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {clips.map((c: any) => {
          const built = !!c.url;
          return (
            <div key={c.index} className={`rounded-xl border-2 ${sel[c.index] ? 'border-primary ring-2 ring-primary/30' : built ? 'border-border' : 'border-dashed border-border/60'} bg-black/40 overflow-hidden flex flex-col`}>
              <div className="relative w-full aspect-[9/16] bg-black/60">
                {built ? (
                  <video src={c.url} className="w-full h-full object-cover" controls preload="metadata" />
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center gap-1 text-center px-2">
                    <div className="text-2xl text-gray-600">+</div>
                    <div className="text-[11px] text-gray-400 leading-tight">Not built yet</div>
                    {c.line ? <div className="text-[10px] text-gray-500 line-clamp-3 mt-1">“{c.line}”</div> : null}
                  </div>
                )}
                <div className={`absolute top-1.5 left-1.5 px-1.5 py-0.5 rounded text-[10px] font-semibold ${built ? 'bg-emerald-500/80 text-white' : 'bg-gray-700/80 text-gray-200'}`}>
                  Clip {c.index}
                </div>
                {built && c.continuity_ok === false && (
                  <div className="absolute top-1.5 right-1.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-500/90 text-black" title={c.continuity_reason || 'Does not continue cleanly from the previous clip'}>
                    ⚠ continuity
                  </div>
                )}
              </div>
              {built && c.continuity_ok === false && (
                <div className="px-2 py-1 text-[10px] text-amber-300 bg-amber-500/10 border-t border-amber-500/20">
                  Doesn’t continue cleanly from clip {c.index - 1}. Select it + “fix continuity” to re-render just this one.
                </div>
              )}
              <label className={`flex items-center gap-1.5 px-2 py-1.5 text-xs cursor-pointer ${built ? 'text-gray-200' : 'text-gray-500 cursor-not-allowed'}`}>
                <input type="checkbox" disabled={!built} checked={!!sel[c.index]} onChange={() => built && setSel((s) => ({ ...s, [c.index]: !s[c.index] }))} />
                {built ? 'Select to fix' : 'Pending'}
              </label>
              {sel[c.index] && built && (
                <input value={reasons[c.index] || ''} onChange={(e) => setReasons((r) => ({ ...r, [c.index]: e.target.value }))}
                  placeholder="what's wrong with this clip?" className="w-full bg-card border-t border-border px-2 py-1.5 text-xs text-gray-200 focus:outline-none" />
              )}
            </div>
          );
        })}
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <button onClick={combine} disabled={!!busy || !allBuilt} title={allBuilt ? '' : 'Build every clip first'}
          className="px-3 py-1.5 rounded-md bg-primary/20 text-primary text-sm font-medium disabled:opacity-50">
          {busy === 'stitch' ? 'Combining…' : 'Combine & render'}</button>
        <button onClick={regen} disabled={!!busy || !anySel} className="px-3 py-1.5 rounded-md border border-border bg-card text-sm font-medium disabled:opacity-50">
          {busy === 'regen' ? 'Regenerating…' : 'Regenerate selected'}</button>
        {!allBuilt && <span className="text-[11px] text-gray-400">Combine unlocks once all {clips.length} clips are built.</span>}
      </div>
    </div>
  );
}

function OrbitGallery() {
  const [gens, setGens] = useState<any[]>(() => peek<any[]>('orbit:gens') || []);
  const [total, setTotal] = useState<number | null>(null);   // #12 true count from the server (uncapped by the display page)
  const [loading, setLoading] = useState(() => !peek<any[]>('orbit:gens'));
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [rawOpen, setRawOpen] = useState<Record<string, boolean>>({});  // per-card "Raw clips (N)" toggle, default collapsed
  const [dateFrom, setDateFrom] = useState('');   // #10 date-range filter (client-side, over the fetched gens)
  const [dateTo, setDateTo] = useState('');
  const [mediaFilter, setMediaFilter] = useState<'all' | 'image' | 'video'>('all');   // All · Images · Videos
  const [uploading, setUploading] = useState(false);           // "Upload video" → /ingest-clip into ORBIT Generated
  const uploadRef = useRef<HTMLInputElement>(null);
  const onUploadVideo = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]; if (file) {
      setUploading(true);
      try { await regenApi.uploadVideo(file, { label: file.name }); await load(); }
      catch (err: any) { alert('Upload failed: ' + (err?.response?.data?.error || err?.message || 'unknown')); }
      finally { setUploading(false); if (uploadRef.current) uploadRef.current.value = ''; }
    }
  };
  const [restitching, setRestitching] = useState<string | null>(null);      // #12 variant id being re-stitched
  const [restitchMsg, setRestitchMsg] = useState<Record<string, string>>({});
  // Per-clip fix: say what's wrong with ONE clip, preview, then re-render just that clip in
  // context (anchored to the previous clip) and re-stitch. Keyed "<variantId>:<clipIndex>".
  const [fixOpen, setFixOpen] = useState<string | null>(null);
  const [fixText, setFixText] = useState<Record<string, string>>({});
  const [fixPreview, setFixPreview] = useState<Record<string, any>>({});
  const [fixBusy, setFixBusy] = useState<string | null>(null);
  const [viewer, setViewer] = useState<any | null>(null);       // gen opened from the coverflow
  const [posters, setPosters] = useState<Record<string, string>>({});
  // Actual $ spent per generation (AE's reconciled cost ledger) — shown as a badge on EVERY card face,
  // so fetched for every generation that produced output (cached; browser caps concurrency naturally).
  // null = fetched but no data; absent = not fetched yet.
  const [costs, setCosts] = useState<Record<string, number | null>>({});
  const costsFetching = useRef<Set<string>>(new Set());
  useEffect(() => {
    gens.filter((g: any) => g?.id && (g.variants || []).length)
      .map((g: any) => g.id as string)
      .forEach((id: string) => {
        if (id in costs || costsFetching.current.has(id)) return;
        costsFetching.current.add(id);
        regenApi.creationCosts(id)
          .then((r: any) => {
            const t = r?.data?.total_usd;
            setCosts((c) => ({ ...c, [id]: typeof t === 'number' ? t : null }));
          })
          .catch(() => setCosts((c) => ({ ...c, [id]: null })))
          .finally(() => costsFetching.current.delete(id));
      });
  }, [gens, costs]);
  // PAGINATION — 20 per page with Prev/Next (was: a small "Load more" at the top, easy to miss). The 12s
  // poll refreshes ONLY page 1 (where in-flight renders land); an older page stays still while you view it.
  const PAGE = 20;
  const [page, setPage] = useState(1);
  const topRef = useRef<HTMLDivElement>(null);
  const keepGen = (g: any) => g.status === 'running' || g.status === 'clips_ready' || !!(g.variants || [])[0]?.play_url;
  useEffect(() => { setPage(1); }, [dateFrom, dateTo]);   // a new date range starts at page 1
  const load = useCallback((isPoll = false) => {
    if (isPoll && (page !== 1 || (typeof document !== 'undefined' && document.hidden))) return;
    regenApi.generations({ limit: PAGE, offset: (page - 1) * PAGE, from: dateFrom || undefined, to: dateTo || undefined }).then(r => {
      // the server is authoritative for the page it returned — replace, never merge stale cards
      const rows = (r.data.data || []).filter(keepGen);
      setGens(rows);
      if (page === 1) put('orbit:gens', rows);
      if (typeof r.data.total === 'number') setTotal(r.data.total);
    })
      .catch(() => {}).finally(() => setLoading(false));
  }, [page, dateFrom, dateTo]);
  useEffect(() => { load(); const t = setInterval(() => load(true), 12000); return () => clearInterval(t); }, [load]);
  const pages = Math.max(1, Math.ceil((total ?? 0) / PAGE));
  const goPage = (n: number) => { setPage(Math.min(pages, Math.max(1, n))); topRef.current?.scrollIntoView({ behavior: 'smooth' }); };
  const pager = pages > 1 && (
    <div className="inline-flex items-center gap-1.5 text-xs">
      <button onClick={() => goPage(page - 1)} disabled={page <= 1}
        className="px-2.5 py-1 rounded-md border border-border bg-card hover:bg-card/70 font-medium disabled:opacity-40">Prev</button>
      <span className="tabular-nums text-gray-400">Page {page} of {pages}</span>
      <button onClick={() => goPage(page + 1)} disabled={page >= pages}
        className="px-2.5 py-1 rounded-md border border-border bg-card hover:bg-card/70 font-medium disabled:opacity-40">Next</button>
    </div>
  );

  const promptOf = (g: any) => g.expectation || g.directive?.assets?.prompt || g.creative_filename || '—';
  const urlIsImg = (u?: string) => !!u && /\.(jpg|jpeg|png|webp|gif)(\?|#|$)/i.test(u);
  const urlIsAudio = (u?: string) => !!u && /\.(mp3|wav|m4a|aac|ogg)(\?|#|$)/i.test(u);
  // Audio-only deliverables ship an .mp3 — detect from the delivered filename / play_url so they render
  // with an <audio controls> player instead of a blank <video>.
  const isAudioGen = (g: any) => {
    const v = (g?.variants || [])[0];
    return urlIsAudio(g?.creative_filename) || urlIsAudio(v?.play_url) || urlIsAudio(v?.download_url);
  };
  // IMAGE vs VIDEO — decide per GENERATION, not per play_url. Most play_urls are extension-less media
  // proxy links, so URL-only sniffing mislabels proxied images as videos (renders a blank <video>).
  // The reliable output-media signal is the file extension of creative_filename (the delivered file),
  // with play_url / download_url as fallbacks. NOTE: recipe ("Image + Voiceover") is intent, not output
  // — those ship an .mp4, so we never key off recipe/model here.
  const isImageGen = (g: any) => {
    const v = (g?.variants || [])[0];
    return urlIsImg(g?.creative_filename) || urlIsImg(v?.play_url) || urlIsImg(v?.download_url);
  };

  // Showcase = the most recent finished generations that actually have a playable output.
  const showcase = gens
    .filter((g) => g.status !== 'running' && g.status !== 'failed' && (g.variants || [])[0]?.play_url)
    .slice(0, 8);

  // Coverflow needs a static poster per card. Images are their own poster; for video we ask the
  // same ffmpeg-frame thumb endpoint MediaThumb uses (cached, so no churn) and fill them in.
  useEffect(() => {
    let alive = true;
    showcase.forEach((g) => {
      const url = (g.variants || [])[0]?.play_url;
      if (!url || isImageGen(g) || posters[g.id]) return;
      const stable = url.split('?')[0].split('#')[0];
      cached(`thumb:${stable}`, 6 * 3600 * 1000, () => regenApi.thumb({ key: stable, url }))
        .then((r: any) => { if (alive && r?.data?.url) setPosters((p) => ({ ...p, [g.id]: r.data.url })); })
        .catch(() => {});
    });
    return () => { alive = false; };
  }, [showcase.map((g) => g.id).join(','), posters]); // eslint-disable-line react-hooks/exhaustive-deps

  const showcaseItems: CoverflowItem[] = showcase.map((g) => {
    const url = (g.variants || [])[0]?.play_url;
    return {
      id: g.id,
      thumbnailUrl: isImageGen(g) ? url : posters[g.id],
      videoUrl: url,
      title: promptOf(g),
      model: g.model || undefined,
    };
  });

  // #10 DATE FILTER — narrow the full grid to a [from, to] day range (inclusive), client-side over the
  // already-fetched generations. Empty inputs ⇒ no filter (the whole gallery, unchanged).
  const _fromT = dateFrom ? new Date(dateFrom + 'T00:00:00').getTime() : null;
  const _toT = dateTo ? new Date(dateTo + 'T23:59:59.999').getTime() : null;
  const dateActive = _fromT != null || _toT != null;
  // The SERVER now filters + paginates the date range (so "Load more" pulls the whole range, not just
  // the first page's in-range slice). `gens` therefore already holds only in-range rows — trust it rather
  // than re-filtering on local-time boundaries, which could drop a row the server included at a day edge.
  const dateFiltered = gens;
  // MEDIA-TYPE filter — client-side over the already-fetched gens. Default 'all'.
  const filtered = mediaFilter === 'all'
    ? dateFiltered
    : dateFiltered.filter((g: any) => isImageGen(g) === (mediaFilter === 'image'));

  // #12 re-stitch a variant's raw clips (drop/reorder) into a NEW variant — no re-render, no spend.
  const doRestitch = async (variantId: string, keepUrls: string[]) => {
    if (keepUrls.length < 1 || restitching) return;
    setRestitching(variantId); setRestitchMsg(m => ({ ...m, [variantId]: '' }));
    try {
      const r = await regenApi.restitchVariant(variantId, keepUrls);
      setRestitchMsg(m => ({ ...m, [variantId]: `Re-stitched ${r.data?.data?.clips ?? keepUrls.length} clips → new video added.` }));
      setTimeout(() => load(), 600);
    } catch (e: any) {
      setRestitchMsg(m => ({ ...m, [variantId]: 'Re-stitch failed — ' + (e?.response?.data?.error || 'try again') }));
    } finally { setRestitching(null); }
  };

  // Preview first — standing rule: nothing paid runs before you've seen the cost.
  const previewClipFix = async (variantId: string, index: number) => {
    const k = `${variantId}:${index}`;
    const instruction = (fixText[k] || '').trim();
    if (!instruction) return;
    setFixBusy(k);
    try {
      const r = await regenApi.refixClip(variantId, index, { instruction, dry_run: true });
      setFixPreview((p: any) => ({ ...p, [k]: r.data }));
    } catch (e: any) {
      setFixPreview((p: any) => ({ ...p, [k]: { error: e?.response?.data?.error || 'preview failed' } }));
    } finally { setFixBusy(null); }
  };

  // Replace ONE clip with your own edit: upload it, swap it into the ordered list, re-stitch.
  // No model involved and nothing spent — this is the "cut it yourself" path.
  const replaceClipWithUpload = async (variantId: string, index: number, clips: any[], file: File) => {
    const k = `${variantId}:${index}`;
    setFixBusy(k);
    try {
      const up = await regenApi.uploadEditedClip(file, file.name);
      const url = up.data?.url;
      if (!url) throw new Error('upload returned no url');
      const ordered = clips.map((c: any) => (Number(c.index) === Number(index) ? url : c.url));
      await regenApi.restitchVariant(variantId, ordered);
      setFixPreview((p: any) => ({ ...p, [k]: { done: true, uploaded: true } }));
      setFixOpen(null);
      load(true);
    } catch (e: any) {
      setFixPreview((p: any) => ({ ...p, [k]: { error: e?.response?.data?.error || e.message || 'upload failed' } }));
    } finally { setFixBusy(null); }
  };

  // Render the replacement clip, then re-stitch the sequence into a NEW variant (original kept).
  const runClipFix = async (variantId: string, index: number) => {
    const k = `${variantId}:${index}`;
    const instruction = (fixText[k] || '').trim();
    if (!instruction) return;
    setFixBusy(k);
    try {
      const r = await regenApi.refixClip(variantId, index, { instruction });
      const urls: string[] = r.data?.restitch_urls || [];
      if (urls.length) await regenApi.restitchVariant(variantId, urls);
      setFixPreview((p: any) => ({ ...p, [k]: { done: true, spent: r.data?.spent_usd, anchored: r.data?.anchored } }));
      setFixOpen(null);
      load(true);
    } catch (e: any) {
      setFixPreview((p: any) => ({ ...p, [k]: { error: e?.response?.data?.error || 'clip fix failed' } }));
    } finally { setFixBusy(null); }
  };

  if (loading && !gens.length) return <Centered><Loader2 className="w-5 h-5 animate-spin" /></Centered>;
  if (!gens.length) return <Centered>Nothing generated yet. Make something in Studio and it lands here.</Centered>;

  return (
    <div>
      {page === 1 && showcaseItems.length > 0 && (
        <div className="mb-8 rounded-3xl border border-violet-500/15 bg-violet-500/[0.04] p-4">
          <div className="flex items-center gap-2 mb-2 px-1">
            <span className="inline-flex items-center rounded-full bg-violet-500/20 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-violet-200 ring-1 ring-violet-400/30">Latest — showcase</span>
            <span className="text-[11px] text-gray-500">newest {showcaseItems.length} · swipe, click to play</span>
          </div>
          <CoverflowGallery items={showcaseItems} onOpen={(it) => setViewer(gens.find((g) => g.id === it.id) || null)} />
        </div>
      )}
      <div ref={topRef} className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="text-sm text-gray-400 flex items-center gap-2">
          {dateActive
            ? <>{total ?? gens.length} generation{(total ?? gens.length) === 1 ? '' : 's'} in this date range.</>
            : <>{total ?? gens.length} generation{(total ?? gens.length) === 1 ? '' : 's'} ORBIT has made.</>}
          {pager}
        </div>
        {/* media-type filter — All · Images · Videos (client-side over the fetched gens) */}
        <div className="inline-flex items-center rounded-md border border-border bg-card p-0.5 text-xs">
          {([['all', 'All'], ['image', 'Images'], ['video', 'Videos']] as const).map(([val, label]) => (
            <button key={val} onClick={() => setMediaFilter(val)}
              className={`rounded px-2.5 py-1 transition ${mediaFilter === val ? 'bg-primary/20 text-primary font-medium' : 'text-gray-400 hover:text-gray-200'}`}>
              {label}
            </button>
          ))}
        </div>
        {/* Upload an external video straight into ORBIT Generated (/ingest-clip) */}
        <div className="flex items-center">
          <input ref={uploadRef} type="file" accept="video/*" hidden onChange={onUploadVideo} />
          <button onClick={() => uploadRef.current?.click()} disabled={uploading}
            className="px-2.5 py-1 rounded-md border border-border bg-card hover:bg-card/70 text-xs font-medium inline-flex items-center gap-1.5 disabled:opacity-60">
            {uploading ? <ThinkingAvatar state="brewing" size={20} /> : <Upload className="w-3.5 h-3.5" />}
            {uploading ? 'Uploading…' : 'Upload video'}
          </button>
        </div>
        {/* #10 date-range filter */}
        <div className="flex items-center gap-1.5 text-xs text-gray-400">
          <span className="hidden sm:inline">From</span>
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
            className="rounded-md bg-card border border-border px-2 py-1 text-gray-200" />
          <span className="hidden sm:inline">to</span>
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
            className="rounded-md bg-card border border-border px-2 py-1 text-gray-200" />
          {dateActive && (
            <button onClick={() => { setDateFrom(''); setDateTo(''); }}
              className="text-gray-400 hover:text-gray-200 inline-flex items-center gap-1"><X className="w-3.5 h-3.5" /> clear</button>
          )}
          <button onClick={() => load()} className="ml-1 hover:text-gray-200 inline-flex items-center gap-1"><RefreshCw className="w-3.5 h-3.5" /> refresh</button>
        </div>
      </div>

      {/* Lightbox — opened from the coverflow. Reuses MediaThumb for playback. */}
      {viewer && (() => {
        const v = (viewer.variants || [])[0];
        const url = v?.play_url;
        return (
          <div className="fixed inset-0 z-[300] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" onClick={() => setViewer(null)}>
            <div className="relative max-h-[90vh] max-w-[92vw]" onClick={(e) => e.stopPropagation()}>
              <button onClick={() => setViewer(null)} className="absolute -top-3 -right-3 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-black/70 text-white ring-1 ring-white/20 hover:bg-black"><X className="w-5 h-5" /></button>
              {isAudioGen(viewer)
                ? <div className="rounded-2xl bg-card border border-border p-8 w-[80vw] max-w-md"><audio src={url} controls className="w-full" /></div>
                : isImageGen(viewer)
                ? <img src={url} alt="" className="max-h-[82vh] max-w-full rounded-2xl object-contain" />
                : <video src={url} controls playsInline className="max-h-[82vh] max-w-full rounded-2xl bg-black" />}
              <div className="mt-3 flex items-center gap-3">
                {url && <a href={toDownloadUrl(v?.download_url || url, v?.filename)} download className="inline-flex items-center gap-1 text-xs text-primary hover:underline"><Download className="w-4 h-4" /> download</a>}
                <span className="text-xs text-gray-400 line-clamp-1">{promptOf(viewer)}</span>
              </div>
            </div>
          </div>
        );
      })()}
      {!filtered.length && (dateActive || mediaFilter !== 'all') && (
        <div className="text-sm text-gray-500 py-8 text-center">
          No {mediaFilter === 'image' ? 'images' : mediaFilter === 'video' ? 'videos' : 'generations'}{dateActive ? ' in this date range' : ''}.
        </div>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-6 gap-4">
        {filtered.map((g: any) => {
          const v = (g.variants || [])[0];
          const url = v?.play_url;
          const isImg = isImageGen(g);
          const isAudio = isAudioGen(g);
          const isOpen = !!open[g.id];
          return (
            <div key={g.id} className="rounded-2xl border border-border bg-card overflow-hidden flex flex-col">
              <div className="relative bg-black aspect-[9/16] flex items-center justify-center">
                {g.status === 'running' ? <span className="text-xs text-gray-400 inline-flex items-center gap-1"><ThinkingAvatar state="brewing" size={20} /> generating…</span>
                  : g.status === 'clips_ready' ? <span className="text-xs text-amber-300 inline-flex flex-col items-center gap-1 px-3 text-center"><Film className="w-7 h-7" /> clips ready — review below</span>
                  : url ? (isAudio ? <div className="w-full px-3 flex flex-col items-center gap-2 text-gray-400"><Mic className="w-8 h-8" /><audio src={url} controls className="w-full" /></div> : isImg ? <img src={url} alt="" className="w-full h-full object-contain" loading="lazy" /> : <MediaThumb url={url} className="w-full h-full object-contain" />)
                  : g.status === 'failed' ? <span className="text-xs text-red-500 inline-flex items-center gap-1 px-3 text-center"><AlertTriangle className="w-4 h-4 shrink-0" /> {g.error || 'failed'}</span>
                  : <span className="text-xs text-gray-500">no output</span>}
                {/* BLOCKED-BUT-VIEWABLE: QA gated it but a render exists — show it, flagged, so it can be diagnosed. */}
                {g.status === 'failed' && url && (
                  <span className="absolute top-2 left-2 inline-flex items-center gap-1 rounded-full bg-amber-500/90 px-2 py-0.5 text-[10px] font-semibold text-black ring-1 ring-amber-300/60 shadow"><AlertTriangle className="w-3 h-3" /> blocked · needs fix</span>
                )}
                {(g.id in costs && costs[g.id] != null) && (
                  <span className="absolute top-2 right-2 inline-flex items-center rounded-full bg-black/70 px-2 py-0.5 text-[10px] font-semibold text-emerald-300 ring-1 ring-white/10 shadow">
                    💰 ${costs[g.id]!.toFixed(2)}
                  </span>
                )}
              </div>
              {g.status === 'clips_ready' && <ClipReview g={g} onChange={() => load()} />}
              <div className="p-2.5">
                <button onClick={() => setOpen(o => ({ ...o, [g.id]: !o[g.id] }))} className="w-full flex items-start gap-1.5 text-left">
                  <ChevronDown className={`w-4 h-4 mt-0.5 shrink-0 text-gray-400 transition ${isOpen ? 'rotate-180' : ''}`} />
                  <span className={`text-xs text-gray-300 flex-1 ${isOpen ? '' : 'line-clamp-2'}`}>{promptOf(g)}</span>
                </button>
                {isOpen && (
                  <div className="mt-2 pl-5 space-y-1.5 text-[11px] text-gray-400">
                    <div>Model: <span className="text-gray-300">{g.model || '—'}</span></div>
                    <div>💰 Spent: <span className="text-gray-300">
                      {!(g.id in costs) ? '…' : (costs[g.id] != null ? `$${costs[g.id]!.toFixed(2)}` : '—')}
                    </span></div>
                    {g.created_at && <div>{new Date(g.created_at).toLocaleString()}</div>}
                    {g.status === 'failed' && url && (
                      <div className="text-amber-500/90 whitespace-pre-wrap break-words"><span className="font-semibold">Blocked by QA — needs fix:</span> {String(g.error || v?.whats_changed || 'quality gate blocked delivery').replace(/\s*\|\s*/g, ' · ')}</div>
                    )}
                    {v?.whats_changed && <div className="text-gray-500">{v.whats_changed}</div>}
                    <ModelsPanel models={v?.models} calls={v?.model_calls} />
                    {url && g.status !== 'running' && (
                      <a href={toDownloadUrl(v?.download_url || url, v?.filename)} download className="inline-flex items-center gap-1 text-primary hover:underline"><Download className="w-3.5 h-3.5" /> download{g.status === 'failed' ? ' to review' : ''}</a>
                    )}
                    {url && !isImg && !isAudio && v?.id && g.status !== 'running' && g.status !== 'failed' && (
                      <SpeedExport variantId={v.id} filename={v?.filename} />
                    )}
                    {!!v?.raw_clips?.length && (
                      <div className="mt-2">
                        <button type="button" onClick={() => setRawOpen(o => ({ ...o, [g.id]: !o[g.id] }))}
                          className="inline-flex items-center gap-1 text-[11px] text-gray-400 hover:text-gray-200">
                          <ChevronDown className={`w-3.5 h-3.5 transition ${rawOpen[g.id] ? 'rotate-180' : ''}`} />
                          Raw clips ({v.raw_clips.length})
                        </button>
                        {rawOpen[g.id] && (() => {
                          const clips = [...v.raw_clips].sort((a: any, b: any) => a.index - b.index);
                          const busy = restitching === v.id;
                          return (
                            <div className="mt-1.5 space-y-1">
                              {clips.map((clip: any) => {
                                const fk = `${v.id}:${clip.index}`;
                                const prev = fixPreview[fk];
                                return (
                                <div key={clip.index} className="rounded-lg bg-black/30 border border-border px-2 py-1 text-gray-300">
                                <div className="flex items-center justify-between gap-2">
                                  <span>Clip {String(clip.index).padStart(2, '0')}</span>
                                  <div className="flex items-center gap-2">
                                    {/* Fix THIS clip in context — keeps clips 1 and 3, re-renders only this
                                        one anchored to the previous clip so it drops back into the sequence */}
                                    <button type="button" disabled={busy}
                                      onClick={() => { setFixOpen(fixOpen === fk ? null : fk); }}
                                      title="Tell it what's wrong with this clip — it re-renders only this clip, continuing from the previous one"
                                      className="text-[10px] text-primary hover:opacity-80 disabled:opacity-40">fix this clip</button>
                                    {/* #12 drop this clip → re-stitch the rest into a new video (no re-render, no spend) */}
                                    {clips.length > 1 && (
                                      <button type="button" disabled={busy}
                                        onClick={() => doRestitch(v.id, clips.filter((c: any) => c.index !== clip.index).map((c: any) => c.play_url))}
                                        title="Drop this clip and re-stitch the rest into a new video (no re-render, no spend)"
                                        className="text-[10px] text-amber-400 hover:text-amber-300 disabled:opacity-40">drop &amp; re-stitch</button>
                                    )}
                                    <a href={toDownloadUrl(clip.play_url, clip.filename)} download className="hover:text-primary"><Download className="w-3.5 h-3.5 shrink-0" /></a>
                                  </div>
                                </div>

                                {fixOpen === fk && (
                                  <div className="mt-1.5 space-y-1.5 border-t border-border/60 pt-1.5">
                                    <input value={fixText[fk] || ''}
                                      onChange={e => { setFixText((t: any) => ({ ...t, [fk]: e.target.value })); setFixPreview((p: any) => ({ ...p, [fk]: null })); }}
                                      placeholder="What's wrong with this clip, and what should it do instead?"
                                      className="w-full rounded border border-border bg-card p-1.5 text-[11px] text-gray-900 dark:text-white" />

                                    {prev?.error && <div className="text-[10px] text-red-500">{prev.error}</div>}
                                    {prev?.done && (
                                      <div className="text-[10px] text-emerald-500">
                                        {prev.uploaded ? 'Your clip replaced it' : `Clip replaced${prev.anchored ? ' (continued from the previous clip)' : ''}`}
                                        {prev.spent != null ? ` · $${Number(prev.spent).toFixed(2)}` : ''} — re-stitched as a new video above.
                                      </div>
                                    )}
                                    {prev?.dry_run && (
                                      <div className="rounded border border-primary/40 bg-primary/5 p-1.5 space-y-1">
                                        <div className="text-[10px] text-gray-400">
                                          Re-renders <strong className="text-gray-200">clip {clip.index} only</strong>
                                          {prev.anchored
                                            ? (prev.anchor_from === 'next clip'
                                                ? ' — matched to the opening frame of the NEXT clip, so the character and setting line up with the rest.'
                                                : ' — continuing from the end of the previous clip, so it fits back between the others.')
                                            : ' — no neighbouring clip to reference, so it renders standalone and the character may not match.'}
                                        </div>
                                        <div className="text-[10px] text-amber-400">Cost ≈ ${Number(prev.projected_usd || 0).toFixed(2)} (this clip only)</div>
                                        {prev.truncated_prompt && (
                                          <div className="text-[10px] text-amber-400">
                                            ⚠ This clip was generated before full prompts were stored, so only part of the
                                            original shot survived and <strong>its spoken line is not recoverable</strong>.
                                            Add the exact words you want in the box above, or the clip may say something else.
                                          </div>
                                        )}
                                        <pre className="whitespace-pre-wrap break-words font-mono text-[9px] text-gray-500 max-h-24 overflow-auto">{prev.prompt_used}</pre>
                                      </div>
                                    )}

                                    <div className="flex items-center gap-2">
                                      {!prev?.dry_run ? (
                                        <button type="button" disabled={fixBusy === fk || !(fixText[fk] || '').trim()}
                                          onClick={() => previewClipFix(v.id, clip.index)}
                                          className="text-[10px] px-2 py-1 rounded border border-border text-gray-300 disabled:opacity-40 inline-flex items-center gap-1">
                                          {fixBusy === fk ? <Loader2 className="w-3 h-3 animate-spin" /> : null} Preview the fix
                                        </button>
                                      ) : (
                                        <button type="button" disabled={fixBusy === fk}
                                          onClick={() => runClipFix(v.id, clip.index)}
                                          className="text-[10px] px-2 py-1 rounded bg-primary text-white disabled:opacity-40 inline-flex items-center gap-1">
                                          {fixBusy === fk ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                                          Re-render this clip (${Number(prev.projected_usd || 0).toFixed(2)})
                                        </button>
                                      )}
                                      <label className="text-[10px] px-2 py-1 rounded border border-border text-gray-300 cursor-pointer hover:bg-white/5"
                                        title="Cut this clip yourself and upload it back in its place — no model, no spend">
                                        Upload my edit
                                        <input type="file" accept="video/*" className="hidden"
                                          onChange={e => {
                                            const f = e.target.files?.[0];
                                            if (f) replaceClipWithUpload(v.id, clip.index, clips, f);
                                            e.currentTarget.value = '';
                                          }} />
                                      </label>
                                      <button type="button" onClick={() => setFixOpen(null)}
                                        className="text-[10px] text-gray-500 hover:text-gray-300">cancel</button>
                                    </div>
                                  </div>
                                )}
                                </div>
                                );
                              })}
                              <div className="flex items-center gap-2 pt-0.5">
                                <button type="button" disabled={busy}
                                  onClick={() => doRestitch(v.id, clips.map((c: any) => c.play_url))}
                                  className="text-[10px] text-gray-400 hover:text-gray-200 disabled:opacity-40 inline-flex items-center gap-1">
                                  {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} re-stitch all
                                </button>
                                {restitchMsg[v.id] && <span className="text-[10px] text-gray-400">{restitchMsg[v.id]}</span>}
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {pager && <div className="mt-6 flex justify-center">{pager}</div>}
    </div>
  );
}

// ── Learning tab: the self-correcting loop made visible + auditable ───────────
// Three stacked sub-views: proposals awaiting admin approval, per-brain status,
// and a reverse-chronological changelog with a decision drill-down.

// Human labels for the engine's "brains" (raw keys → readable). Unknown keys are
// title-cased so a new brain never renders as a raw snake_case string.
const BRAIN_LABELS: Record<string, string> = {
  voice_cast: 'Voice casting',
  voice: 'Voice',
  hook: 'Hook',
  script: 'Script',
  pacing: 'Pacing',
  captions: 'Captions',
  style: 'Style',
  spokesperson: 'Spokesperson',
  music: 'Music',
  cta: 'Call to action',
};
const brainLabel = (b?: string) =>
  (b && BRAIN_LABELS[b]) || (b || '—').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

// Agreement / rate values may arrive as 0–1 fractions or 0–100 percents — normalise.
const fmtAgree = (v: number | null | undefined) =>
  v == null ? '—' : `${(v <= 1 ? v * 100 : v).toFixed(0)}%`;
const fmtRate = (v: number | null | undefined) =>
  v == null ? '—' : `${(v <= 1 ? v * 100 : v).toFixed(0)}%`;

function LearnBadge({ tone, children }: { tone: 'green' | 'amber' | 'blue' | 'gray' | 'red'; children: any }) {
  const map: Record<string, string> = {
    green: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
    amber: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
    blue: 'bg-sky-500/15 text-sky-300 border-sky-500/30',
    gray: 'bg-white/5 text-gray-300 border-white/10',
    red: 'bg-red-500/15 text-red-300 border-red-500/30',
  };
  return <span className={`text-[11px] px-2 py-0.5 rounded-full border ${map[tone]}`}>{children}</span>;
}

function LearningTab() {
  const [view, setView] = useState<'approval' | 'brains' | 'history'>('approval');
  const [toast, setToast] = useState<string | null>(null);
  const showToast = useCallback((m: string) => {
    setToast(m);
    setTimeout(() => setToast(null), 4000);
  }, []);

  return (
    <div>
      <div className="flex flex-wrap items-center gap-1 mb-5">
        {([
          ['approval', 'Needs your approval'],
          ['brains', 'Brains'],
          ['history', 'History / audit'],
        ] as const).map(([k, label]) => (
          <button
            key={k}
            onClick={() => setView(k)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
              view === k
                ? 'bg-emerald-500/15 text-emerald-200 border-emerald-500/40'
                : 'text-gray-400 border-transparent hover:bg-white/5'
            }`}
          >{label}</button>
        ))}
      </div>

      {view === 'approval' ? <LearnProposals showToast={showToast} />
        : view === 'brains' ? <LearnBrains showToast={showToast} />
        : <LearnHistory />}

      {toast && (
        <div className="fixed bottom-6 right-6 z-50 px-4 py-3 rounded-xl border border-emerald-500/40 bg-emerald-500/15 backdrop-blur text-emerald-100 text-sm shadow-lg inline-flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4" /> {toast}
        </div>
      )}
    </div>
  );
}

// ── A. Needs your approval ────────────────────────────────────────────────────
function LearnProposals({ showToast }: { showToast: (m: string) => void }) {
  const [proposals, setProposals] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<Record<string, boolean>>({});
  const [rejecting, setRejecting] = useState<Record<string, string>>({});
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [raw, setRaw] = useState<Record<string, boolean>>({});

  const load = useCallback(() => {
    setLoading(true); setError(null);
    regenApi.learnProposals({ status: 'pending_admin', limit: 100 })
      .then(r => setProposals(r.data.proposals || []))
      .catch(() => setError('Could not load proposals.'))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const approve = (id: string) => {
    setBusy(b => ({ ...b, [id]: true }));
    regenApi.learnApprove(id)
      .then(r => {
        setProposals(ps => ps.filter(p => p.id !== id));
        showToast(`Engine updated: ${brainLabel(r.data.proposal?.brain)}`);
      })
      .catch(err => {
        if (err?.response?.status === 409) { showToast('Already reviewed — refreshing.'); load(); }
        else showToast('Approve failed.');
      })
      .finally(() => setBusy(b => ({ ...b, [id]: false })));
  };
  const reject = (id: string) => {
    const reason = (rejecting[id] || '').trim();
    setBusy(b => ({ ...b, [id]: true }));
    regenApi.learnReject(id, reason)
      .then(() => {
        setProposals(ps => ps.filter(p => p.id !== id));
        setRejecting(r => { const n = { ...r }; delete n[id]; return n; });
        showToast('Proposal rejected.');
      })
      .catch(err => {
        if (err?.response?.status === 409) { showToast('Already reviewed — refreshing.'); load(); }
        else showToast('Reject failed.');
      })
      .finally(() => setBusy(b => ({ ...b, [id]: false })));
  };

  if (loading) return <Centered><Loader2 className="w-6 h-6 animate-spin" /> Loading proposals…</Centered>;
  if (error) return <Centered><AlertTriangle className="w-5 h-5 text-red-400" /> {error} <button onClick={load} className="underline">retry</button></Centered>;
  if (!proposals.length) return <Centered>No pending changes — ORBIT is still gathering proof.</Centered>;

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button onClick={load} className="text-xs text-gray-400 hover:text-gray-200 inline-flex items-center gap-1"><RefreshCw className="w-3.5 h-3.5" /> refresh</button>
      </div>
      {proposals.map((p) => {
        const r = p.evidence?.reasoning || {};
        const options = r.options || {};
        const change = p.evidence?.proposed_change || {};
        const preferred = change.preferred || {};
        const avoided = change.avoided || [];
        const isOpen = !!open[p.id];
        const b = !!busy[p.id];
        return (
          <div key={p.id} className="rounded-2xl border border-border bg-card p-4">
            {/* Header */}
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <Brain className="w-4 h-4 text-emerald-300" />
              <span className="font-semibold text-white">{brainLabel(p.brain)}</span>
              {p.vertical && <LearnBadge tone="gray">{p.vertical}</LearnBadge>}
              <LearnBadge tone="green">{fmtAgree(p.agreement_before)} → {fmtAgree(p.agreement_after)} on held-out cases</LearnBadge>
              {p.created_at && <span className="text-[11px] text-gray-500 ml-auto">{new Date(p.created_at).toLocaleString()}</span>}
            </div>

            {/* What it noticed */}
            {p.evidence?.pattern_summary && (
              <div className="text-sm text-gray-200 mb-3">{p.evidence.pattern_summary}</div>
            )}

            {/* What will change */}
            <div className="grid sm:grid-cols-2 gap-3 mb-3">
              <div className="rounded-lg border border-border p-3">
                <div className="text-[11px] uppercase tracking-wide text-gray-500 mb-1.5">Will prefer</div>
                {Object.keys(preferred).length ? (
                  <div className="flex flex-wrap gap-1.5">
                    {Object.entries(preferred).map(([val, score]: any) => (
                      <span key={val} className="text-xs px-2 py-0.5 rounded bg-emerald-500/10 border border-emerald-500/25 text-emerald-200">
                        {val} <span className="text-emerald-400/70">{typeof score === 'number' ? score.toFixed(2) : String(score)}</span>
                      </span>
                    ))}
                  </div>
                ) : <span className="text-xs text-gray-500">—</span>}
              </div>
              <div className="rounded-lg border border-border p-3">
                <div className="text-[11px] uppercase tracking-wide text-gray-500 mb-1.5">Will avoid</div>
                {avoided.length ? (
                  <div className="flex flex-wrap gap-1.5">
                    {avoided.map((val: any) => (
                      <span key={val} className="text-xs px-2 py-0.5 rounded bg-red-500/10 border border-red-500/25 text-red-200">{val}</span>
                    ))}
                  </div>
                ) : <span className="text-xs text-gray-500">—</span>}
              </div>
            </div>

            {/* Expand: proof + reasoning */}
            <button onClick={() => setOpen(o => ({ ...o, [p.id]: !o[p.id] }))} className="text-xs text-gray-400 hover:text-gray-200 inline-flex items-center gap-1 mb-2">
              <ChevronDown className={`w-4 h-4 transition ${isOpen ? 'rotate-180' : ''}`} /> {isOpen ? 'Hide' : 'Show'} the proof & reasoning
            </button>

            {isOpen && (
              <div className="space-y-4 mb-3">
                {/* Which videos led to this */}
                {!!(p.evidence?.trigger_creatives || []).length && (
                  <div>
                    <div className="text-[11px] uppercase tracking-wide text-gray-500 mb-2">Which videos led to this</div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                      {p.evidence.trigger_creatives.map((tc: any, i: number) => (
                        <div key={i} className="rounded-lg border border-border overflow-hidden bg-black/30 flex flex-col">
                          <div className="aspect-[9/16] bg-black flex items-center justify-center">
                            {tc.thumb_url
                              ? <img src={tc.thumb_url} alt="" className="w-full h-full object-contain" loading="lazy" />
                              : <span className="text-[10px] text-gray-600">no thumb</span>}
                          </div>
                          <div className="p-2 space-y-1">
                            <div className="text-[11px] text-gray-300 truncate" title={tc.creative_ref}>{tc.creative_ref || '—'}</div>
                            <div className="flex flex-wrap gap-1">
                              {tc.option_value && <LearnBadge tone="blue">{tc.option_value}</LearnBadge>}
                              {tc.human_verdict && <LearnBadge tone={tc.human_verdict === 'win' ? 'green' : tc.human_verdict === 'loss' ? 'red' : 'gray'}>{tc.human_verdict}</LearnBadge>}
                              {tc.roi != null && <LearnBadge tone="gray">ROI {fmtRoi(tc.roi)}</LearnBadge>}
                            </div>
                            {tc.feedback && <div className="text-[11px] text-gray-400 line-clamp-3">"{tc.feedback}"</div>}
                            {!!(tc.blamed_brains || []).length && (
                              <div className="text-[10px] text-amber-300/80">blamed: {tc.blamed_brains.map(brainLabel).join(', ')}</div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* How it decided */}
                <div>
                  <div className="text-[11px] uppercase tracking-wide text-gray-500 mb-2">
                    How it decided — trained on {fmtNum(r.train_n)} / held out {fmtNum(r.holdout_n)}
                  </div>
                  {Object.keys(options).length ? (
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="text-gray-500 text-left">
                            <th className="py-1 pr-3 font-medium">Option</th>
                            <th className="py-1 pr-3 font-medium">n</th>
                            <th className="py-1 pr-3 font-medium">wins</th>
                            <th className="py-1 pr-3 font-medium">win rate</th>
                            <th className="py-1 pr-3 font-medium">wilson</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(options).map(([val, o]: any) => (
                            <tr key={val} className="border-t border-border/60 text-gray-300">
                              <td className="py-1 pr-3">{val}</td>
                              <td className="py-1 pr-3">{fmtNum(o?.n)}</td>
                              <td className="py-1 pr-3">{fmtNum(o?.wins)}</td>
                              <td className="py-1 pr-3">{fmtRate(o?.win_rate)}</td>
                              <td className="py-1 pr-3">{fmtRate(o?.wilson)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : <span className="text-xs text-gray-500">no per-option breakdown</span>}
                </div>

                {/* Raw JSON */}
                <div>
                  <button onClick={() => setRaw(x => ({ ...x, [p.id]: !x[p.id] }))} className="text-[11px] text-gray-500 hover:text-gray-300 underline">
                    {raw[p.id] ? 'hide' : 'view'} raw evidence
                  </button>
                  {raw[p.id] && (
                    <pre className="mt-2 p-3 rounded-lg bg-black/40 border border-border text-[10px] text-gray-400 overflow-x-auto max-h-72">{JSON.stringify(p.evidence, null, 2)}</pre>
                  )}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-border">
              <Button size="sm" onClick={() => approve(p.id)} disabled={b}>
                {b ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4 mr-1" />} Approve
              </Button>
              {rejecting[p.id] !== undefined ? (
                <>
                  <input
                    autoFocus
                    value={rejecting[p.id]}
                    onChange={(e) => setRejecting(r => ({ ...r, [p.id]: e.target.value }))}
                    placeholder="reason (optional)"
                    className="flex-1 min-w-[160px] rounded-md border border-border bg-card px-2 h-8 text-sm text-white placeholder:text-gray-500"
                  />
                  <Button size="sm" variant="destructive" onClick={() => reject(p.id)} disabled={b}>
                    {b ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirm reject'}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setRejecting(r => { const n = { ...r }; delete n[p.id]; return n; })} disabled={b}>Cancel</Button>
                </>
              ) : (
                <Button size="sm" variant="outline" onClick={() => setRejecting(r => ({ ...r, [p.id]: '' }))} disabled={b}>
                  <X className="w-4 h-4 mr-1" /> Reject
                </Button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── B. Brains status ──────────────────────────────────────────────────────────
function LearnBrains({ showToast }: { showToast: (m: string) => void }) {
  const [brains, setBrains] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [running, setRunning] = useState(false);

  const load = useCallback(() => {
    setLoading(true); setError(null);
    regenApi.learnBrains()
      .then(r => setBrains(r.data.brains || []))
      .catch(() => setError('Could not load brains.'))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const runTuning = () => {
    setRunning(true);
    regenApi.learnRunTuning()
      .then(() => { showToast('Learning run complete.'); load(); })
      .catch(() => showToast('Learning run failed.'))
      .finally(() => setRunning(false));
  };

  const statusTone = (s: string) => s === 'promoted' ? 'green' : s === 'suggesting' ? 'amber' : 'gray';

  if (loading) return <Centered><Loader2 className="w-6 h-6 animate-spin" /> Loading brains…</Centered>;
  if (error) return <Centered><AlertTriangle className="w-5 h-5 text-red-400" /> {error} <button onClick={load} className="underline">retry</button></Centered>;

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm text-gray-400">{brains.length} brain{brains.length === 1 ? '' : 's'} — proven vs still learning.</div>
        <Button size="sm" variant="outline" onClick={runTuning} disabled={running}>
          {running ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RotateCw className="w-4 h-4 mr-1" />} Run learning now
        </Button>
      </div>
      {!brains.length ? <Centered>No brains yet.</Centered> : (
        <div className="grid sm:grid-cols-2 gap-3">
          {brains.map((br, i) => (
            <div key={i} className="rounded-2xl border border-border bg-card p-4">
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <Brain className="w-4 h-4 text-emerald-300" />
                <span className="font-semibold text-white">{brainLabel(br.brain)}</span>
                {br.vertical && <LearnBadge tone="gray">{br.vertical}</LearnBadge>}
                <LearnBadge tone={statusTone(br.status) as any}>{br.status || '—'}</LearnBadge>
                {br.has_active_rule && <LearnBadge tone="blue">rule live</LearnBadge>}
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div><span className="text-gray-500">Labeled</span> <span className="text-gray-200">{fmtNum(br.labeled_count)}</span></div>
                <div><span className="text-gray-500">Held-out agreement</span> <span className="text-gray-200">{fmtAgree(br.holdout_agreement)}</span></div>
                <div><span className="text-gray-500">Pending</span> <span className="text-gray-200">{fmtNum(br.pending_proposals)}</span></div>
                <div><span className="text-gray-500">Applied</span> <span className="text-gray-200">{fmtNum(br.applied_proposals)}</span></div>
              </div>
              {br.active_rule && (br.active_rule.preferred || br.active_rule.avoided) && (
                <div className="mt-2 text-[11px] text-gray-400">
                  {br.active_rule.preferred && Object.keys(br.active_rule.preferred).length > 0 && (
                    <div>prefers: <span className="text-emerald-300">{Object.keys(br.active_rule.preferred).join(', ')}</span></div>
                  )}
                  {!!(br.active_rule.avoided || []).length && (
                    <div>avoids: <span className="text-red-300">{br.active_rule.avoided.join(', ')}</span></div>
                  )}
                </div>
              )}
              {br.last_analyzed_at && <div className="mt-2 text-[10px] text-gray-500">last analyzed {new Date(br.last_analyzed_at).toLocaleString()}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── C. History / audit ────────────────────────────────────────────────────────
function LearnHistory() {
  const [events, setEvents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [reqId, setReqId] = useState('');
  const [decisions, setDecisions] = useState<any[] | null>(null);
  const [decLoading, setDecLoading] = useState(false);
  const [decError, setDecError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true); setError(null);
    regenApi.learnEvents({ limit: 100 })
      .then(r => setEvents(r.data.events || []))
      .catch(() => setError('Could not load history.'))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const loadDecisions = () => {
    const id = reqId.trim();
    if (!id) return;
    setDecLoading(true); setDecError(null); setDecisions(null);
    regenApi.learnDecisions(id)
      .then(r => setDecisions(r.data.brain_breakdown || []))
      .catch(() => setDecError('No decisions found for that request.'))
      .finally(() => setDecLoading(false));
  };

  return (
    <div className="space-y-6">
      {/* Decision drill-down */}
      <div className="rounded-2xl border border-border bg-card p-4">
        <div className="text-sm font-semibold text-white mb-2">Audit a generation</div>
        <div className="text-xs text-gray-500 mb-2">Paste a request_id to see what each brain chose and whether it was judged correctly.</div>
        <div className="flex gap-2">
          <input
            value={reqId}
            onChange={(e) => setReqId(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && loadDecisions()}
            placeholder="request_id"
            className="flex-1 rounded-md border border-border bg-card px-3 h-9 text-sm text-white placeholder:text-gray-500"
          />
          <Button size="sm" onClick={loadDecisions} disabled={decLoading || !reqId.trim()}>
            {decLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          </Button>
        </div>
        {decError && <div className="mt-2 text-xs text-red-300">{decError}</div>}
        {decisions && (
          decisions.length ? (
            <div className="mt-3 space-y-3">
              {decisions.map((d, i) => (
                <div key={i} className="rounded-lg border border-border p-3">
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <span className="text-xs text-gray-300 truncate" title={d.creative_ref}>{d.creative_ref || '—'}</span>
                    {d.human_verdict && <LearnBadge tone={d.human_verdict === 'win' ? 'green' : d.human_verdict === 'loss' ? 'red' : 'gray'}>{d.human_verdict}</LearnBadge>}
                    {d.roi != null && <LearnBadge tone="gray">ROI {fmtRoi(d.roi)}</LearnBadge>}
                  </div>
                  {d.reason && <div className="text-[11px] text-gray-400 mb-2">"{d.reason}"</div>}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                    {Object.entries(d.brains || {}).map(([bk, bv]: any) => (
                      <div key={bk} className="flex items-center gap-2 text-[11px]">
                        <span className="text-gray-500 w-28 shrink-0">{brainLabel(bk)}</span>
                        <span className="text-gray-300">{bv?.chosen ?? '—'}</span>
                        {bv?.verdict && <LearnBadge tone={bv.verdict === 'win' ? 'green' : bv.verdict === 'loss' ? 'red' : 'gray'}>{bv.verdict}</LearnBadge>}
                        {bv?.blamed && <LearnBadge tone="amber">blamed</LearnBadge>}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : <div className="mt-2 text-xs text-gray-500">No brain breakdown for that request.</div>
        )}
      </div>

      {/* Changelog */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="text-sm font-semibold text-white">Changelog</div>
          <button onClick={load} className="text-xs text-gray-400 hover:text-gray-200 inline-flex items-center gap-1"><RefreshCw className="w-3.5 h-3.5" /> refresh</button>
        </div>
        {loading ? <Centered><Loader2 className="w-6 h-6 animate-spin" /> Loading history…</Centered>
          : error ? <Centered><AlertTriangle className="w-5 h-5 text-red-400" /> {error} <button onClick={load} className="underline">retry</button></Centered>
          : !events.length ? <Centered>No learning events yet.</Centered>
          : (
            <div className="space-y-2">
              {events.map((e, i) => {
                // Honest read-only status: a "proposed" lesson is NOT steering output yet — only an
                // "active" (approved/applied) one is. Approve proposed ones in "Needs your approval".
                const st = String(e.status || e.state || '').toLowerCase();
                const proposed = /propos|pending|await|review/.test(st);
                const active = !st || /active|applied|approve|live/.test(st);
                return (
                <div key={i} className="rounded-lg border border-border bg-card p-3 flex flex-wrap items-center gap-2">
                  <Brain className="w-3.5 h-3.5 text-emerald-300 shrink-0" />
                  <span className="text-sm text-white font-medium">{brainLabel(e.brain)}</span>
                  {proposed ? <LearnBadge tone="amber">proposed · not steering yet</LearnBadge>
                    : active ? <LearnBadge tone="green">active</LearnBadge>
                    : <LearnBadge tone="gray">{st}</LearnBadge>}
                  {e.vertical && <LearnBadge tone="gray">{e.vertical}</LearnBadge>}
                  <span className="text-sm text-gray-300 flex-1 min-w-[120px]">{e.summary || e.detail || '—'}</span>
                  {(e.agreement_before != null || e.agreement_after != null) && (
                    <LearnBadge tone="green">{fmtAgree(e.agreement_before)} → {fmtAgree(e.agreement_after)}</LearnBadge>
                  )}
                  {e.created_at && <span className="text-[11px] text-gray-500">{new Date(e.created_at).toLocaleString()}</span>}
                </div>
                );
              })}
            </div>
          )}
      </div>
    </div>
  );
}

function TabBtn({ active, onClick, accent, children }: any) {
  return (
    <button
      onClick={onClick}
      style={{ fontSize: 15, ['--tab-accent' as any]: accent, ...(active ? { color: accent, borderColor: accent } : {}) }}
      className={`orbit-tab px-2 py-2 font-medium -mb-px border-b-2 inline-flex items-center whitespace-nowrap rounded-t-md transition-colors ${
        active ? 'bg-white/5' : 'border-transparent text-gray-300'
      }`}
    >{children}</button>
  );
}

// ── New Variation flow ────────────────────────────────────────────────────────
function NewVariation({ onSubmitted, onTree }: { onSubmitted: () => void; onTree: () => void }) {
  const [losers, setLosers] = useState<Loser[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Loser | null>(null);
  const [expectation, setExpectation] = useState('');
  const [variationType, setVariationType] = useState<string>('');
  const [model, setModel] = useState<string>('');
  const [variantCount, setVariantCount] = useState(3);
  const [directive, setDirective] = useState<any>(null);
  const [preserve, setPreserve] = useState<string[]>([]);
  const [interpreting, setInterpreting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');
  const [truth, setTruth] = useState<{ source?: string; synced_at?: string }>({});
  const [refreshing, setRefreshing] = useState(false);
  const [refs, setRefsState] = useRefs();
  // PREVIEW-FIRST gate: "Generate variants →" fires a dry-run first and shows what it produced
  // (rewritten copy / clip plan / projected cost) before the real, spending request goes out.
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState<DryRunPreview | null>(null);
  const [previewErr, setPreviewErr] = useState('');

  // ── Loser-redraw conversational gate (Phases 2-4) ────────────────────────────────────────────
  // A Studio-style read → cross-check → correct loop, layered ABOVE the Phase-1 suggestion card
  // (mirrors OrbitModal's conv panel 1:1, retargeted at thread `redraw:<creative_id>`). The Phase-1
  // card + preview-first + cost-gate above stay fully intact/untouched — this is an additive layer.
  const [redrawConv, setRedrawConv] = useState<RedrawMsg[]>([]);
  const [redrawSending, setRedrawSending] = useState(false);
  const [redrawCorr, setRedrawCorr] = useState('');
  const [redrawScript, setRedrawScript] = useState('');   // paste box for "new character + winning script"
  const [redrawApproved, setRedrawApproved] = useState(false);
  const redrawSeeded = useRef<string | null>(null);   // last creative_id seeded — re-picking a loser re-seeds

  // Post one turn to the redraw-chat thread, then reload it. Never generates until a confirm locks it.
  const sendRedraw = async (opts: { message?: string; confirm?: boolean }) => {
    if (!selected) return;
    setRedrawSending(true);
    try {
      const r = await regenApi.redrawChat(selected.creative_id, opts);
      if (r.data?.approved) setRedrawApproved(true);
      const t = await regenApi.studioThread(`redraw:${selected.creative_id}`);
      setRedrawConv((t.data?.messages || []) as RedrawMsg[]);
    } catch (_) { /* conversation is advisory — a failure never blocks the existing flow below */ }
    finally { setRedrawSending(false); }
  };
  const confirmRedraw = () => sendRedraw({ confirm: true });
  const sendRedrawCorrection = (text: string) => {
    const t = text.trim();
    if (!t) return Promise.resolve();
    setRedrawCorr('');
    return sendRedraw({ message: t });
  };

  // Seed the redraw conversation once per picked loser (guarded so re-renders never re-seed).
  useEffect(() => {
    if (!selected) return;
    if (redrawSeeded.current === selected.creative_id) return;
    redrawSeeded.current = selected.creative_id;
    setRedrawConv([]); setRedrawApproved(false); setRedrawScript(''); setRedrawCorr('');
    sendRedraw({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  useEffect(() => {
    // top-50 losers, cached 1h → no recompute / slowdown on every tab visit.
    // Only spin on a cold cache so re-opening the tab doesn't flash a full re-load.
    setLoading(!hasCached('losers:redraw'));
    cached('losers:redraw', 3600000, () => regenApi.getLosers({ minSpend: 500, maxRoas: 1.0, limit: 50 }))
      .then(r => {
        setTruth({ source: r.data.data.source, synced_at: r.data.data.synced_at });
        const list: Loser[] = r.data.data.losers || [];
        // Deep-link: /variation-studio?creative=<id> (from a "Regenerate" button)
        const cid = new URLSearchParams(window.location.search).get('creative');
        if (cid) {
          regenApi.getCreative(cid)
            .then(cr => {
              const c: Loser = cr.data.data;
              setLosers([c, ...list.filter(l => l.creative_id !== c.creative_id)]);
              pick(c);
            })
            .catch(() => setLosers(list));
        } else {
          setLosers(list);
        }
      })
      .catch(e => setErr(e?.response?.data?.error || 'Failed to load losers'))
      .finally(() => setLoading(false));
  }, []);

  const pick = (l: Loser) => {
    setSelected(l);
    setVariationType(l.diagnosis?.recommended_type || 'Hook Change Only');
    setDirective(null);
  };

  const interpret = async () => {
    if (!selected) return;
    setInterpreting(true); setErr('');
    try {
      const r = await regenApi.interpret({
        creative_id: selected.creative_id, expectation, variation_type: variationType || null,
      });
      const d = r.data.data;
      setDirective(d);
      if (d?.chosen_variation_type) setVariationType(d.chosen_variation_type);
      if (Array.isArray(d?.preserve)) setPreserve(d.preserve);
      if (d?.variant_count) setVariantCount(d.variant_count);
    } catch (e: any) {
      setErr(e?.response?.data?.error || 'Interpreter unavailable');
    } finally { setInterpreting(false); }
  };

  const submit = async () => {
    if (!selected) return;
    setSubmitting(true); setErr('');
    try {
      const refIds = getRefs().map(r => r.id).filter(id => id !== selected.creative_id);
      await regenApi.createRequest({
        creative_id: selected.creative_id, variation_type: variationType,
        expectation, directive, preserve, variant_count: variantCount,
        model: model || undefined,
        reference_creative_ids: refIds.length ? refIds : undefined,
      });
      setPreviewOpen(false);
      onSubmitted();
    } catch (e: any) {
      setErr(e?.response?.data?.error || 'Failed to create request');
    } finally { setSubmitting(false); }
  };

  // Fire a DRY-RUN of the exact same request (directive.assets.dry_run=true), poll the request
  // until AE lands the preview (status 'dry_run') or gives up (status 'failed'), then show it.
  // Nothing spends here — the real request only goes out if the user confirms in the preview.
  const openPreview = async () => {
    if (!selected) return;
    setPreviewErr(''); setPreviewData(null); setPreviewLoading(true); setPreviewOpen(true);
    try {
      const refIds = getRefs().map(r => r.id).filter(id => id !== selected.creative_id);
      const dryDirective = { ...(directive || {}), assets: { ...((directive && directive.assets) || {}), dry_run: true } };
      const r = await regenApi.createRequest({
        creative_id: selected.creative_id, variation_type: variationType,
        expectation, directive: dryDirective, preserve, variant_count: variantCount,
        model: model || undefined,
        reference_creative_ids: refIds.length ? refIds : undefined,
      });
      const rid = r.data.data.id;
      let data = r.data.data;
      for (let i = 0; i < 75 && data.status !== 'dry_run' && data.status !== 'failed'; i++) {
        await new Promise(res => setTimeout(res, 2000));
        const g = await regenApi.getRequest(rid);
        data = g.data.data;
      }
      if (data.status === 'dry_run') {
        setPreviewData(data.directive?.dry_run_preview || {});
      } else if (data.status === 'failed') {
        setPreviewErr(data.error || 'Preview failed — nothing was generated.');
      } else {
        setPreviewErr('Preview is taking longer than expected — try again in a moment.');
      }
    } catch (e: any) {
      setPreviewErr(e?.response?.data?.error || 'Could not build a preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  const generateTree = async () => {
    if (!selected) return;
    setSubmitting(true); setErr('');
    try {
      await regenApi.createTree({ creative_id: selected.creative_id, model: model || undefined });
      onTree();
    } catch (e: any) {
      setErr(e?.response?.data?.error || 'Failed to generate tree');
    } finally { setSubmitting(false); }
  };

  if (loading) return <Centered><Loader2 className="w-6 h-6 animate-spin" /> Loading losing creatives…</Centered>;

  return (
    <div>
    {!!refs.length && (
      <div className="mb-4 p-2 rounded-lg border border-primary/30 bg-primary/5 flex items-center gap-2 flex-wrap">
        <span className="text-xs font-medium text-primary"><Clapperboard className="inline w-3.5 h-3.5 mr-1 align-[-2px]" /> References ({refs.length}):</span>
        {refs.map(r => (
          <span key={r.id} className="text-[11px] bg-white dark:bg-gray-800 border border-border rounded-full px-2 py-0.5 flex items-center gap-1">
            {r.filename}
            <button onClick={() => setRefsState(refs.filter(x => x.id !== r.id))} className="text-gray-400 hover:text-red-500">×</button>
          </span>
        ))}
        <button onClick={() => setRefsState([])} className="text-[11px] text-gray-400 underline ml-auto">clear all</button>
      </div>
    )}
    <div className="grid md:grid-cols-2 gap-6 redraw-tune">
      {/* left: pick a loser */}
      <div>
        <h2 className="text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">1 · Pick a losing creative</h2>
        <div className="flex items-center justify-between text-[11px] text-gray-400 mb-2">
          <span>Source: {truth.source || '—'}{truth.synced_at ? ` · synced ${new Date(truth.synced_at).toLocaleString()}` : ''}</span>
          <button
            onClick={() => { setRefreshing(true); invalidate('losers'); regenApi.refreshTruth().then(() => regenApi.getLosers({ minSpend: 500, maxRoas: 1.0, limit: 50 })).then(r => { setTruth({ source: r.data.data.source, synced_at: r.data.data.synced_at }); setLosers(r.data.data.losers || []); }).finally(() => setRefreshing(false)); }}
            disabled={refreshing} className="underline hover:text-gray-600 disabled:opacity-50">
            {refreshing ? 'syncing…' : 'refresh truth'}
          </button>
        </div>
        <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-1">
          {losers.map(l => (
            <button key={l.creative_id} onClick={() => pick(l)}
              className={`w-full text-left p-3 rounded-lg border ${
                selected?.creative_id === l.creative_id ? 'border-primary bg-accent' : 'border-border hover:bg-accent/50'
              }`}>
              <div className="flex justify-between items-start gap-2">
                <span className="font-medium text-sm truncate text-gray-900 dark:text-white">{l.filename}</span>
                <span className="text-xs font-semibold text-red-600 whitespace-nowrap">ROI {fmtRoi(l.roas)}</span>
              </div>
              <div className="text-xs text-gray-500 mt-1">
                ${l.spend} · hook {pctOf(l.hook_rate)} · hold {pctOf(l.hold_rate)} · {l.editor_name}
              </div>
            </button>
          ))}
          {!losers.length && <p className="text-sm text-gray-500">No losing video creatives found.</p>}
        </div>
      </div>

      {/* right: request */}
      <div>
        {!selected ? (
          <Centered>Select a creative to begin.</Centered>
        ) : (
          <div className="space-y-4">
            {/* The actual losing creative — watch what underperformed before you redraw it */}
            {selected.download_url && (
              <div className="flex gap-3">
                <div className="w-40 shrink-0 aspect-[9/16] rounded-lg overflow-hidden bg-black border border-border">
                  <MediaThumb url={selected.download_url} className="w-full h-full object-cover" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-gray-900 dark:text-white truncate">{selected.filename}</div>
                  <div className="text-xs text-red-500 font-semibold mt-0.5">ROI {fmtRoi(selected.roas)} · why it's a loser</div>
                  <p className="text-xs text-gray-500 mt-1">{selected.diagnosis?.summary}</p>
                  <a href={toDownloadUrl(selected.download_url, selected.filename)} download
                    className="inline-flex items-center gap-1 text-xs text-primary hover:underline mt-2"><Download className="w-3.5 h-3.5" /> download</a>
                </div>
              </div>
            )}
            <div>
              <div className="text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">Performance metrics</div>
              <MetricPanel l={selected} />
            </div>
            <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
              <div className="flex items-center gap-2 text-amber-800 dark:text-amber-300 text-sm font-medium">
                <AlertTriangle className="w-4 h-4" /> Diagnosis
              </div>
              <p className="text-xs text-amber-900 dark:text-amber-200 mt-1">{selected.diagnosis?.summary}</p>
              {selected.diagnosis?.directive_hint && (
                <p className="text-xs text-amber-800 dark:text-amber-300 mt-1">→ {selected.diagnosis.directive_hint}</p>
              )}
            </div>

            {/* PHASE 1 smart loser-redraw: judge (above) → suggest (here) → user decides which
                fix to run, BEFORE anything dispatches. Selecting an option just sets
                variationType, so the existing preview-first + Generate flow below is untouched. */}
            {selected.diagnosis?.suggestion && (() => {
              const s = selected.diagnosis.suggestion!;
              const options = [s.recommended, ...s.alternatives];
              return (
                <div className="p-3 rounded-lg border border-primary/30 bg-primary/5">
                  <p className="text-xs text-gray-700 dark:text-gray-300">
                    📉 We judged this lost on <b>{s.lagging.label} {fmtSuggestVal(s.lagging.metric, s.lagging.value)}</b>
                    {' '}(target {fmtSuggestVal(s.lagging.metric, s.lagging.target)}) — {s.why}
                  </p>
                  <p className="text-xs text-emerald-700 dark:text-emerald-400 mt-1.5">
                    ✅ Suggested: <b>{s.recommended.title}</b> — {s.recommended.does}
                  </p>
                  <div className="mt-2 space-y-1.5">
                    {options.map(opt => (
                      <button key={opt.type} type="button" onClick={() => setVariationType(opt.type)}
                        className={`w-full text-left p-2 rounded-lg border flex items-start gap-2 transition-colors ${
                          variationType === opt.type
                            ? 'border-primary bg-primary/10'
                            : 'border-border hover:bg-accent/50'
                        }`}>
                        {variationType === opt.type
                          ? <CheckCircle2 className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                          : <span className="w-4 h-4 rounded-full border border-border shrink-0 mt-0.5" />}
                        <span className="min-w-0">
                          <span className="text-xs font-semibold text-gray-900 dark:text-white">
                            {opt.title}{opt.type === s.recommended.type && ' (recommended)'}
                          </span>
                          <span className="block text-[11px] text-gray-500 mt-0.5">{opt.does}</span>
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* PHASE 2-4 loser-redraw conversational gate: talk through / re-plan the fix, ask
                clarifying questions, confirm → dispatches (via the backend's redraw-chat endpoint,
                which reuses createRequest/dispatchRun or studioDispatchVideo — no new dispatch path
                here). Additive layer above the Phase-1 card; that card + its flow are untouched. */}
            {selected.diagnosis?.suggestion && (() => {
              const redrawLastAsst = [...redrawConv].reverse().find(m => m.role === 'assistant' && m.data);
              const redrawClarifying: { slot: string; q: string }[] = redrawLastAsst?.data?.clarifying || [];
              const redrawConfirmed = redrawApproved || !!redrawLastAsst?.data?.redraw_approved;
              return (
                <div className="rounded-xl border border-violet-200 dark:border-violet-900/50 bg-violet-50/40 dark:bg-violet-950/10 p-3 space-y-3">
                  <div className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-white">
                    <MessageSquare className="w-4 h-4 text-violet-600" /> Talk it through
                  </div>

                  {redrawConv.length > 0 && (
                    <div className="space-y-1.5 max-h-52 overflow-y-auto rounded-lg bg-white/60 dark:bg-gray-900/40 p-2 border border-violet-100 dark:border-violet-900/40">
                      {redrawConv.map(m => (
                        <div key={m.id} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                          <div className={`max-w-[85%] rounded-lg px-2.5 py-1.5 text-xs whitespace-pre-line ${m.role === 'user'
                            ? 'bg-violet-600 text-white'
                            : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200'}`}>
                            {renderBold(m.text || '')}
                          </div>
                        </div>
                      ))}
                      {redrawSending && (
                        <div className="flex justify-start">
                          <div className="rounded-lg px-2.5 py-1.5 text-xs bg-gray-100 dark:bg-gray-800 text-gray-500 flex items-center gap-1">
                            <Loader2 className="w-3 h-3 animate-spin" /> reading…
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {redrawClarifying.length > 0 && !redrawConfirmed && (
                    <div className="rounded-lg border border-amber-200 dark:border-amber-900/50 bg-amber-50/70 dark:bg-amber-950/10 p-2 space-y-1">
                      <div className="flex items-center gap-1 text-[11px] font-semibold text-amber-800 dark:text-amber-300">
                        <AlertTriangle className="w-3 h-3" /> A few things to confirm
                      </div>
                      {redrawClarifying.map((c, i) => (
                        <button key={i} onClick={() => setRedrawCorr(c.q)} disabled={redrawSending}
                          className="block w-full text-left text-xs text-amber-900 dark:text-amber-200 hover:underline disabled:opacity-40">
                          • {c.q}
                        </button>
                      ))}
                    </div>
                  )}

                  {/* "New character + winning script" — the one fix outside the FIX_CATALOG catalog
                      above; sends the Phase-4 assets via the redraw-chat message, same confirm gate. */}
                  {!redrawConfirmed && (
                    <div className="rounded-lg border border-fuchsia-200 dark:border-fuchsia-900/50 bg-fuchsia-50/40 dark:bg-fuchsia-950/10 p-2 space-y-1.5">
                      <div className="text-[11px] font-semibold text-fuchsia-800 dark:text-fuchsia-300">New character + winning script</div>
                      <p className="text-[11px] text-gray-500">Keep the same scene, swap in a brand-new person, and have them speak a winning script instead of the original.</p>
                      <textarea value={redrawScript} onChange={e => setRedrawScript(e.target.value)} rows={2}
                        placeholder="Paste the winning script, or a winner clip URL — leave blank to let it rewrite"
                        className="w-full rounded border border-fuchsia-200 dark:border-fuchsia-800 bg-transparent p-1.5 text-xs text-gray-900 dark:text-white" />
                      <button type="button" disabled={redrawSending}
                        onClick={async () => {
                          // TWO turns, sequential: the fix pick, then the script source on its own —
                          // so the backend's pasted-script detector reads it in isolation, not mixed
                          // in with the fix-selection sentence.
                          await sendRedrawCorrection('Use a new character + winning script.');
                          await sendRedrawCorrection(redrawScript.trim() || 'let it rewrite');
                        }}
                        className="text-xs font-medium px-2.5 py-1 rounded-lg bg-fuchsia-600 text-white hover:bg-fuchsia-700 disabled:opacity-40">
                        Use this fix
                      </button>
                    </div>
                  )}

                  {redrawConfirmed ? (
                    <div className="flex items-center gap-1.5 text-xs font-medium text-emerald-700 dark:text-emerald-300">
                      <CheckCircle2 className="w-4 h-4" /> Approved — generating now. Check the Requests tab.
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <button type="button" onClick={confirmRedraw} disabled={redrawSending}
                          className="px-3 py-1.5 rounded-lg text-xs font-medium bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40">
                          Confirm & generate
                        </button>
                        <span className="text-[11px] text-gray-400">or correct it below — I'll re-plan</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <textarea value={redrawCorr} onChange={e => setRedrawCorr(e.target.value)} rows={1}
                          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendRedrawCorrection(redrawCorr); } }}
                          placeholder='e.g. "use the hook change instead", "make it 2"'
                          className="flex-1 rounded-lg border border-gray-300 dark:border-gray-700 bg-transparent p-2 text-sm text-gray-900 dark:text-white resize-none" />
                        <button type="button" onClick={() => sendRedrawCorrection(redrawCorr)} disabled={redrawSending || !redrawCorr.trim()}
                          className="shrink-0 px-3 py-2 rounded-lg text-sm font-medium bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-40">
                          Send
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            <div>
              <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">2 · What do you expect from this variation?</label>
              <textarea value={expectation} onChange={e => setExpectation(e.target.value)} rows={4}
                placeholder="e.g. Ditch the gutter clip — open with something Texas-homeowner + rising bills. Keep HER voice and story. Make the first few seconds feel urgent."
                className="mt-1 w-full rounded-lg border border-border bg-card p-3 text-sm text-gray-900 dark:text-white" />
              <Button variant="outline" className="mt-2" onClick={interpret} disabled={interpreting}>
                {interpreting ? <><Loader2 className="w-4 h-4 animate-spin mr-1" /> Interpreting…</> : <><Sparkles className="w-4 h-4 mr-1" /> Interpret with AI</>}
              </Button>
            </div>

            <div>
              <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">3 · Variation type (flow)</label>
              <select value={variationType} onChange={e => setVariationType(e.target.value)}
                className="mt-1 w-full rounded-lg border border-border bg-card p-2 text-sm text-gray-900 dark:text-white">
                {VARIATION_TYPES.map(t => (
                  <option key={t} value={t}>{t}{selected.diagnosis?.recommended_type === t ? '  (recommended)' : ''}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">4 · Generation model</label>
              <select value={model} onChange={e => setModel(e.target.value)}
                className="mt-1 w-full rounded-lg border border-border bg-card p-2 text-sm text-gray-900 dark:text-white">
                {GEN_MODELS.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
              </select>
              <p className="text-[11px] text-gray-400 mt-1">Only used by generative flows (b-roll / hook generation). Auto picks the best available.</p>
            </div>

            <div>
              <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">Preserve</label>
              <div className="flex flex-wrap gap-2 mt-1">
                {PRESERVE_OPTIONS.map(p => (
                  <button key={p} onClick={() => setPreserve(s => s.includes(p) ? s.filter(x => x !== p) : [...s, p])}
                    className={`text-xs px-2 py-1 rounded-full border ${
                      preserve.includes(p) ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-gray-600 dark:text-gray-300'
                    }`}>{p}</button>
                ))}
              </div>
            </div>

            {directive && (
              <div className="p-3 rounded-lg border border-border bg-card text-xs space-y-1">
                <div className="font-semibold text-gray-700 dark:text-gray-300">AI understood:</div>
                <div className="text-gray-600 dark:text-gray-400">{directive.rationale}</div>
                {directive.asset_directive && <div><b>Asset:</b> {directive.asset_directive}</div>}
                {directive.tone_directive && <div><b>Tone:</b> {directive.tone_directive}</div>}
                {Array.isArray(directive.conflicts_or_clarifications) && directive.conflicts_or_clarifications.length > 0 && (
                  <div className="text-amber-700 dark:text-amber-400 mt-1">
                    <b className="inline-flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> Clarify:</b>
                    <ul className="list-disc ml-4">{directive.conflicts_or_clarifications.map((c: string, i: number) => <li key={i}>{c}</li>)}</ul>
                  </div>
                )}
              </div>
            )}

            <div className="flex items-center gap-3">
              <label className="text-sm text-gray-700 dark:text-gray-300"># variants</label>
              <input type="number" min={1} max={6} value={variantCount} onChange={e => setVariantCount(parseInt(e.target.value) || 3)}
                className="w-16 rounded border border-border bg-card p-1 text-sm text-gray-900 dark:text-white" />
              <Button variant="ghost" onClick={generateTree} disabled={submitting} className="ml-auto" title="Generate the full Hook→Script→UGC→B-roll testing tree">
                <GitBranch className="inline w-3.5 h-3.5 mr-1 align-[-2px]" /> Generate framework tree
              </Button>
              <Button onClick={openPreview} disabled={submitting || previewLoading}>
                {previewLoading ? <><Loader2 className="w-4 h-4 animate-spin mr-1" /> Building preview…</> : 'Generate variants →'}
              </Button>
            </div>
            <p className="text-[11px] text-gray-400">
              Est. cost: Hook / Caption / Reclean ≈ &lt;$0.05 (transcription + stitch) · Avatar ≈ TikTok credits · Script ≈ ElevenLabs credits. Framework tree = many nodes (one per branch). You can Stop anytime before paid steps.
            </p>

            {err && <p className="text-sm text-red-600">{err}</p>}
          </div>
        )}
      </div>
    </div>
    {previewOpen && (
      <GenerationPreview
        preview={previewData}
        loading={previewLoading}
        busy={submitting}
        error={previewLoading ? undefined : (previewData ? err : previewErr)}
        onConfirm={submit}
        onCancel={() => { setPreviewOpen(false); setPreviewData(null); setPreviewErr(''); }}
      />
    )}
    </div>
  );
}

// ── Requests list + review/approve ────────────────────────────────────────────
function RequestsList() {
  const [reqs, setReqs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [abPkg, setAbPkg] = useState<Record<string, any>>({});
  const [costs, setCosts] = useState<Record<string, any>>({});   // request_id → {total_usd, by_provider}
  // Live progress/ETA per running request — read from the SAME team-jobs feed the floating monitor
  // uses (variation_requests has no progress/eta_sec columns). Keyed by job_id === request id.
  const [jobMap, setJobMap] = useState<Record<string, any>>({});

  // Cache the list (15s TTL) so re-opening the Requests tab reuses data instead of
  // re-firing listRequests + a burst of cost lookups every time. The 8s poll forces
  // fresh (repopulating the cache); mutations force fresh too.
  const load = useCallback((force = false) => {
    if (force) invalidate('reqs:list');
    setLoading(true);
    cached('reqs:list', 15000, () => regenApi.listRequests()).then(r => {
      const rows = r.data.data || [];
      setReqs(rows);
      // pull the per-creation cost breakdown once per request (skip ones we already have)
      rows.slice(0, 40).forEach((row: any) => {
        setCosts(prev => {
          if (prev[row.id]) return prev;
          regenApi.creationCosts(row.id).then(c => setCosts(p => ({ ...p, [row.id]: c.data }))).catch(() => {});
          return prev;
        });
      });
    }).finally(() => setLoading(false));
  }, []);
  useEffect(() => {
    const warm = peek<any>('reqs:list', 15000);   // seed instantly on a warm re-open — no spinner
    if (warm) { setReqs(warm.data?.data || []); setLoading(false); }
    load();
    const t = setInterval(() => load(true), 8000);
    return () => clearInterval(t);
  }, [load]);
  // Poll the live jobs feed (shared, cached) so running cards can show % · ETA + a progress bar.
  useEffect(() => {
    let alive = true;
    const pull = () => regenApi.teamJobs()
      .then((r: any) => { if (!alive) return;
        const m: Record<string, any> = {};
        (r.data.jobs || []).forEach((j: any) => { if (j.job_id) m[j.job_id] = j; });
        setJobMap(m); })
      .catch(() => {});
    pull();
    const t = setInterval(pull, 8000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  if (loading && !reqs.length) return <Centered><Loader2 className="w-6 h-6 animate-spin" /> Loading…</Centered>;

  return (
    <div className="space-y-4">
      <div className="flex justify-end"><Button variant="ghost" onClick={() => load(true)}><RefreshCw className="w-4 h-4 mr-1" /> Refresh</Button></div>
      {reqs.map(r => (
        <div key={r.id} className="p-4 rounded-lg border border-border bg-card">
          <div className="flex justify-between items-center">
            <div>
              <div className="font-medium text-sm text-gray-900 dark:text-white">{r.creative_filename}</div>
              <div className="text-xs text-gray-500">{r.variation_type} · {new Date(r.created_at).toLocaleString()}</div>
            </div>
            {(() => {
              // Orphaned job: 'running' but no variant after well past the expected render window
              // (the callback that flips it to ready/failed was lost). Don't keep lying "running" —
              // surface it as stuck and let the user re-dispatch instead of only being able to Stop.
              const stale = r.status === 'running' && !(r.variants || []).length &&
                (Date.now() - new Date(r.created_at).getTime()) > 12 * 60 * 1000;
              return (
                <div className="flex items-center gap-2">
                  <StatusBadge status={r.status} />
                  {/* Live % · ETA + thin bar — only while genuinely running (disappears the instant the
                      request is terminal, since this whole block is gated on r.status === 'running'). */}
                  {r.status === 'running' && !stale && jobMap[r.id] && (() => {
                    const ji = jobMap[r.id];
                    return (
                      <div className="flex items-center gap-1.5 text-[11px] text-gray-500 tabular-nums">
                        <span className="whitespace-nowrap">{ji.progress}%{ji.eta_sec != null ? ` · ETA ${fmtEta(ji.eta_sec)}` : ''}</span>
                        <div className="h-1.5 w-16 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
                          <div className="h-full bg-primary transition-all duration-700" style={{ width: `${ji.progress || 0}%` }} />
                        </div>
                      </div>
                    );
                  })()}
                  {stale && <span className="text-[11px] text-amber-600 dark:text-amber-400">stuck — no result</span>}
                  {r.status === 'running' && !stale && (
                    <Button variant="destructive" onClick={() => regenApi.cancel(r.id).then(() => load(true))} title="Stop the job — no further credits spent">
                      Stop
                    </Button>
                  )}
                  {(r.status === 'failed' || stale) && (
                    <Button variant="outline" onClick={() => { if (window.confirm('Re-run this generation? It re-dispatches the job to ORBIT (new credits may be spent).')) regenApi.redispatch(r.id).then(() => load(true)).catch((e: any) => alert('Retry failed: ' + (e?.response?.data?.error || e?.message || 'unknown error'))); }} title="Re-dispatch this job to ORBIT">
                      Retry
                    </Button>
                  )}
                  {r.status !== 'running' && (
                    <Button variant="ghost" onClick={() => { if (window.confirm('Discard this request and its variants?')) regenApi.discard(r.id).then(() => load(true)); }} title="Discard request + variants">
                      Discard
                    </Button>
                  )}
                  {stale && (
                    <Button variant="ghost" onClick={() => { if (window.confirm('Give up on this stuck job?')) regenApi.discard(r.id).then(() => load(true)); }} title="Discard the stuck request">
                      Discard
                    </Button>
                  )}
                </div>
              );
            })()}
          </div>
          {r.expectation && <p className="text-xs text-gray-600 dark:text-gray-400 mt-2 italic">“{r.expectation}”</p>}
          {costs[r.id] && (costs[r.id].total_usd > 0 || Object.keys(costs[r.id].by_provider || {}).length > 0) && (
            <div className="mt-2 text-[11px] text-gray-600 dark:text-gray-300 flex flex-wrap items-center gap-2">
              <span className="font-semibold"><DollarSign className="inline w-3.5 h-3.5 mr-1 align-[-2px]" />${Number(costs[r.id].total_usd || 0).toFixed(4)}</span>
              {Object.entries(costs[r.id].by_provider || {}).map(([p, c]: any) => (
                <span key={p} className="px-1.5 py-0.5 rounded bg-accent text-[10px]">{p}: {Number(c) === 0 ? 'free' : `$${Number(c).toFixed(4)}`}</span>
              ))}
            </div>
          )}
          {r.status === 'failed' && r.error && (
            <div className="mt-2 text-[11px] text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-2 break-words">
              <span className="font-semibold inline-flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> Why it failed:</span> {String(r.error).replace(/\s*\|\s*/g, ' · ')}
            </div>
          )}
          {/* Multi-face notice — only shows once the pipeline records it; field may be absent on older records. */}
          {(() => { const mf = r.multi_face_count ?? r.assets?.multi_face_count; return mf >= 2 ? (
            <div className="mt-2 text-[11px] text-amber-700 dark:text-amber-300 flex items-center gap-1">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0" /> {mf} people in clip — one voice used for all.
            </div>
          ) : null; })()}

          <div className="grid sm:grid-cols-2 gap-3 mt-3">
            {(r.variants || []).map((v: any) => (
              <div key={v.id} className="rounded-lg border border-border p-2">
                {(v.play_url || v.video_url)
                  ? <div className="mx-auto mb-2 w-44 aspect-[9/16] rounded-lg overflow-hidden bg-black"><MediaThumb url={v.play_url || v.video_url} s3key={v.s3_key} className="w-full h-full object-cover" /></div>
                  : <div className="text-xs text-gray-500 italic py-6 text-center">queued to production pipeline</div>}
                <div className="text-xs font-medium text-gray-800 dark:text-gray-200">{v.recipe}</div>
                <div className="text-xs text-gray-500 mt-1">{v.whats_changed}</div>
                {modelsLine(v.models) && <div className="text-[11px] text-gray-400 font-mono mt-1 truncate" title={modelsLine(v.models)}>{modelsLine(v.models)}</div>}
                {v.media_file_id && <div className="text-xs text-emerald-600 mt-1 flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> saved to Variations/{r.creative_filename}</div>}
                <div className="flex items-center justify-between mt-2">
                  <span className="text-xs text-gray-400">conf {v.confidence ?? '—'}</span>
                  {v.approved
                    ? <span className="text-xs text-green-600 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> approved</span>
                    : (
                      <div className="flex gap-2">
                        <Button variant="ghost" onClick={() => { if (window.confirm('Discard this variant?')) regenApi.discard(r.id, v.id).then(() => load(true)); }}>Discard</Button>
                        {(v.video_url || v.s3_url) && <Button variant="outline" onClick={() => regenApi.approve(r.id, v.id).then(() => load(true))}>Approve</Button>}
                      </div>
                    )}
                </div>
                {(v.video_url || v.s3_url) && (
                  <Button variant="ghost" className="mt-2 text-xs" onClick={() => regenApi.abPackage(r.id, v.id).then(res => setAbPkg(p => ({ ...p, [v.id]: res.data.data })))}>
                    <FlaskConical className="inline w-3.5 h-3.5 mr-1 align-[-2px]" /> Prepare A/B test
                  </Button>
                )}
                {abPkg[v.id] && (
                  <div className="mt-2 p-2 rounded-lg border border-border bg-accent/40 text-xs space-y-1">
                    <div className="font-semibold">A/B package (you launch in Meta)</div>
                    <div>Budget/variant: <b>${abPkg[v.id].suggested?.budget_per_variant_usd}</b> · split {abPkg[v.id].suggested?.split} · {abPkg[v.id].suggested?.duration_days}d</div>
                    {/* Only a redraw-of-a-real-ad has an "original" to use as control. A from-scratch
                        Studio/Avatar/UGC creative has none — don't show a dead link; say so. */}
                    {abPkg[v.id].original?.url
                      ? <div className="truncate"><a className="text-primary underline inline-flex items-center gap-1" href={abPkg[v.id].original.url} target="_blank" rel="noreferrer"><Play className="w-3 h-3" /> original (control)</a></div>
                      : <div className="text-amber-600 dark:text-amber-400">No original to use as control — this is a from-scratch creative. A/B it against your current best performer.</div>}
                    <div className="truncate"><a className="text-primary underline inline-flex items-center gap-1" href={abPkg[v.id].variant?.url} target="_blank" rel="noreferrer"><Play className="w-3 h-3" /> variant</a></div>
                    <div className="text-gray-500">{abPkg[v.id].original?.url ? abPkg[v.id].how_to : 'Add this variant as an ad in a fresh ad set (or alongside your current control), 50/50 even split, run 3 days, then compare hook/hold/CTR/ROI.'}</div>
                  </div>
                )}
              </div>
            ))}
            {!(r.variants || []).length && r.status === 'running' && (
              <div className="text-xs text-gray-500 flex items-center gap-2"><ThinkingAvatar state="brewing" size={20} /> generating…</div>
            )}
          </div>
          <AuditBox requestId={r.id} />
          {r.status !== 'running' && (
            <RefineBox requestId={r.id} onRefined={() => load(true)}
              creativeRef={(r.variants || [])[0]?.filename || undefined}
              onDiscuss={(ctx) => window.dispatchEvent(new CustomEvent('orbit:discuss', { detail: ctx }))} />
          )}
        </div>
      ))}
      {!reqs.length && <Centered>No variation requests yet.</Centered>}
    </div>
  );
}

const EVENT_STYLE: Record<string, string> = {
  done: 'text-green-600', fail: 'text-red-600', revise: 'text-amber-600',
  coached: 'text-purple-600', reward: 'text-blue-600',
};

// Durable per-task audit: everything the team did for THIS job (from Postgres).
function AuditBox({ requestId }: { requestId: string }) {
  const [open, setOpen] = useState(false);
  const [events, setEvents] = useState<any[] | null>(null);
  const [loading, setLoading] = useState(false);

  const load = () => {
    setLoading(true);
    regenApi.teamAudit(requestId).then(r => setEvents(r.data.events || [])).catch(() => setEvents([])).finally(() => setLoading(false));
  };
  const toggle = () => { const n = !open; setOpen(n); if (n && events === null) load(); };

  return (
    <div className="mt-3 border-t border-border pt-2">
      <button onClick={toggle} className="text-[11px] text-gray-500 hover:text-gray-800 flex items-center gap-1">
        <Search className="inline w-3.5 h-3.5 mr-1 align-[-2px]" /> Team audit {open ? <ChevronUp className="inline w-3.5 h-3.5" /> : <ChevronDown className="inline w-3.5 h-3.5" />} <span className="text-gray-400">(durable log of every persona step)</span>
      </button>
      {open && (
        <div className="mt-2">
          {loading ? <div className="text-[11px] text-gray-400 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> loading…</div>
            : !events?.length ? <div className="text-[11px] text-gray-400">No recorded steps for this job (older jobs pre-date the audit log, or this recipe didn't run the team).</div>
            : (
              <div className="space-y-1 max-h-72 overflow-auto pr-1">
                {events.map((e, i) => (
                  <div key={i} className="text-[11px] flex gap-2 items-start">
                    <span className="text-gray-400 tabular-nums whitespace-nowrap">{e.at ? new Date(e.at).toLocaleTimeString() : ''}</span>
                    <span className="font-medium text-gray-700 dark:text-gray-200 w-28 truncate">{e.role || e.persona}</span>
                    <span className={`w-14 ${EVENT_STYLE[e.event] || 'text-gray-500'}`}>{e.event}{e.ms ? ` ${(e.ms / 1000).toFixed(1)}s` : ''}</span>
                    <span className="text-gray-500 flex-1">{e.detail || e.task || ''}{e.helpfulness != null ? ` · helpful ${(e.helpfulness * 100).toFixed(0)}%` : ''}</span>
                  </div>
                ))}
              </div>
            )}
        </div>
      )}
    </div>
  );
}

function RefineBox({ requestId, onRefined, creativeRef, onDiscuss }: {
  requestId: string; onRefined: () => void; creativeRef?: string;
  onDiscuss?: (ctx: { jobId: string; creativeRef?: string }) => void;
}) {
  const [fb, setFb] = useState('');
  const [busy, setBusy] = useState(false);
  const [savingFb, setSavingFb] = useState(false);
  const [err, setErr] = useState('');
  const [sent, setSent] = useState(false);
  const [fbSaved, setFbSaved] = useState(false);
  // Two-step: interpret first, confirm second. Nothing is taught until it's approved.
  const [proposal, setProposal] = useState<any>(null);
  const [proposalRule, setProposalRule] = useState('');
  const send = () => {
    if (!fb.trim()) return;
    setBusy(true); setErr(''); setSent(false); setFbSaved(false);
    regenApi.refine(requestId, { feedback: fb.trim() })
      .then(() => { setFb(''); setSent(true); onRefined(); })
      // it used to fail silently (no .catch) — a 404 looked identical to "nothing happened"
      .catch((e: any) => setErr(e?.response?.data?.error || e.message || 'refine failed'))
      .finally(() => setBusy(false));
  };
  // STEP 1 — interpret WITHOUT saving, so a mis-read never becomes a permanent instruction.
  const reviewFeedback = () => {
    if (!fb.trim()) return;
    setSavingFb(true); setErr(''); setSent(false); setFbSaved(false); setProposal(null);
    regenApi.teamFeedbackPropose({
      note: fb.trim(), creative_ref: creativeRef || undefined, job_id: requestId,
    })
      .then(r => { setProposal(r.data); setProposalRule(r.data?.rule || fb.trim()); })
      .catch((e: any) => setErr(e?.response?.data?.error || e.message || 'could not read that feedback'))
      .finally(() => setSavingFb(false));
  };

  // STEP 2 — record it. No regeneration, no credit spent. Writes the verdict AND, when there is
  // something to correct, the persona lesson — the same pipeline the Team Room uses.
  const confirmFeedback = () => {
    if (!fb.trim()) return;
    setSavingFb(true); setErr('');
    regenApi.feedbackOnly({
      request_id: requestId, creative_ref: creativeRef || undefined, feedback: fb.trim(),
      persona: proposal?.persona || undefined, category: proposal?.category || undefined,
      rule: proposalRule.trim() || undefined, normalized: !!proposal?.normalized,
      context: proposal?.context || undefined,
      confirmed: !!(proposal?.teaches && proposal?.persona),
    })
      .then(() => { setFb(''); setProposal(null); setFbSaved(true); })
      .catch((e: any) => setErr(e?.response?.data?.error || e.message || 'feedback failed'))
      .finally(() => setSavingFb(false));
  };
  return (
    <div className="mt-3 border-t border-border pt-2">
      <div className="text-[11px] text-gray-500 mb-1"><MessageSquare className="inline w-3.5 h-3.5 mr-1 align-[-2px]" /> Not right? Tell it what to change — it regenerates with your feedback:</div>
      <div className="flex gap-2">
        <input value={fb} onChange={e => setFb(e.target.value)} onKeyDown={e => e.key === 'Enter' && send()}
          placeholder="e.g. keep her voice, make the hook more urgent, show the savings number sooner"
          className="flex-1 rounded border border-border bg-card p-2 text-xs text-gray-900 dark:text-white" />
        <Button variant="outline" onClick={reviewFeedback} disabled={busy || savingFb || !fb.trim()} className="text-xs" title="See exactly what ORBIT will learn before anything is saved — no new video, no credit spent">
          {savingFb ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send feedback'}
        </Button>
        <Button onClick={send} disabled={busy || savingFb || !fb.trim()} className="text-xs">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Regenerate'}
        </Button>
      </div>

      {/* ── Confirm before anything is learned ── */}
      {proposal && (
        <div className="mt-2 rounded border border-primary/40 bg-primary/5 p-2.5 space-y-2">
          {proposal.sentiment === 'praise' ? (
            <p className="text-[11px] text-emerald-600 dark:text-emerald-400">
              Read as positive — this will be recorded as a <strong>win</strong> for the team. No correction stored.
            </p>
          ) : proposal.teaches ? (
            <>
              <p className="text-[11px] text-gray-600 dark:text-gray-300">
                We'll teach the <strong>{proposal.role}</strong>
                {proposal.inferred_persona && <span className="text-amber-600 dark:text-amber-400"> (role inferred — correct it below if wrong)</span>}:
              </p>
              <input value={proposalRule} onChange={e => setProposalRule(e.target.value)}
                className="w-full rounded border border-border bg-card p-2 text-xs text-gray-900 dark:text-white" />
              {proposal.normalized && (
                <p className="text-[10px] text-gray-500">Reworded so it still makes sense on a future job. Edit freely.</p>
              )}
            </>
          ) : (
            <p className="text-[11px] text-gray-500">{proposal.reason_not_teaching}</p>
          )}
          <div className="flex gap-2 items-center">
            <Button onClick={confirmFeedback} disabled={savingFb} className="text-xs">
              {savingFb ? <Loader2 className="w-4 h-4 animate-spin" /> : (proposal.teaches ? 'Correct — teach this' : 'Record it')}
            </Button>
            <Button variant="outline" onClick={() => setProposal(null)} disabled={savingFb} className="text-xs">Not right</Button>
            {onDiscuss && (
              <Button variant="outline" className="text-xs"
                onClick={() => onDiscuss({ jobId: requestId, creativeRef })}
                title="Open a meeting in the team room about this request">
                Discuss in team room
              </Button>
            )}
          </div>
        </div>
      )}
      {err && <div className="text-[11px] text-red-600 mt-1 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> {err}</div>}
      {sent && <div className="text-[11px] text-emerald-600 mt-1 flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Regenerating with your feedback — it'll appear as a new request above.</div>}
      {fbSaved && <div className="text-[11px] text-emerald-600 mt-1 flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Feedback saved — ORBIT will learn from it, no new video generated.</div>}
    </div>
  );
}

const LIB_VERTICALS = ['home_insurance', 'auto_insurance', 'medicare', 'final_expense', 'bizop', 'refinance'];

// Module-level (stable) so it doesn't remount the media grid on every keystroke → no video reload churn.
function Section({ title, hint, children }: any) {
  return (
    <div className="mb-6">
      <div className="flex items-baseline gap-2 mb-2">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{title}</h3>
        <span className="text-[11px] text-gray-400">{hint}</span>
      </div>
      {children}
    </div>
  );
}

function CreativeLibrary({ onRegenerate }: { onRegenerate: (creativeId: string) => void }) {
  const [vertical, setVertical] = useState('');   // '' = ALL verticals (was scoped to home_insurance)
  const [scraper, setScraper] = useState<any[]>([]);
  const [ourWins, setOurWins] = useState<any[]>([]);
  const [losers, setLosers] = useState<any[]>([]);
  const [assets, setAssets] = useState<any[]>([]);
  const [assetKind, setAssetKind] = useState('all');
  const [loading, setLoading] = useState(true);
  const [refs, setRefsState] = useRefs();
  const [sel, setSel] = useState<Set<string>>(new Set());   // losers selected to regenerate
  const [busy, setBusy] = useState(false);
  const [remix, setRemix] = useState<Record<string, string>>({});   // url -> label (any library clip)
  const [rprompt, setRprompt] = useState('');
  const [rengine, setRengine] = useState('seedance');
  const [rmsg, setRmsg] = useState('');
  const [renhancing, setRenhancing] = useState(false);
  const [rplan, setRplan] = useState(false);                 // plan-as-script checkbox
  const [rscript, setRscript] = useState<string | null>(null);   // drafted script awaiting approval
  const [rsec, setRsec] = useState(10);                      // remix output length (s)

  useEffect(() => {
    // Spinner only on a genuinely cold load, and tied ONLY to the main list (losers). The old
    // Promise.all meant ONE hanging fetch (e.g. All-verticals scraper) left the spinner forever —
    // the app worked but Creative Library "kept loading". Each fetch is now independent and can't
    // block the others or the spinner.
    let alive = true;
    setLoading(!hasCached(`losers`));
    cached(`losers`, 3600000, () => regenApi.getLosers({ minSpend: 500, maxRoas: 1.0, limit: 50 }))
      .then(r => { if (alive) setLosers(r.data.data.losers || []); })
      .catch(() => { if (alive) setLosers([]); })
      .finally(() => { if (alive) setLoading(false); });
    cached(`scraper:${vertical}`, 180000, () => regenApi.scraperWinners(vertical, 12)).then(r => alive && setScraper(r.data.winners || [])).catch(() => alive && setScraper([]));
    cached(`ourwins`, 180000, () => regenApi.ourWinners({ limit: 24 })).then(r => alive && setOurWins(r.data.data || [])).catch(() => alive && setOurWins([]));
    cached(`assets:all`, 3600000, () => regenApi.getAssets({})).then(r => alive && setAssets(r.data.data || [])).catch(() => alive && setAssets([]));
    return () => { alive = false; };
  }, [vertical]);

  const isRef = (id: string) => refs.some(r => r.id === id);
  const isSel = (id: string) => sel.has(id);
  const toggleSel = (id: string) => setSel(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  // PREVIEW-FIRST gate for the batch path (mirrors NewVariation.openPreview, reuses GenerationPreview):
  // one dry-run + confirm PER selected creative, so nothing in a multi-select batch spends blind.
  // batchQueue[0] is the creative currently being previewed; confirming dispatches it for real and
  // advances to the next; cancelling drops the whole remaining queue (nothing further spends).
  const [batchQueue, setBatchQueue] = useState<string[] | null>(null);
  const [batchPreview, setBatchPreview] = useState<DryRunPreview | null>(null);
  const [batchLoading, setBatchLoading] = useState(false);
  const [batchErr, setBatchErr] = useState('');

  const previewNextInBatch = async (queue: string[]) => {
    if (!queue.length) { setBatchQueue(null); setSel(new Set()); window.location.href = '/variation-studio#requests'; return; }
    setBatchQueue(queue);
    setBatchErr(''); setBatchPreview(null); setBatchLoading(true);
    try {
      const id = queue[0];
      const l = losers.find(x => x.creative_id === id);
      const refIds = refs.map(r => r.id).filter(r => r !== id);
      const r = await regenApi.createRequest({
        creative_id: id,
        variation_type: l?.diagnosis?.recommended_type || 'Hook Change Only',
        reference_creative_ids: refIds.length ? refIds : undefined,
        directive: { assets: { dry_run: true } },
      });
      const rid = r.data.data.id;
      let data = r.data.data;
      for (let i = 0; i < 75 && data.status !== 'dry_run' && data.status !== 'failed'; i++) {
        await new Promise(res => setTimeout(res, 2000));
        const g = await regenApi.getRequest(rid);
        data = g.data.data;
      }
      if (data.status === 'dry_run') setBatchPreview(data.directive?.dry_run_preview || {});
      else if (data.status === 'failed') setBatchErr(data.error || 'Preview failed — nothing was generated.');
      else setBatchErr('Preview is taking longer than expected — try again in a moment.');
    } catch (e: any) {
      setBatchErr(e?.response?.data?.error || 'Could not build a preview');
    } finally { setBatchLoading(false); }
  };
  const confirmBatch = async () => {
    if (!batchQueue || !batchQueue.length) return;
    const id = batchQueue[0];
    setBatchLoading(true);
    try {
      const l = losers.find(x => x.creative_id === id);
      const refIds = refs.map(r => r.id).filter(r => r !== id);
      await regenApi.createRequest({
        creative_id: id,
        variation_type: l?.diagnosis?.recommended_type || 'Hook Change Only',
        reference_creative_ids: refIds.length ? refIds : undefined,
      });
    } finally {
      previewNextInBatch(batchQueue.slice(1));
    }
  };
  const cancelBatch = () => { setBatchQueue(null); setBatchPreview(null); setBatchErr(''); setSel(new Set()); };

  // "Regenerate selected": open New Variation for one (its own preview-first flow), or preview-gate
  // the batch for many — one dry-run + confirm at a time (see previewNextInBatch/confirmBatch above).
  const regenerateSelected = () => {
    const ids = Array.from(sel);
    if (!ids.length) return;
    if (ids.length === 1) { onRegenerate(ids[0]); return; }   // single → rich New Variation flow (refs travel via tray)
    previewNextInBatch(ids);
  };

  const RefBtn = ({ id, filename }: { id: string; filename: string }) => (
    <button onClick={() => setRefsState(toggleRef({ id, filename }))}
      className={`text-[11px] font-medium px-2.5 py-1 rounded-md border transition inline-flex items-center gap-1 ${isRef(id) ? 'bg-indigo-600 text-white border-indigo-600' : 'border-indigo-200 text-indigo-600 hover:bg-indigo-50 dark:border-indigo-800 dark:text-indigo-300 dark:hover:bg-indigo-950'}`}>
      {isRef(id) ? <><Check className="w-3 h-3" /> reference</> : <><Plus className="w-3 h-3" /> reference</>}
    </button>
  );

  // Remix = pick ANY library clip (incl. scraper winners) as a reference video + a prompt → generate.
  const inRemix = (url?: string) => !!url && url in remix;
  const toggleRemix = (url?: string, label = '') => {
    if (!url) return;
    setRemix(m => { const n = { ...m }; if (url in n) delete n[url]; else n[url] = label; return n; });
  };
  const RemixBtn = ({ url, label }: { url?: string; label?: string }) => (
    <button onClick={() => toggleRemix(url, label)} disabled={!url}
      className={`text-[11px] font-medium px-2.5 py-1 rounded-md border transition inline-flex items-center gap-1 ${inRemix(url) ? 'bg-fuchsia-600 text-white border-fuchsia-600' : 'border-fuchsia-200 text-fuchsia-600 hover:bg-fuchsia-50 dark:border-fuchsia-800 dark:text-fuchsia-300 dark:hover:bg-fuchsia-950'} disabled:opacity-40`}>
      {inRemix(url) ? <><Check className="w-3 h-3" /> remix</> : <><Sparkles className="w-3 h-3" /> remix</>}
    </button>
  );
  const renhance = async () => {
    if (!rprompt.trim()) { setRmsg('Type an instruction to enhance.'); return; }
    setRenhancing(true); setRmsg('');
    try {
      const r = await regenApi.enhancePrompt({ prompt: rprompt.trim(), mode: 'enhance', engine: rengine, vertical });
      if (r.data.text) setRprompt(r.data.text);
    } catch { setRmsg('Enhance unavailable.'); } finally { setRenhancing(false); }
  };
  const remixGenerate = async (finalPrompt: string) => {
    const urls = Object.keys(remix);
    setBusy(true); setRmsg('');
    try {
      const fd = new FormData();
      fd.append('prompt', finalPrompt.trim());
      fd.append('engine', rengine);
      fd.append('seconds', String(rsec));
      fd.append('video_urls', JSON.stringify(urls));
      fd.append('vertical', vertical);
      await regenApi.generate(fd);
      setRemix({}); setRprompt(''); setRscript(null);
      setRmsg('ok:started — track it in Requests + the Creative Team Room.');
    } catch (e: any) {
      setRmsg(e?.response?.data?.error || 'Failed to start.');
    } finally { setBusy(false); }
  };
  const runRemix = async () => {
    const urls = Object.keys(remix);
    if (!urls.length || !rprompt.trim()) { setRmsg('Pick at least one clip and type an instruction.'); return; }
    if (rplan && !rscript) {   // draft a timed script first, pause for approval
      setBusy(true); setRmsg('');
      try {
        const r = await regenApi.enhancePrompt({ prompt: rprompt.trim(), mode: 'script', engine: rengine, vertical });
        setRscript(r.data.text || rprompt.trim());
      } catch { setRmsg('Could not draft the script.'); } finally { setBusy(false); }
      return;
    }
    await remixGenerate(rprompt);
  };

  return (
    <div className="pb-16">
      <div className="flex items-center gap-2 mb-4">
        <span className="text-xs text-gray-500">Vertical:</span>
        <select value={vertical} onChange={e => setVertical(e.target.value)}
          className="rounded border border-border bg-card p-1 text-sm text-gray-900 dark:text-white">
          <option value="">All verticals</option>
          {LIB_VERTICALS.map(v => <option key={v} value={v}>{v}</option>)}
        </select>
        {!!refs.length && <span className="text-[11px] text-primary ml-auto"><Clapperboard className="inline w-3.5 h-3.5 mr-1 align-[-2px]" /> {refs.length} reference(s) selected — used on your next regenerate</span>}
      </div>
      {loading && <Centered><Loader2 className="w-6 h-6 animate-spin" /> Loading library…</Centered>}

      <Section title="Scraper winners (competitors)" hint={`${scraper.length} proven ${vertical} ads — remix any into a new video`}>
        {!scraper.length ? <p className="text-xs text-gray-400">No scraped winners for this vertical yet (seed via the daily scrape).</p> :
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
            {scraper.map((w, i) => (
              <div key={i} className={`rounded-lg border overflow-hidden bg-card ${inRemix(w.url) ? 'border-fuchsia-500 ring-1 ring-fuchsia-400' : 'border-border'}`}>
                <LibMedia url={w.url} mediaType={w.media_type} className="w-full aspect-[9/16] object-cover bg-black" />
                <div className="p-2 space-y-1">
                  <div className="text-[11px] text-gray-500">score {Math.round(w.score || 0)} · {w.hook?.slice(0, 36) || '—'}</div>
                  <RemixBtn url={w.url} label={w.hook || `winner ${Math.round(w.score || 0)}`} />
                </div>
              </div>
            ))}
          </div>}
      </Section>

      <Section title="Your winners (ROI ≥ 0%)" hint={`${ourWins.length} — mark as reference, re-use, or remix`}>
        {!ourWins.length ? <p className="text-xs text-gray-400">No winning creatives found.</p> :
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
            {ourWins.map(w => (
              <div key={w.creative_id} className={`rounded-lg border overflow-hidden bg-card ${inRemix(w.download_url) ? 'border-fuchsia-500 ring-1 ring-fuchsia-400' : isRef(w.creative_id) ? 'border-primary' : 'border-border'}`}>
                <LibMedia url={w.download_url} className="w-full aspect-[9/16] object-cover bg-black" />
                <div className="p-2 space-y-1">
                  <div className="text-[11px] font-medium truncate text-gray-900 dark:text-white">{w.filename}</div>
                  <div className="text-[11px] text-green-600">ROI {fmtRoi(w.roas)} · ${w.spend}</div>
                  <div className="flex items-center gap-1">
                    <RefBtn id={w.creative_id} filename={w.filename} />
                    <RemixBtn url={w.download_url} label={w.filename} />
                  </div>
                </div>
              </div>
            ))}
          </div>}
      </Section>

      <Section title="Your losers (regenerate these)" hint={`${losers.length} — preview, select one or many, or mark as reference`}>
        {!losers.length ? <p className="text-xs text-gray-400">No losing creatives found.</p> :
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
            {losers.map(l => (
              <div key={l.creative_id} className={`rounded-lg border overflow-hidden bg-card ${isSel(l.creative_id) ? 'border-primary ring-1 ring-primary' : isRef(l.creative_id) ? 'border-primary' : 'border-border'}`}>
                <div className="relative">
                  <LibMedia url={l.download_url} className="w-full aspect-[9/16] object-cover bg-black" />
                  <label className="absolute top-1 left-1 bg-white/90 dark:bg-gray-900/90 rounded px-1 py-0.5 flex items-center gap-1 text-[11px] cursor-pointer">
                    <input type="checkbox" checked={isSel(l.creative_id)} onChange={() => toggleSel(l.creative_id)} /> select
                  </label>
                </div>
                <div className="p-2 space-y-1">
                  <div className="text-[11px] font-medium truncate text-gray-900 dark:text-white">{l.filename}</div>
                  <div className="text-[11px] text-red-600">ROI {fmtRoi(l.roas)} · ${l.spend} · hook {pctOf(l.hook_rate)}</div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" onClick={() => onRegenerate(l.creative_id)} className="flex-1 text-xs">Regenerate →</Button>
                    <RefBtn id={l.creative_id} filename={l.filename} />
                    <RemixBtn url={l.download_url} label={l.filename} />
                  </div>
                </div>
              </div>
            ))}
          </div>}
      </Section>

      {/* sticky action bar for multi-select (losers → regenerate) */}
      {sel.size > 0 && Object.keys(remix).length === 0 && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 px-4 py-2 rounded-full border border-border bg-white dark:bg-gray-800 shadow-xl">
          <span className="text-sm font-medium">{sel.size} selected</span>
          {!!refs.length && <span className="text-[11px] text-primary">· {refs.length} ref(s)</span>}
          <Button onClick={regenerateSelected} disabled={busy || !!batchQueue} className="text-sm">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : sel.size === 1 ? 'Regenerate →' : `Regenerate ${sel.size} →`}
          </Button>
          <button onClick={() => setSel(new Set())} className="text-xs text-gray-400 hover:text-gray-600">clear</button>
        </div>
      )}

      {/* Remix composer — pick ANY clip(s) + an instruction → generate a new video */}
      {Object.keys(remix).length > 0 && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 w-[min(680px,92vw)] rounded-2xl border border-fuchsia-300 bg-white dark:bg-gray-800 shadow-2xl p-3">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <span className="text-sm font-semibold text-fuchsia-700 dark:text-fuchsia-400 inline-flex items-center gap-1"><Sparkles className="w-4 h-4" /> Remix {Object.keys(remix).length} clip(s)</span>
            <button onClick={() => setRemix({})} className="text-[11px] text-gray-400 hover:text-gray-600">clear</button>
            <label className="text-[11px] text-gray-600 flex items-center gap-1 ml-2 cursor-pointer" title="Draft a timed scene-by-scene script first, then approve to generate">
              <input type="checkbox" checked={rplan} onChange={e => { setRplan(e.target.checked); setRscript(null); }} /> plan as script
            </label>
            <label className="text-[11px] text-gray-600 flex items-center gap-1 ml-2" title="Output length (≤ 15s for Seedance)">
              length <input type="number" min={4} max={rengine === 'seedance' ? 15 : 148} value={rsec} onChange={e => setRsec(parseInt(e.target.value) || 10)} className="w-12 rounded border border-border bg-card p-0.5" />s
            </label>
            <div className="ml-auto"><EngineSelect value={rengine} onChange={setRengine} /></div>
          </div>
          <div className="flex flex-wrap gap-1 mb-2">
            {[`Make the hook better, keep the rest`, `Create a fresh variation of this`, `Use this style for ${vertical}`, `Mash these up into one better ${vertical} ad`].map(p => (
              <button key={p} onClick={() => setRprompt(p)} className="text-[11px] px-2 py-0.5 rounded-full border border-border text-gray-500 hover:bg-accent">{p}</button>
            ))}
          </div>
          {rscript != null && (
            <div className="mb-2 p-2 rounded-lg border border-primary/40 bg-primary/5">
              <div className="text-[11px] font-medium text-primary mb-1"><FileText className="inline w-3.5 h-3.5 mr-1 align-[-2px]" /> Draft script — review/edit, then approve:</div>
              <textarea value={rscript} onChange={e => setRscript(e.target.value)} rows={4}
                className="w-full rounded border border-border bg-card p-2 text-xs font-mono resize-y" />
              <div className="flex items-center gap-2 mt-1">
                <Button onClick={() => remixGenerate(rscript!)} disabled={busy} className="text-xs inline-flex items-center gap-1">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-3.5 h-3.5" /> Approve & Generate</>}
                </Button>
                <button onClick={() => setRscript(null)} className="text-[11px] text-gray-400 hover:text-gray-600">cancel</button>
              </div>
            </div>
          )}
          <div className="flex items-end gap-2">
            <textarea value={rprompt} onChange={e => setRprompt(e.target.value)} rows={2}
              placeholder="Tell it what to make from the selected clip(s)…"
              className="flex-1 rounded-xl border border-border bg-card p-2 text-sm resize-none" />
            <button onClick={renhance} disabled={renhancing || !rprompt.trim()}
              title="Enhance: rewrite into a richer, engine-optimized instruction"
              className="mb-0.5 px-2.5 py-2 rounded-lg border border-border text-sm text-gray-600 hover:bg-accent disabled:opacity-40">
              {renhancing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            </button>
            <Button onClick={runRemix} disabled={busy || !rprompt.trim()} className="mb-0.5">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (rplan && !rscript ? 'Draft script' : 'Generate')}
            </Button>
          </div>
          {rmsg && (() => { const ok = rmsg.startsWith('ok:'); return (
            <div className={`text-[11px] mt-1 flex items-center gap-1 ${ok ? 'text-green-600' : 'text-red-600'}`}>
              {ok ? <CheckCircle2 className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />}{ok ? rmsg.slice(3) : rmsg}
            </div>); })()}
        </div>
      )}

      {/* PREVIEW-FIRST gate for the multi-select batch — one dry-run + confirm per creative,
          "X left" in the confirm label so the queue's progress is visible. */}
      {batchQueue && batchQueue.length > 0 && (
        <GenerationPreview
          preview={batchPreview}
          loading={batchLoading}
          busy={batchLoading}
          error={batchLoading ? undefined : (batchPreview ? undefined : batchErr)}
          onConfirm={confirmBatch}
          onCancel={cancelBatch}
          confirmLabel={`Generate for real → (${batchQueue.length} left)`}
        />
      )}
    </div>
  );
}

function TreesList() {
  const [trees, setTrees] = useState<any[]>([]);
  const [open, setOpen] = useState<string | null>(null);
  const [tree, setTree] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    regenApi.listTrees().then(r => setTrees(r.data.data || [])).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!open) { setTree(null); return; }
    const t = setInterval(() => regenApi.getTree(open).then(r => setTree(r.data.data)), 4000);
    regenApi.getTree(open).then(r => setTree(r.data.data));
    return () => clearInterval(t);
  }, [open]);

  const Explainer = () => (
    <div className="rounded-xl border border-indigo-200 dark:border-indigo-900 bg-indigo-50/60 dark:bg-indigo-950/30 p-4 mb-4">
      <h2 className="text-base font-semibold text-gray-900 dark:text-white"><GitBranch className="inline w-3.5 h-3.5 mr-1 align-[-2px]" /> Path-to-Winner Planner</h2>
      <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
        A structured way to <b>find winners methodically instead of guessing</b>. You start from one creative and ORBIT
        branches it into systematic variations, one <b>axis</b> at a time:
      </p>
      <div className="grid sm:grid-cols-3 gap-2 mt-3 text-xs">
        <div className="rounded-lg bg-card border border-border p-2"><b>1 · Explore</b><div className="text-gray-500">Test different <b>hooks &amp; angles</b> — same offer, many openings.</div></div>
        <div className="rounded-lg bg-card border border-border p-2"><b>2 · Expand winners</b><div className="text-gray-500">Take what worked, vary the <b>avatar / setting</b>.</div></div>
        <div className="rounded-lg bg-card border border-border p-2"><b>3 · Scale</b><div className="text-gray-500">Multiply the proven branch; prune the losers.</div></div>
      </div>
      <p className="text-[11px] text-gray-500 mt-3">To start: go to <b>redraw-losers</b> → pick a creative → <b><GitBranch className="inline w-3.5 h-3.5 mr-1 align-[-2px]" /> Generate framework tree</b>. Each node becomes a variation you can launch &amp; A/B test; winning branches are expanded automatically.</p>
    </div>
  );

  if (loading && !trees.length) return <Centered><Loader2 className="w-6 h-6 animate-spin" /> Loading…</Centered>;
  if (!trees.length) return (
    <div className="max-w-2xl mx-auto">
      <Explainer />
      <div className="text-center text-sm text-gray-400 py-6">No plans yet — create one from <b>redraw-losers</b> to see it branch out here.</div>
    </div>
  );

  return (
    <div>
      <Explainer />
      <div className="grid md:grid-cols-2 gap-6">
      <div className="space-y-2">
        {trees.map(t => (
          <button key={t.tree_id} onClick={() => setOpen(t.tree_id)}
            className={`w-full text-left p-3 rounded-lg border ${open === t.tree_id ? 'border-primary bg-accent' : 'border-border hover:bg-accent/50'}`}>
            <div className="text-sm font-medium text-gray-900 dark:text-white truncate">{t.creative}</div>
            <div className="text-xs text-gray-500">{t.ready}/{t.nodes} nodes ready · {new Date(t.created_at).toLocaleString()}</div>
          </button>
        ))}
      </div>
      <div>
        {!tree ? <Centered>Select a tree.</Centered> : (
          <div className="space-y-3">
            {tree.phases.map((p: any) => (
              <div key={p.phase} className="p-3 rounded-lg border border-border bg-card">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-semibold text-gray-900 dark:text-white">{p.phase} phase</span>
                  <span className="text-xs text-gray-500">{p.ready}/{p.total} ready · split {p.split}</span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {p.nodes.map((n: any) => (
                    <div key={n.id} className="rounded-lg border border-border overflow-hidden bg-background">
                      {n.variant?.play_url
                        ? <MediaThumb url={n.variant.play_url} className="w-full aspect-[9/16] object-cover bg-black" />
                        : <div className="w-full aspect-[9/16] flex flex-col items-center justify-center text-[11px] text-gray-500 bg-black/5 p-2 text-center">
                            {n.status === 'running' ? <><Loader2 className="w-4 h-4 animate-spin mr-1" /> rendering…</>
                              : n.status === 'failed' ? <span className="text-red-500 inline-flex items-center gap-1"><X className="w-3 h-3" /> failed</span> : n.status}
                            {n.error && <span className="text-[10px] text-red-400 mt-1 line-clamp-3">{n.error}</span>}
                          </div>}
                      <div className="p-1.5">
                        <div className="flex items-center justify-between">
                          <span className="text-[11px] text-gray-600 dark:text-gray-300">{n.node}</span>
                          <StatusBadge status={n.status} />
                        </div>
                        {n.variant && !n.variant.approved && (n.variant.play_url) && (
                          <Button variant="outline" className="mt-1 w-full text-[11px] py-0.5"
                            onClick={() => regenApi.approve(n.id, n.variant.id).then(() => regenApi.getTree(open!).then(r => setTree(r.data.data)))
                              .catch((e: any) => alert('Could not pick this winner: ' + (e?.response?.data?.error || 'please try again')))}>
                            <Check className="w-3.5 h-3.5 mr-1" /> Pick winner
                          </Button>
                        )}
                        {/* retry a failed or stuck node */}
                        {(n.status === 'failed' || (n.status === 'running' && !n.variant)) && (
                          <Button variant="ghost" className="mt-1 w-full text-[11px] py-0.5"
                            onClick={() => regenApi.redispatch(n.id).then(() => regenApi.getTree(open!).then(r => setTree(r.data.data)))
                              .catch((e: any) => alert('Retry failed: ' + (e?.response?.data?.error || 'please try again')))}>
                            <RotateCw className="w-3.5 h-3.5 mr-1" /> Retry
                          </Button>
                        )}
                        {n.variant?.approved && <div className="text-[11px] text-green-600 mt-1 flex items-center gap-1"><Check className="w-3 h-3" /> picked</div>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      </div>
    </div>
  );
}

function ResultsList() {
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(() => {
    setLoading(true);
    // cache 2 min so revisiting Results is instant (was slow → looked broken)
    cached('outcomes', 120000, () => regenApi.outcomes()).then(r => setRows(r.data.data || [])).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading && !rows.length) return <Centered><Loader2 className="w-6 h-6 animate-spin" /> Loading…</Centered>;
  if (!rows.length) return <Centered>No approved variations yet. Approve one, launch the A/B, and its lift shows here.</Centered>;

  return (
    <div className="space-y-3">
      <div className="flex justify-end"><Button variant="ghost" onClick={load}><RefreshCw className="w-4 h-4 mr-1" /> Refresh</Button></div>
      {rows.map(o => {
        const lift = o.lift;
        const up = lift && lift.pct != null && lift.pct >= 0;
        return (
          <div key={o.id} className="p-3 rounded-lg border border-border bg-card">
            <div className="flex justify-between items-center">
              <div className="text-sm font-medium text-gray-900 dark:text-white">{o.filename || o.creative_id}</div>
              <span className="text-xs text-gray-500">{o.variation_type} · fixing {o.lagging_metric === 'roas' ? 'ROI' : o.lagging_metric}</span>
            </div>
            {o.measured && lift ? (
              <div className={`text-sm mt-1 font-semibold ${up ? 'text-green-600' : 'text-red-600'}`}>
                {lift.metric}: {fmtMaybe(lift.baseline)} → {fmtMaybe(lift.variant)}{' '}
                {lift.pct != null && <span className="inline-flex items-center gap-0.5">({up ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />} {Math.abs(lift.pct).toFixed(0)}%)</span>}
              </div>
            ) : (
              <div className="text-xs text-gray-500 mt-1">Awaiting live data — launch the A/B; lift appears once the variant accrues ad metrics.</div>
            )}
          </div>
        );
      })}
    </div>
  );
}
const fmtMaybe = (v: number) => (v != null && v < 1 && v > 0) ? `${(v * 100).toFixed(1)}%` : String(v);

function StatusBadge({ status }: { status: string }) {
  // Colour + a HUMAN label for every real status. clips_ready/dry_run used to fall through to a gray
  // "pending" pill showing the raw snake_case word — now they read plainly.
  const color: any = {
    running: 'bg-blue-100 text-blue-700', ready: 'bg-green-100 text-green-700',
    approved: 'bg-emerald-100 text-emerald-700', failed: 'bg-red-100 text-red-700',
    pending: 'bg-gray-100 text-gray-700', clips_ready: 'bg-amber-100 text-amber-700',
    dry_run: 'bg-indigo-100 text-indigo-700', cancelled: 'bg-gray-100 text-gray-600',
    merged: 'bg-green-100 text-green-700', stuck: 'bg-orange-100 text-orange-700',
  };
  const label: any = {
    running: 'Generating', ready: 'Ready', approved: 'Approved', failed: 'Failed', pending: 'Queued',
    clips_ready: 'Review clips', dry_run: 'Cost estimate', cancelled: 'Cancelled', merged: 'Merged', stuck: 'Stuck',
  };
  return <span className={`text-xs px-2 py-0.5 rounded-full ${color[status] || color.pending}`}>{label[status] || status}</span>;
}

const pctOf = (v: number | null) => v == null ? 'n/a' : `${Math.round(v * 100)}%`;
function Centered({ children }: any) {
  return <div className="flex items-center justify-center gap-2 text-sm text-gray-500 py-12">{children}</div>;
}

// Render library media correctly:
//  • IMAGE winners were black (image.jpg loaded into <video>) → render <img>, and use object-CONTAIN
//    so the whole ad is visible inside the 9:16 frame (object-cover was cropping/overflowing).
//  • VIDEOS use preload="none" so a gridful of S3 clips doesn't mass-download + churn/block the UI;
//    the clip loads only when the user hits play.
function LibMedia({ url, mediaType, s3key, className }: { url?: string; mediaType?: string; s3key?: string; className?: string; preload?: 'none' | 'metadata' }) {
  // stable server-generated poster (never a black card, no refresh) via the shared MediaThumb
  return <MediaThumb url={url} mediaType={mediaType} s3key={s3key} className={className} />;
}

// ── Studio: ChatGPT/Claude-style generation console (prompt + refs → video) ───
const STUDIO_ENGINES = [
  { id: 'auto', group: '', label: 'Auto — brain picks (cheapest)', hint: '' },
  { id: 'image', group: 'Image', label: 'Gemini Imagen', hint: 'text → photoreal image · Gemini → OpenAI → Ideogram · anti-slop' },
  { id: 'seedance', group: 'Kie', label: 'Seedance 2.0', hint: 'Kie Seedance · prompt + refs · 4–15s · native audio ($$)' },
  { id: 'fal-seedance', group: 'fal', label: 'Seedance', hint: 'same Seedance on fal — cheaper ($)' },
  { id: 'fal-kling', group: 'fal', label: 'Kling 2.0', hint: 'best price/quality ($)' },
  { id: 'fal-wan', group: 'fal', label: 'Wan 2.6', hint: 'budget (¢)' },
  { id: 'veo-extend', group: 'Premium', label: 'Veo 3.1 extend', hint: 'up to 148s · image→video ($$$$ — avoid for bulk)' },
  // A MODE, not a video engine: Venice writes the script AND speaks it (the only pair that will
  // read mature copy as approved), the picture still comes from a normal video lane and the clips
  // render SILENT so the words never enter a video model's prompt. Picking it is the same as
  // typing "go through the venice route" in the chat.
  { id: 'venice-uncensored', group: 'Voice', label: 'Venice — uncensored VO', hint: 'mature/uncensored script + voice · visuals stay compliant · silent clips + lip-sync' },
];

const ENGINE_ICON: Record<string, any> = {
  auto: Sparkles, image: ImageIcon, seedance: Video, 'fal-seedance': Video,
  'fal-kling': Film, 'fal-wan': Zap, 'veo-extend': Clapperboard,
  'venice-uncensored': Mic,
};

// Clean, roomy model picker with real icons (replaces the tiny grouped <select>).
// Purely presentational — same value/onChange contract as the old select.
function EngineSelect({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [open, setOpen] = useState(false);
  const ref = React.useRef<HTMLDivElement>(null);
  const cur = STUDIO_ENGINES.find(e => e.id === value) || STUDIO_ENGINES[0];
  const CurIcon = ENGINE_ICON[cur.id] || Sparkles;
  useEffect(() => {
    if (!open) return;
    const h = (ev: MouseEvent) => { if (ref.current && !ref.current.contains(ev.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);
  return (
    <div className="relative" ref={ref}>
      <button type="button" onClick={() => setOpen(o => !o)}
        className="flex items-center gap-1.5 pl-1.5 pr-2 py-1 rounded-xl border border-border bg-card hover:bg-accent/60 transition max-w-[220px]">
        <span className="flex h-5 w-5 items-center justify-center rounded-md bg-primary/15 text-primary shrink-0"><CurIcon className="w-3.5 h-3.5" /></span>
        <span className="text-sm font-medium text-gray-900 dark:text-white truncate">{cur.label}</span>
        <ChevronDown className={`w-3.5 h-3.5 text-gray-400 transition shrink-0 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute z-[200] bottom-full mb-2 left-0 w-[340px] max-h-[380px] overflow-auto rounded-2xl border border-border bg-neutral-900 shadow-2xl p-1.5 backdrop-blur-none" style={{ backgroundColor: '#16161c' }}>
          {STUDIO_ENGINES.map(e => {
            const Icon = ENGINE_ICON[e.id] || Sparkles;
            const active = e.id === value;
            return (
              <button key={e.id} type="button" onClick={() => { onChange(e.id); setOpen(false); }}
                className={`w-full flex items-start gap-3 px-2.5 py-2 rounded-xl text-left transition ${active ? 'bg-primary/15' : 'hover:bg-accent/60'}`}>
                <span className={`flex h-9 w-9 items-center justify-center rounded-lg shrink-0 ${active ? 'bg-primary text-white' : 'bg-white/5 text-gray-300'}`}><Icon className="w-5 h-5" /></span>
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-900 dark:text-white truncate">{e.label}</span>
                    {e.group && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-white/10 text-gray-400 shrink-0">{e.group}</span>}
                    {active && <Check className="w-4 h-4 text-primary ml-auto shrink-0" />}
                  </span>
                  {e.hint && <span className="block text-[11px] text-gray-500 mt-0.5 leading-snug">{e.hint}</span>}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Studio = a persistent ChatGPT-style thread. Scripts/ad-copies become referenceable artifacts,
// so "make a creative from that script" resolves server-side. Media artifacts show inline, big.
// Defined at MODULE level (NOT inside StudioChat's render). If these live inside the render, every
// 6s poll re-creates their component identity → React remounts every bubble → the native <details>
// "details" expander loses its open state and snaps shut. Stable identity keeps it open.
// COST CONFIRMATION is NOT a failure — the engine stopped BEFORE spending because the job is projected over
// the confirm threshold. Render it as a neutral prompt (not an alarming amber/AlertTriangle error bubble), with
// a real next action even when the request id is missing, and an inline error instead of a native alert().
const CostConfirmCard = ({ amount, note, rid }: { amount: string; note?: string; rid?: string }) => {
  const [starting, setStarting] = useState(false);
  const [startErr, setStartErr] = useState('');
  const detail = (note || '').split('—')[0]?.trim();
  const go = async () => {
    if (!rid) return;
    setStarting(true); setStartErr('');
    try { await regenApi.confirmCost(rid); }
    catch (e: any) { setStartErr(e?.response?.data?.error || e?.message || 'Could not start it — try again.'); setStarting(false); }
  };
  return (
    <div className="max-w-2xl rounded-2xl border border-primary/40 bg-primary/5 px-4 py-3">
      <div className="flex items-start gap-2">
        <DollarSign className="w-4 h-4 shrink-0 mt-0.5 text-primary" />
        <div className="min-w-0">
          <div className="text-sm text-gray-100 font-medium">Ready to generate — this will cost about ${amount}</div>
          <div className="text-xs text-gray-400 mt-1">Nothing has been spent yet.{detail ? ` ${detail}.` : ''}</div>
          {rid ? (
            <button onClick={go} disabled={starting}
              className="mt-2 inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 disabled:opacity-50">
              {starting ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Starting…</> : <><Play className="w-3 h-3 fill-current" /> Generate — spend ~${amount}</>}
            </button>
          ) : (
            <div className="mt-2 text-xs text-gray-400">Open this generation from <span className="text-gray-200">ORBIT Generated</span> to confirm and start it.</div>
          )}
          {startErr && <div className="mt-1.5 text-xs text-red-400">{startErr}</div>}
        </div>
      </div>
    </div>
  );
};

const ErrorBubble = ({ m }: { m: any }) => {
  const raw = String(m?.error || m?.text || '').trim() || 'Generation failed.';
  const lower = raw.toLowerCase();
  // COST CONFIRMATION — show the neutral prompt card, not an error.
  const costMatch = raw.match(/COST_CONFIRM_REQUIRED\|([\d.]+)\|([^\n]*)/);
  const rid = m?.data?.request_id || m?.request_id;
  if (costMatch) return <CostConfirmCard amount={costMatch[1]} note={costMatch[2]} rid={rid} />;
  const friendly = /provider|unavailable|snag/.test(lower)
    ? 'Our writing or rendering service is temporarily down. Try again in a few minutes.'
    : "Couldn't generate this.";
  const isLong = raw.length > 140 || raw !== friendly;
  return (
    <div className="max-w-2xl rounded-2xl border border-amber-500/40 bg-amber-500/10 px-4 py-3">
      <div className="flex items-start gap-2">
        <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 text-amber-500" />
        <div className="min-w-0">
          <div className="text-sm text-amber-200 leading-relaxed whitespace-pre-wrap break-words">{friendly}</div>
          {isLong && (
            <details className="mt-1.5 group">
              <summary className="text-[11px] text-amber-400/80 cursor-pointer select-none hover:text-amber-300">details</summary>
              <div className="mt-1 text-xs text-gray-400 whitespace-pre-wrap break-words leading-relaxed">{raw}</div>
            </details>
          )}
        </div>
      </div>
    </div>
  );
};

const US_STATES = ['Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming'];
// Follow-ups on the latest finished video — each posts a phrase the chat router already handles.
const FOLLOW_UPS: [string, string][] = [
  ['Make it shorter', 'make it 20 seconds'],
  ['New person', 'same script with a different person'],
  ['Price card on', 'add a price card and end card'],
];
const FollowUpChips = ({ onPick, disabled }: { onPick: (text: string) => void; disabled?: boolean }) => {
  const [picking, setPicking] = useState(false);
  const chip = 'inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full border border-border text-gray-300 hover:bg-accent disabled:opacity-40';
  return (
    <div className="mt-2 px-1 flex flex-wrap items-center gap-1.5 max-w-[40vh]">
      {picking ? (
        <span className="inline-flex items-center gap-1">
          <select autoFocus defaultValue="" aria-label="Pick a state" disabled={disabled}
            onChange={e => { if (e.target.value) { setPicking(false); onPick(`make the same video for ${e.target.value}`); } }}
            className="text-[11px] rounded-full border border-border bg-card px-2 py-1">
            <option value="" disabled>Pick a state…</option>
            {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <button type="button" onClick={() => setPicking(false)} aria-label="Cancel" className="p-0.5 rounded hover:bg-accent text-gray-400"><X className="w-3.5 h-3.5" /></button>
        </span>
      ) : (
        <button type="button" disabled={disabled} onClick={() => setPicking(true)} className={chip}>Same ad for another state</button>
      )}
      {FOLLOW_UPS.map(([label, text]) => (
        <button key={label} type="button" disabled={disabled} onClick={() => onPick(text)} className={chip}>{label}</button>
      ))}
    </div>
  );
};

const MediaBubble = ({ m, onRunReal, onEdit, onReview, runningReal, onFollowUp, followUpBusy, onPreviewImage }:
  { m: any; onRunReal?: (m: any) => void; onEdit?: (m: any) => void; onReview?: () => void; runningReal?: boolean;
    onFollowUp?: (text: string) => void; followUpBusy?: boolean; onPreviewImage?: () => void }) => {
  const v = (m.variants || [])[0];
  const url = v?.play_url;
  const isImg = !!url && /\.(jpg|jpeg|png|webp|gif)(\?|#|$)/i.test(url);
  const isAudio = !!url && /\.(mp3|wav|m4a|aac|ogg)(\?|#|$)/i.test(url);
  // BLOCKED-BUT-VIEWABLE: the QA gate blocked delivery (status=failed) but AE surfaced a viewable render.
  // Show it, clearly flagged with the gate reasons, so the user can watch it and diagnose — "let me see
  // what is failing". A truly output-less failure (no url) still shows the plain error below.
  const blocked = m.status === 'failed' && !!url;
  // A DRY RUN IS FINISHED, NOT RUNNING. It ends on status 'dry_run' with no variants and therefore
  // no url, which made the condition below (`!url && status !== 'failed'`) true forever: the bubble
  // span "generating…" indefinitely, and Stop did nothing because the request was already terminal.
  const isDry = m.status === 'dry_run' || !!(m.data?.dry_run);
  const gateReasons = String(m.error || v?.whats_changed || 'quality gate blocked delivery').replace(/\s*\|\s*/g, ' · ');
  // A dry run is a PLAN, not a render → the pre-flight card (normalized AE report + Edit / Run-for-real),
  // NOT a finished/failed video bubble. Falls back to the legacy dry_run_preview when preflight is absent.
  if (isDry) return (
    <PreflightCard preflight={m.preflight} legacy={m.dry_run_preview || m.data?.dry_run_preview}
      busy={runningReal} onEdit={() => onEdit?.(m)} onRunReal={() => onRunReal?.(m)} onPreviewImage={onPreviewImage} />
  );
  // REVIEW-FIRST in the chat: a clips_ready run staged its clips (in review_clips, surfaced by /studio/thread)
  // but has no combined video yet. Render the same ClipReview strip the gallery uses — approve to combine or
  // regenerate the bad ones — so the chat never spins "generating…" forever on a job that is waiting on YOU.
  if (m.status === 'clips_ready') {
    const rid = m.data?.request_id || m.request_id;
    const review = m.review_clips || m.directive?.review_clips || [];
    if (rid && Array.isArray(review) && review.length) {
      return <ClipReview g={{ id: rid, directive: { review_clips: review } }} onChange={() => onReview?.()} />;
    }
  }
  return (
    <div className="rounded-2xl border border-border bg-card p-2 inline-block">
      {m.status === 'running' || (!url && m.status !== 'failed') ? (
        <div className="flex flex-col items-center gap-3 text-sm text-gray-400 h-[62vh] max-h-[62vh] aspect-[9/16] justify-center px-4 text-center">
          {/* STUCK: a job still 'running' after 12 min (same threshold as the Requests tab) is almost
              certainly orphaned. Say so and offer Retry, instead of spinning "generating…" forever. */}
          {(m.created_at && (Date.now() - new Date(m.created_at).getTime()) > 12 * 60 * 1000)
            ? <span className="inline-flex items-center gap-2 text-orange-400"><AlertTriangle className="w-5 h-5" /> Taking longer than usual — it may be stuck.</span>
            : <span className="inline-flex items-center gap-2"><Loader2 className="w-5 h-5 animate-spin" /> generating…</span>}
          <div className="flex items-center gap-2">
            {/* Retry a stuck job — re-dispatches to ORBIT. */}
            {(m.created_at && (Date.now() - new Date(m.created_at).getTime()) > 12 * 60 * 1000) && (m.data?.request_id || m.request_id) && (
              <button
                onClick={() => {
                  const rid = m.data?.request_id || m.request_id;
                  if (!window.confirm('Retry this generation? It re-dispatches to ORBIT (new credits may be spent).')) return;
                  regenApi.redispatch(rid).then(() => onReview?.()).catch(() => {});
                }}
                className="px-2.5 py-1 rounded-lg border border-primary/50 text-primary text-xs hover:bg-primary/10">
                Retry
              </button>
            )}
            {/* STOP this generation end-to-end — ORBIT aborts before the next paid step. */}
            {(m.data?.request_id || m.request_id) && (
              <button
                onClick={() => {
                  const rid = m.data?.request_id || m.request_id;
                  if (!window.confirm('Stop this generation? A clip already rendering still bills; no further clips will start.')) return;
                  regenApi.cancel(rid).catch(() => {});
                }}
                className="px-2.5 py-1 rounded-lg border border-red-500/40 text-red-500 text-xs hover:bg-red-500/10">
                Stop
              </button>
            )}
          </div>
        </div>
      ) : m.status === 'failed' && !url ? (
        <div className="flex items-center gap-2 text-sm text-red-500 h-[24vh] w-[24vh] justify-center px-4 text-center"><AlertTriangle className="w-5 h-5 shrink-0" /> {m.error || 'failed'}</div>
      ) : (
        <div className="relative">
          {isAudio
            ? <div className="flex flex-col items-center justify-center gap-3 h-[24vh] w-[40vh] max-w-full rounded-xl bg-black/60 px-4 text-gray-400"><Mic className="w-8 h-8" /><audio src={url} controls className="w-full" /></div>
            : isImg
            ? <img src={url} alt="" className="h-[62vh] max-h-[62vh] aspect-[9/16] object-contain rounded-xl bg-black" loading="lazy" />
            : <MediaThumb url={url} className="h-[62vh] max-h-[62vh] aspect-[9/16] object-contain rounded-xl bg-black" />}
          {blocked && (
            <span className="absolute top-2 left-2 inline-flex items-center gap-1 rounded-full bg-amber-500/90 px-2 py-0.5 text-[10px] font-semibold text-black ring-1 ring-amber-300/60 shadow">
              <AlertTriangle className="w-3 h-3" /> blocked · needs fix
            </span>
          )}
        </div>
      )}
      {blocked && (
        <div className="mt-2 px-1 max-w-[40vh]">
          <div className="text-[11px] text-amber-500/90 whitespace-pre-wrap break-words"><span className="font-semibold">Blocked by QA — needs fix:</span> {gateReasons}</div>
          <a href={toDownloadUrl(v?.download_url || url, v?.filename)} download className="mt-1 inline-flex items-center gap-1 text-xs text-primary hover:underline"><Download className="w-4 h-4" /> download to review</a>
        </div>
      )}
      {url && m.status !== 'running' && m.status !== 'failed' && (
        <div className="mt-2 px-1">
          <div className="flex items-center gap-3">
            <a href={toDownloadUrl(v?.download_url || url, v?.filename)} download className="inline-flex items-center gap-1 text-xs text-primary hover:underline"><Download className="w-4 h-4" /> download</a>
            {v?.whats_changed && <span className="text-[11px] text-gray-500 truncate max-w-[40vh]">{v.whats_changed}</span>}
          </div>
          {modelsLine(v?.models) && <div className="mt-1 text-[10px] text-gray-500 font-mono truncate max-w-[40vh]" title={modelsLine(v?.models)}>{modelsLine(v?.models)}</div>}
        </div>
      )}
      {onFollowUp && url && m.status === 'ready' && <FollowUpChips onPick={onFollowUp} disabled={followUpBusy} />}
    </div>
  );
};

// One conversation row in the Studio sidebar — click to open, hover for a kebab menu with
// Rename (inline) and Delete (like Claude Code's chat panel). Module-level so a sidebar
// re-render (after each send / poll) never remounts the row mid-edit.
function ThreadRow({ t, active, onOpen, onRename, onDelete }:
  { t: any; active: boolean; onOpen: () => void; onRename: (title: string) => void; onDelete: () => void }) {
  const [menu, setMenu] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<string>(t.title || '');
  const ref = React.useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!menu) return;
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setMenu(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [menu]);
  const startRename = () => { setDraft(t.title || ''); setEditing(true); setMenu(false); };
  const commit = () => { const v = draft.trim(); if (v && v !== t.title) onRename(v); setEditing(false); };

  if (editing) return (
    <input autoFocus value={draft} onChange={(e) => setDraft(e.target.value)}
      onKeyDown={(e) => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') setEditing(false); }}
      onBlur={commit}
      className="block w-full rounded-lg border border-primary/50 bg-card px-2.5 py-2 text-xs text-white outline-none" />
  );

  return (
    <div ref={ref} className="group relative">
      <button onClick={onOpen} title={t.title || 'New chat'}
        className={`block w-full rounded-lg pl-2.5 pr-7 py-2 text-left text-xs transition ${active ? 'bg-primary/15 text-primary' : 'text-gray-300 hover:bg-accent'}`}>
        <span className="block truncate">{t.title || 'New chat'}</span>
        {t.last_message_at && <span className="block mt-0.5 truncate text-[10px] text-gray-500">{fmtStamp(t.last_message_at)}</span>}
      </button>
      <button onClick={(e) => { e.stopPropagation(); setMenu(m => !m); }} aria-label="Thread options"
        className={`absolute right-1 top-1/2 -translate-y-1/2 rounded-md p-1 text-gray-400 hover:text-gray-100 hover:bg-white/10 transition ${menu ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
        <MoreVertical className="w-3.5 h-3.5" />
      </button>
      {menu && (
        <div className="absolute right-1 top-9 z-[200] w-32 rounded-lg border border-border shadow-xl p-1" style={{ backgroundColor: '#16161c' }}>
          <button onClick={startRename}
            className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-xs text-gray-200 hover:bg-white/10 text-left">
            <Pencil className="w-3.5 h-3.5" /> Rename
          </button>
          <button onClick={() => { setMenu(false); onDelete(); }}
            className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-xs text-red-400 hover:bg-red-500/10 text-left">
            <Trash2 className="w-3.5 h-3.5" /> Delete
          </button>
        </div>
      )}
    </div>
  );
}

function StudioChat() {
  const [messages, setMessages] = useState<any[]>(() => peek<any[]>('studio:thread') || []);
  const [prompt, setPrompt] = useState('');
  const [engine, setEngine] = useState('auto');
  const [seconds, setSeconds] = useState(0);   // 0 = Auto → runtime sized from the script (not a forced 15s)
  const [aspect, setAspect] = useState('9:16');
  const [resolution, setResolution] = useState('480p');   // default 480p (cheaper/faster); user can bump it
  const [seedanceModel, setSeedanceModel] = useState('');  // '' = Seedance 2.0 (default) · 'bytedance/seedance-2-5' = 2.5
  // Gemini Omni has NO 480p tier — its cheapest valid resolution is 360p. Every other model keeps its
  // current default (480p); only Omni remaps to its own valid tiers (360p/720p/1080p/4k, default 360p).
  const isOmni = seedanceModel === 'google/gemini-omni-flash-1-1';
  const resChoices = isOmni ? ['360p', '720p', '1080p', '4k'] : ['480p', '720p', '1080p', '4k'];
  const effectiveResolution = resChoices.includes(resolution) ? resolution : resChoices[0];
  const [audio, setAudio] = useState(true);
  const [captions, setCaptions] = useState(true);   // explicit user choice — burn subtitles or not
  // Default OFF: speak the user's script verbatim (the script-is-core rule). Turning it ON lets ORBIT
  // rewrite the hook — an explicit opt-in, never the silent default.
  const [improveHook, setImproveHook] = useState(false);
  const [genPath, setGenPath] = useState('auto');   // auto | scratch (t2v) | avatar (real library clip)
  const [refImage, setRefImage] = useState<File | null>(null);   // optional CHARACTER-reference image → i2v
  const refImageRef = React.useRef<HTMLInputElement>(null);
  const [sending, setSending] = useState(false);
  const [enhancing, setEnhancing] = useState(false);
  const [optionsOpen, setOptionsOpen] = useState(false);   // ⚙ Options accordion — collapsed by default
  // GENERATE-FIRST: the obvious action makes a video. Estimating cost first is an explicit opt-in, not the
  // default — otherwise users click the primary button and only ever get plan cards ("Preview-forever" trap).
  // Spend stays protected: the backend cost-confirm gate still fires before any paid provider call over the
  // threshold, so generate-by-default cannot silently run up a large bill. dry_run still flows unchanged when
  // the user chooses "Estimate cost"; only the video-make lane reads it (script/copy turns ignore it).
  const [dryRun, setDryRun] = useState(false);
  const [reviewFirst, setReviewFirst] = useState(true);   // DEFAULT ON (opt-out): render clips → pause for review before combining, so one bad clip never forces a whole re-render. Only the reference-video lane acts on it (others ignore it = deliver as before); API callers that don't use this composer never send it.
  // SIMPLE MODE (default) hides the technical controls; Advanced = the full composer, unchanged.
  const [advanced, setAdvanced] = useState(() => { try { return localStorage.getItem('studio:advanced') === '1'; } catch { return false; } });
  const toggleAdvanced = () => {
    const next = !advanced;
    try { localStorage.setItem('studio:advanced', next ? '1' : '0'); } catch { /* storage blocked — mode just won't persist */ }
    // Back to simple → reset the hidden controls so simple mode always sends today's defaults.
    if (!next) {
      setEngine('auto'); setGenPath('auto'); setSeconds(0); setResolution('480p'); setSeedanceModel('');
      setAudio(true); setCaptions(true); setImproveHook(false); setReviewFirst(true); setOptionsOpen(false);
    }
    setAdvanced(next);
  };
  const [realRunId, setRealRunId] = useState('');   // the dry-run bubble whose "Run for real" is in flight
  const [err, setErr] = useState('');
  const [threadId, setThreadId] = useState<string>(() => localStorage.getItem('studio:thread_id') || '');
  const [threads, setThreads] = useState<any[]>([]);   // past conversations for the sidebar
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const taRef = React.useRef<HTMLTextAreaElement>(null);

  // Auto-grow the composer from 1 → ~6 rows, then let it scroll internally. Runs on every
  // keystroke and resets to a single row after send (prompt cleared).
  useEffect(() => {
    const el = taRef.current; if (!el) return;
    el.style.height = 'auto';
    const max = 24 * 6 + 16; // ~6 rows (24px line-height) + vertical padding
    el.style.height = Math.min(el.scrollHeight, max) + 'px';
    el.style.overflowY = el.scrollHeight > max ? 'auto' : 'hidden';
  }, [prompt]);

  // Durable thread (Postgres) → survives refresh/tab-switch. Poll for live media status.
  const load = useCallback((isPoll = false) => {
    if (isPoll && typeof document !== 'undefined' && document.hidden) return;
    regenApi.studioThread(threadId || undefined).then(r => {
      const list = r.data.messages || [];
      setMessages(list); put('studio:thread', list);
      // Resolve the backend's default thread id (u:<userId>) once, so the sidebar can highlight it
      // and later chat/thread calls target it explicitly. Only until we've picked a thread.
      if (r.data.thread_id && !threadId) setThreadId(r.data.thread_id);
    }).catch(() => {});
  }, [threadId]);
  useEffect(() => { load(); const t = setInterval(() => load(true), 6000); return () => clearInterval(t); }, [load]);
  // Conversations list (sidebar) — refreshed on mount, after each send, and on new chat.
  const loadThreads = useCallback(() => {
    regenApi.studioThreads().then(r => setThreads(r.data.threads || [])).catch(() => {});
  }, []);
  useEffect(() => { loadThreads(); }, [loadThreads]);

  // ── Ad Library hand-off ───────────────────────────────────────────────────────────────────────
  // Someone clicked "Recreate in Studio" on an ad card over in the Ad Library and landed here with
  // ?handoff=<token>. Redeem it server-side, then open a FRESH conversation whose first turn carries
  // the clip — which lands on handleStudioChat's recreate gate: it posts the plan ("same script,
  // same location, only the person changes") and waits. Nothing generates until the user says go.
  //
  // The redemption is single-use, so this must fire exactly once: the ref survives React's
  // development double-invoke, and the token is stripped from the URL as soon as it's read so a
  // refresh doesn't try to spend it again. A token that was already spent means the conversation is
  // already open — say so quietly instead of showing a red error over a working chat.
  const handoffRef = React.useRef(false);
  useEffect(() => {
    if (handoffRef.current) return;
    const token = new URLSearchParams(window.location.search).get('handoff');
    if (!token) return;
    handoffRef.current = true;

    const url = new URL(window.location.href);
    url.searchParams.delete('handoff');
    window.history.replaceState({}, '', url);

    (async () => {
      setSending(true);
      try {
        const r = await regenApi.studioHandoff(token);
        const d = r.data;

        // A hand-off is a new subject, so it gets its own conversation. Clearing the
        // local id is NOT enough — the server resolves an empty thread_id to the
        // user's default thread, so the ad would land in whatever chat they were
        // last in, and a pending gate still open there would swallow this turn and
        // burn the single-use token. Name an explicit thread instead.
        const freshThread = `handoff:${token}`;
        setThreadId(freshThread);
        localStorage.setItem('studio:thread_id', freshThread);

        // The Ad Library may already have done the thinking: a script someone wrote
        // and approved, claims they ruled unsupportable, a note on what must change.
        // Carry it into the FIRST turn so the conversation starts from the buyer's
        // work rather than from the raw competitor ad — otherwise the hand-off
        // silently throws that work away and offers a plain clone.
        const brief: string[] = [];
        if (d.change_before_use) brief.push(`Before we use it: ${d.change_before_use}`);
        if (d.do_not_say?.length) brief.push(`Do NOT say any of these — they were ruled unsupportable: ${d.do_not_say.join(' · ')}`);
        // Burned-in text is the one instruction that changes what the engine must
        // NOT do. Put it first — a brief the model reads late is a brief it drops.
        if (d.burned_in_captions?.present || d.burned_in_captions?.uncertain) {
          const words = (d.burned_in_captions.doNotRenderOnScreen || []).slice(0, 8);
          brief.unshift(
            (d.burned_in_captions.uncertain
              ? `IMPORTANT — we could not verify whether the source has text burned into the picture, so assume it does. `
              : `IMPORTANT — the source clip has text burned into the picture. `)
            + `Do NOT reproduce any on-screen text: `
            + `render the video clean and leave captions to be added afterwards. `
            + (words.length ? `Specifically do not put these on screen: ${words.join(' · ')}. ` : '')
            + `Do not copy the first frame as a visual reference — on this kind of ad it is the hook card, not the scene.`);
        }
        if (d.approved_script) brief.push(`Our approved script (use THIS, not the original's words):\n${d.approved_script}`);
        else if (d.draft_script) brief.push(`Draft script from our analysis (not yet approved):\n${d.draft_script}`);
        if (d.plan?.campaign) brief.push(`This is for campaign ${d.plan.campaign}${d.plan.targetCpl ? `, target CPL $${d.plan.targetCpl}` : ''}.`);
        // The payload also carries the ad's own copy, hook and angle. Passing only
        // the clip meant the engine re-derived all of that from the page name.
        const srcAd: any = d.ad || {};
        const facts = [
          srcAd.headline && `headline: ${srcAd.headline}`,
          srcAd.hook && `hook: ${srcAd.hook}`,
          srcAd.angle && `angle: ${srcAd.angle}`,
          srcAd.daysRunning && `it has run ${srcAd.daysRunning} days`,
        ].filter(Boolean);
        if (facts.length) brief.push(`What the original ad is: ${facts.join(' · ')}.`);
        if (srcAd.primaryText) brief.push(`Its primary text: "${String(srcAd.primaryText).slice(0, 400)}"`);

        // WHICH LANE. Sending recreate_source_url opens the faithful-clone gate
        // unconditionally — "same location, same wardrobe, only the person
        // changes". That is only coherent for a single held take. On a cut
        // montage it produced one talking head with no b-roll, no captions and
        // none of the funnel screen-recordings that made the original convert.
        // So a montage goes down the b-roll lane with its BEATS instead, and the
        // clip travels as a reference link rather than a clone source.
        const isClone = d.mode === 'recreate';
        // A still must not be handed a video instruction. The engine routes on
        // the words it reads, so say "static image ad" plainly and never mention
        // b-roll or cutaways for one.
        const isImage = d.mode === 'image' || d.media_type === 'image';

        if (!isClone && !isImage && d.structure?.kind && d.structure.kind !== 'single_take') {
          brief.unshift(
            `This ad is NOT a single take — ${d.structure.why} `
            + `Rebuild the STRUCTURE with our own footage: UGC + b-roll cutaways, same beat `
            + `order and pacing. Do not generate one continuous talking head.`);
          if (d.beats?.length) {
            brief.push('Beat structure to follow:\n'
              + d.beats.map((b: any) => `  ${b.t} — ${b.beat} (${b.purpose})`).join('\n'));
          }
          brief.push(`Reference clip (watch, do not clone): ${d.source_url}`);
        }

        // Shots no video model can make. This changes what the buyer must supply,
        // so it goes near the top — discovering it at delivery is too late.
        if (d.cannot_generate?.elements?.length) {
          brief.splice(1, 0,
            'PARTS OF THIS AD CANNOT BE GENERATED — they must be supplied:\n'
            + d.cannot_generate.elements
                .map((e: any) => `  • ${e.what} (at ~${e.at_seconds}s) — ${e.what_to_do}`)
                .join('\n')
            + '\nBuild everything else and leave these as gaps in the edit; do not approximate them.');
        }

        const opener = isClone
          ? 'Recreate this'
          : isImage
            ? (d.suggested_prompt
               || 'Make a STATIC IMAGE ad (not a video) modelled on this reference')
            : (d.suggested_prompt
               || 'Rebuild this ad as UGC + b-roll cutaways using our own footage');

        const first = await regenApi.studioChat({
          thread_id: freshThread,
          message: brief.length ? `${opener}\n\n${brief.join('\n\n')}` : opener,
          vertical: d.vertical || undefined,
          // Say the lane in a FIELD. The router picks the image lane on
          // request_type; prose ("Make a STATIC IMAGE ad — not a video") lost to
          // the talking-head default and offered to render it with Seedance.
          ...(isImage ? { request_type: 'image' } : {}),
          // Only a real single take is handed over as a CLONE source.
          ...(isClone ? {
            recreate_source_url: d.source_url,
            recreate_label: d.label,
            recreate_thumb: d.source_url,
          } : {}),
        });
        if (first.data.thread_id) {
          setThreadId(first.data.thread_id);
          localStorage.setItem('studio:thread_id', first.data.thread_id);
        }
        setTimeout(() => { load(); loadThreads(); }, 300);
      } catch (e: any) {
        const body = e?.response?.data || {};
        setErr(body.already_open
          ? 'That ad is already open in a conversation below.'
          : (body.error || 'Could not open that ad — send it again from the Ad Library.'));
      } finally { setSending(false); }
    })();
  }, [load, loadThreads]);
  // Only scroll to the bottom when the USER sends a message (their own send action).
  // Never yank the user down on incoming assistant messages, streaming updates, or errors —
  // they stay wherever they've scrolled so error boxes don't get scrolled away.
  const scrollOnNextRef = React.useRef(false);
  const didInitScrollRef = React.useRef(false);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    // On the FIRST populated load, jump straight to the newest message (the thread opens at the
    // bottom, like every chat app) — instant, no smooth animation. Then only auto-scroll on the
    // user's own send; incoming poll updates never yank the view.
    if (!didInitScrollRef.current && messages.length) {
      didInitScrollRef.current = true;
      el.scrollTo({ top: el.scrollHeight });
      return;
    }
    if (!scrollOnNextRef.current) return;
    scrollOnNextRef.current = false;
    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, [messages]);

  const enhance = async () => {
    if (!prompt.trim()) { setErr('Type a message to enhance.'); return; }
    setEnhancing(true); setErr('');
    try {
      const r = await regenApi.enhancePrompt({ prompt: prompt.trim(), mode: 'enhance', engine, seconds });
      if (r.data.text) setPrompt(r.data.text);
    } catch { setErr('Enhance unavailable.'); } finally { setEnhancing(false); }
  };

  const post = async (text: string, opts: { dryRun?: boolean } = {}) => {
    if (!text.trim() || sending) return;
    // Dry Run sends the SAME request with dry_run:true (AE previews + prices, spends nothing). Real Run
    // omits the flag entirely = today's behavior. A "Run for real" click passes an explicit override.
    const useDry = opts.dryRun !== undefined ? opts.dryRun : dryRun;
    // "stop" / "cancel" in chat = kill every generation still running in this thread, end to end.
    // Handled client-side so it works even while the router/engine is busy.
    if (/^(stop|cancel|abort|halt)\b/i.test(text.trim())) {
      const running = messages.filter((m: any) => m.kind === 'video' && m.status === 'running'
        && (m.data?.request_id || m.request_id));
      if (!running.length) { setErr('Nothing is generating right now.'); return; }
      setSending(true); setErr('');
      // Count what ACTUALLY cancelled. Swallowing failures and then claiming "stopped" would be a
      // confident lie — a stale/finished id updates zero rows and the job keeps spending.
      const results = await Promise.all(running.map((m: any) =>
        regenApi.cancel(m.data?.request_id || m.request_id).then(() => true).catch(() => false)));
      const okN = results.filter(Boolean).length;
      setTimeout(() => load(), 400);
      setSending(false);
      // Cancellation is COOPERATIVE: the engine checks between clips, so a clip already rendering
      // still bills. Promise "no further clips will start", never "no further credits spent".
      setErr(okN === running.length
        ? `Stop requested for ${okN} generation${okN > 1 ? 's' : ''} — no further clips will start.`
        : `Stop requested for ${okN} of ${running.length} — ${running.length - okN} could not be cancelled (may already be finishing).`);
      return;
    }
    setSending(true); setErr('');
    scrollOnNextRef.current = true;
    setMessages(m => [...m, { id: 'tmp-' + Date.now(), role: 'user', kind: 'text', text }]);
    try {
      if (refImage) {
        // CHARACTER-REFERENCE image + script → end-to-end video THROUGH the chat (/studio/chat multipart),
        // so the image persists in the thread next to the prompt AND reaches AE as image_urls (image-to-
        // video). The video bubble + live status land in this same thread on reload.
        const fd = new FormData();
        fd.append('message', text);
        // Downscale a full-size phone photo BEFORE upload so it doesn't trip the "file too large" cap.
        fd.append('images', await downscaleImage(refImage));
        if (threadId) fd.append('thread_id', threadId);
        fd.append('engine', engine);
        if (seconds > 0) fd.append('seconds', String(seconds));
        fd.append('aspect_ratio', aspect);
        fd.append('resolution', effectiveResolution);
        fd.append('generate_audio', String(audio));
        fd.append('captions', String(captions));
        fd.append('allow_rewrite', String(improveHook));
        if (genPath) fd.append('gen_path', genPath);
        if (seedanceModel) fd.append('seedance_model', seedanceModel);   // first-class model select (2.0 default)
        if (useDry) fd.append('dry_run', 'true');   // preview the plan + cost, spend nothing
        if (reviewFirst) fd.append('review_only', 'true');   // render clips → pause for review before stitch
        const r = await regenApi.studioChatForm(fd);
        if (r.data.thread_id && !threadId) { setThreadId(r.data.thread_id); localStorage.setItem('studio:thread_id', r.data.thread_id); }
        setRefImage(null); if (refImageRef.current) refImageRef.current.value = '';
        setTimeout(() => { load(); loadThreads(); }, 300);
      } else {
        const r = await regenApi.studioChat({ message: text, thread_id: threadId || undefined, engine,
          seedance_model: seedanceModel || undefined,
          seconds: seconds > 0 ? seconds : undefined, aspect_ratio: aspect, resolution: effectiveResolution, generate_audio: audio, captions,
          allow_rewrite: improveHook, gen_path: genPath, dry_run: useDry || undefined, review_only: reviewFirst || undefined });
        if (r.data.thread_id && !threadId) { setThreadId(r.data.thread_id); localStorage.setItem('studio:thread_id', r.data.thread_id); }
        setTimeout(() => { load(); loadThreads(); }, 300);
      }
    } catch (e: any) {
      const msg = e?.response?.data?.error || 'Failed to reach Studio — try again.';
      setErr(msg);
      // TERMINATE the thinking avatar on a hard failure: append an assistant message so the last
      // message is no longer the user's, else `waitingReply` stays true and the avatar spins forever
      // (a hard POST failure never stores a server-side reply, so the 6s poll can't self-heal it).
      setMessages(m => [...m, { id: 'err-' + Date.now(), role: 'assistant', kind: 'error', text: msg, status: 'failed' }]);
    } finally { setSending(false); }
  };
  const send = () => { const t = prompt.trim(); if (!t) { setErr('Type a message.'); return; } setPrompt(''); post(t); };

  // The brief that produced a given assistant bubble = the nearest preceding user message — skipping a
  // pure confirmation ("yes" / "go") so a route-confirm gate re-runs the real brief, not the "yes".
  const originalUserTextFor = (m: any): string => {
    const idx = messages.findIndex((x: any) => x.id === m.id);
    if (idx < 0) return '';
    const isConfirm = (t: string) => /^(y|yes|yeah|yep|ya|ok|okay|k|go|sure|do it|confirm|confirmed|proceed|generate|make it)\W*$/i.test(t.trim());
    let firstUser = '';
    for (let i = idx - 1; i >= 0; i--) {
      const x = messages[i];
      if (x.role !== 'user' || !x.text) continue;
      if (!firstUser) firstUser = x.text as string;   // remember the immediate one as a fallback
      if (!isConfirm(x.text as string)) return x.text as string;
    }
    return firstUser;
  };
  // "Run for real" on a pre-flight card → re-dispatch the SAME brief through the existing send path with
  // dry_run OFF. No new endpoint, no stored-plan replay — the same request, now for real.
  const runReal = async (m: any) => {
    const text = originalUserTextFor(m);
    if (!text) { setErr('Could not find the original brief — retype it and send with Real Run off.'); return; }
    setRealRunId(m.id);
    try { await post(text, { dryRun: false }); } finally { setRealRunId(''); }
  };
  // "Edit" → drop the brief back into the composer (still in Dry Run) so the user can adjust and preview again.
  const editFromPreview = (m: any) => {
    const text = originalUserTextFor(m);
    if (text) setPrompt(text);
    setDryRun(true); setErr('');
    setTimeout(() => taRef.current?.focus(), 0);
  };
  const newChat = () => {
    const t = 't:' + Date.now();
    setThreadId(t); localStorage.setItem('studio:thread_id', t); setMessages([]); put('studio:thread', []);
    didInitScrollRef.current = false; loadThreads();
  };
  // Switch to a past conversation — load its full history (GET /thread?thread_id=…) and keep chatting
  // in it with context. Setting threadId re-runs `load` for that thread via the effect above.
  const openThread = (tid: string) => {
    if (tid === threadId) return;
    setThreadId(tid); localStorage.setItem('studio:thread_id', tid);
    setMessages([]); put('studio:thread', []); didInitScrollRef.current = false;
  };
  // Rename a conversation — optimistic local update, PATCH persists it; reload on failure.
  const renameThread = (tid: string, title: string) => {
    const t = title.trim();
    if (!t) return;
    setThreads(ts => ts.map(x => x.thread_id === tid ? { ...x, title: t } : x));
    regenApi.studioThreadRename(tid, t).catch(() => loadThreads());
  };
  // Delete a conversation — confirm, drop it from the sidebar, DELETE persists it; if it was the
  // active chat, switch to a fresh empty thread. Reload on failure to restore truth.
  const deleteThread = (tid: string) => {
    if (!window.confirm('Delete this conversation? This removes its messages and cannot be undone.')) return;
    setThreads(ts => ts.filter(x => x.thread_id !== tid));
    regenApi.studioThreadDelete(tid).catch(() => loadThreads());
    if (tid === threadId) {
      const nt = 't:' + Date.now();
      setThreadId(nt); localStorage.setItem('studio:thread_id', nt);
      setMessages([]); put('studio:thread', []); didInitScrollRef.current = false;
    }
  };

  // ErrorBubble + MediaBubble are defined at MODULE level (above) so the 6s poll never remounts them.
  // Follow-up chips go only on the newest finished video in the thread.
  const latestDoneId = [...messages].reverse().find((x: any) => x.kind === 'video' && x.status === 'ready' && (x.variants || [])[0]?.play_url)?.id;
  // The opt-in preview image goes only on the newest dry-run plan (the server previews that one).
  const latestDryId = [...messages].reverse().find((x: any) => x.kind === 'video' && x.status === 'dry_run')?.id;

  // Each message springs in (subtle rise + fade) and re-layouts smoothly as siblings appear.
  const msgAnim = {
    layout: true,
    initial: { opacity: 0, y: 12 },
    animate: { opacity: 1, y: 0 },
    transition: { type: 'spring' as const, stiffness: 320, damping: 30 },
  };

  return (
    <div className="flex gap-3 flex-1 min-h-0">
      {/* conversations sidebar — past Studio chats, ChatGPT/Claude style. Compact; hidden on mobile. */}
      <aside className="hidden md:flex w-52 shrink-0 flex-col rounded-2xl border border-border bg-card/40 overflow-hidden">
        <button onClick={newChat}
          className="m-2 inline-flex items-center justify-center gap-1.5 rounded-xl border border-border bg-card px-3 py-2 text-sm text-gray-200 hover:bg-accent">
          <MessageSquare className="w-3.5 h-3.5" /> New chat
        </button>
        <div className="flex-1 overflow-y-auto overscroll-contain px-2 pb-2 space-y-1">
          {!threads.length ? (
            <div className="px-2 py-3 text-[11px] text-gray-500">No conversations yet.</div>
          ) : threads.map((t: any) => (
            <ThreadRow key={t.thread_id} t={t} active={t.thread_id === threadId}
              onOpen={() => openThread(t.thread_id)}
              onRename={(title) => renameThread(t.thread_id, title)}
              onDelete={() => deleteThread(t.thread_id)} />
          ))}
        </div>
      </aside>

      {/* main chat column */}
      <div className="flex flex-col flex-1 min-h-0">
      <div className="flex items-center justify-between mb-2 shrink-0">
        <div className="text-xs text-gray-500">Studio — ask for scripts, ad copy, or a video. It remembers, so you can say "make a video from that script".</div>
        <button onClick={newChat} className="text-xs text-gray-400 hover:text-gray-200 inline-flex items-center gap-1"><MessageSquare className="w-3.5 h-3.5" /> New chat</button>
      </div>

      {/* thread */}
      <div ref={scrollRef} style={{ WebkitOverflowScrolling: 'touch' }} className="flex-1 min-h-0 overflow-y-auto overscroll-contain space-y-4 pr-1">
        {!messages.length ? (
          <div className="h-full flex flex-col items-center justify-center text-center text-gray-400">
            <span className="flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/15 text-primary mb-4"><Sparkles className="w-8 h-8" /></span>
            <div className="text-lg font-medium text-gray-200">What do you want to make?</div>
            <div className="text-sm mt-1 max-w-md">Try "write me 5 hook scripts for auto insurance", then "make a video from the 3rd one". Your thread is saved.</div>
          </div>
        ) : messages.map((m: any) => {
          if (m.role === 'user') return (
            <motion.div key={m.id} {...msgAnim} className="flex flex-col items-end">
              <div className="max-w-2xl rounded-2xl bg-primary text-primary-foreground px-4 py-2.5 text-sm whitespace-pre-wrap">
                {/* Reference image(s) attached to this turn → shown next to the prompt so the thread reads
                    "this image + this script → this video". Persists across reloads (durable proxy url). */}
                {Array.isArray(m.data?.image_urls) && m.data.image_urls.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {m.data.image_urls.map((u: string, i: number) => (
                      <img key={i} src={u} alt="reference" className="w-16 h-16 rounded-lg object-cover ring-1 ring-white/25" loading="lazy" />
                    ))}
                  </div>
                )}
                {/* Recreate This: the winning clip being cloned, shown on the "Recreate this" turn. */}
                {m.data?.recreate_source_url && (
                  <div className="mb-2 flex items-center gap-2">
                    <div className="w-16 h-[100px] shrink-0 rounded-lg overflow-hidden ring-1 ring-white/25">
                      <MediaThumb url={m.data.recreate_source_url} className="w-full h-full object-cover" />
                    </div>
                    {m.data?.recreate_label && <span className="text-[11px] opacity-90 truncate">{m.data.recreate_label}</span>}
                  </div>
                )}
                {m.text}
              </div>
              <Stamp at={m.created_at} />
            </motion.div>
          );
          if (m.kind === 'script' || m.kind === 'ad_copy') return (
            <motion.div key={m.id} {...msgAnim} className="flex flex-col items-start"><div className="max-w-2xl rounded-2xl border border-border bg-card px-4 py-3">
              <div className="flex items-center gap-1.5 text-[11px] font-medium text-primary mb-1.5">
                <FileText className="w-3.5 h-3.5" /> {m.data?.title || (m.kind === 'script' ? 'Script' : 'Ad copy')}
              </div>
              <pre className="text-sm text-gray-200 whitespace-pre-wrap font-sans leading-relaxed">{m.text}</pre>
              {m.kind === 'script' && (
                <button onClick={() => post('make a video from that script')} disabled={sending}
                  className="mt-2.5 inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg bg-primary/15 text-primary hover:bg-primary/25 disabled:opacity-40">
                  <Video className="w-3.5 h-3.5" /> Make a video from this
                </button>
              )}
            </div><Stamp at={m.created_at} /></motion.div>
          );
          // A BLOCKED media message that still has a viewable render → MediaBubble (shows it, flagged with
          // the gate reasons) so the user can view what's failing. Only an OUTPUT-LESS failure / error text
          // falls to ErrorBubble.
          const hasViewableOutput = (m.kind === 'video' || m.kind === 'image') && !!(m.variants || [])[0]?.play_url;
          if ((m.status === 'failed' || m.kind === 'error') && !hasViewableOutput) return (
            <motion.div key={m.id} {...msgAnim} className="flex flex-col items-start"><ErrorBubble m={m} /><Stamp at={m.created_at} /></motion.div>
          );
          if (m.kind === 'video' || m.kind === 'image') return (
            <motion.div key={m.id} {...msgAnim} className="flex flex-col items-start"><MediaBubble m={m} onRunReal={runReal} onEdit={editFromPreview} onReview={() => load()} runningReal={realRunId === m.id}
              onFollowUp={m.id === latestDoneId ? (t: string) => post(t) : undefined} followUpBusy={sending}
              onPreviewImage={m.id === latestDryId && !sending ? () => post('show me a preview image') : undefined} /><Stamp at={m.created_at} /></motion.div>
          );
          // Gate choice-buttons (additive): when a text message carries a non-empty `choices` array,
          // render each as a pill that sends its `send` string through the SAME `post` path the composer
          // uses (so model / dropdown / dry-run state is preserved exactly). Only the newest message is
          // live — clicking appends a user bubble (this stops being last → buttons vanish), and an
          // already-answered gate loaded from the server is never last, so buttons can't be re-clicked.
          const choices = Array.isArray(m.data?.choices) ? m.data.choices : [];
          const isLastMsg = messages[messages.length - 1]?.id === m.id;
          return (
            <motion.div key={m.id} {...msgAnim} className="flex flex-col items-start">
              <div className="max-w-2xl rounded-2xl border border-border bg-card px-4 py-2.5 text-sm text-gray-200 whitespace-pre-wrap">{m.text}</div>
              {choices.length > 0 && isLastMsg && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {choices.map((c: any, i: number) => (
                    <button key={i} onClick={() => post(String(c.send))} disabled={sending}
                      className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg bg-primary/15 text-primary hover:bg-primary/25 disabled:opacity-40">
                      {c.label}
                    </button>
                  ))}
                </div>
              )}
              <Stamp at={m.created_at} />
            </motion.div>
          );
        })}
        {/* Keep the thinking indicator alive until the assistant actually answers — not just
            for the ~1s dispatch round-trip. `sending` covers the immediate send; `waitingReply`
            (last message is still the user's) keeps it up through the async generation until the
            server appends a reply (script/text/running-video/error), at which point MediaBubble's
            own "generating…" spinner takes over for the video itself. Self-heals on every poll. */}
        {/* KIMI-STYLE THINKING AVATAR — stays from the first processing tick until the final artifact
            is delivered. `sending` = the dispatch round-trip; `waitingReply` = the async office is
            still writing the script/reply; video/image `running` = the media itself is rendering.
            The moment the script/image/video lands (status → ready/failed) `processing` flips false
            and the avatar settles out. Self-heals on every 6s poll. */}
        {(() => {
          const videoRunning = messages.some((mm: any) => mm.kind === 'video' && mm.status === 'running');
          const imageRunning = messages.some((mm: any) => mm.kind === 'image' && mm.status === 'running');
          // waitingReply is the SHORT window between the user's send and the office storing a reply
          // (script/text lands in seconds; a video job flips the last message to an assistant 'video'
          // bubble almost immediately). Time-bound it so a silent server failure — where no reply is
          // ever stored and the poll keeps returning last-msg=user — can't hang the avatar forever.
          const _last = messages[messages.length - 1];
          const waitingReply = !!_last && _last.role === 'user'
            && (!_last.created_at || (Date.now() - new Date(_last.created_at).getTime()) < 4 * 60 * 1000);
          const processing = sending || waitingReply || videoRunning || imageRunning;
          if (!processing) return null;
          const label = videoRunning ? 'Brewing your video'
            : imageRunning ? 'Rendering your image'
            : waitingReply ? 'Thinking'
            : 'Working on it';
          return (
            <motion.div {...msgAnim} className="flex justify-start items-center gap-3">
              <ThinkingAvatar state={videoRunning || imageRunning ? 'brewing' : 'thinking'} />
              <div className="rounded-2xl border border-border bg-card px-4 py-2.5 text-sm text-gray-400 inline-flex items-center gap-2">
                <span className="pith-dots">{label}<i>.</i><i>.</i><i>.</i></span>
                {videoRunning && <span className="text-gray-500">· a video can take a few minutes</span>}
              </div>
              <style>{`
                .pith-dots i { opacity:0; animation: pith-dot 1.4s infinite; }
                .pith-dots i:nth-child(2){ animation-delay:.2s } .pith-dots i:nth-child(3){ animation-delay:.4s }
                @keyframes pith-dot { 0%,80%,100%{ opacity:0 } 40%{ opacity:1 } }
                @media (prefers-reduced-motion: reduce){ .pith-dots i{ animation:none; opacity:1 } }
              `}</style>
            </motion.div>
          );
        })()}
      </div>

      {/* composer */}
      <div className="mt-2 shrink-0">
        {err && <div className="text-xs text-red-600 mb-2">{err}</div>}
        <div className="group rounded-2xl border border-border bg-card/80 backdrop-blur-xl shadow-xl p-2 space-y-2 transition-shadow focus-within:border-primary/50 focus-within:ring-2 focus-within:ring-primary/30">
          <textarea ref={taRef} value={prompt} onChange={e => setPrompt(e.target.value)} rows={1}
            onKeyDown={e => {
              if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); send(); }
            }}
            placeholder="Message Studio… e.g. 'write 5 hook scripts for auto insurance' or 'make a video of a woman explaining $59/mo' (Enter to send · Shift+Enter for a new line). Attach a character image + a script → an end-to-end video."
            className="w-full bg-transparent text-sm resize-none focus:outline-none placeholder:text-gray-500 py-1 leading-6" />
          {/* #9 accept an image (character/style ref) OR a video (same-person video-ref lock, new script) */}
          <input ref={refImageRef} type="file" accept="image/*,video/*" className="hidden"
            onChange={e => setRefImage(e.target.files?.[0] || null)} />
          {refImage && (() => {
            const isVid = /^video\//i.test(refImage.type) || /\.(mp4|mov|webm|m4v|avi)$/i.test(refImage.name);
            return (
              <div className="flex items-center gap-2 text-[11px] text-gray-600 dark:text-gray-300">
                {isVid
                  ? <video src={URL.createObjectURL(refImage)} muted className="w-9 h-9 rounded object-cover border border-border" />
                  : <img src={URL.createObjectURL(refImage)} alt="character reference" className="w-9 h-9 rounded object-cover border border-border" />}
                <span className="truncate max-w-[220px]">{isVid
                  ? (advanced ? 'Reference video · same person, new script' : 'Your video · same person, new script')
                  : (advanced ? 'Character reference' : 'Your character photo')} · {refImage.name}</span>
                <button onClick={() => { setRefImage(null); if (refImageRef.current) refImageRef.current.value = ''; }}
                  title="Remove reference" className="p-0.5 rounded hover:bg-accent"><X className="w-3.5 h-3.5" /></button>
              </div>
            );
          })()}
          {/* ⚙ OPTIONS — collapsed by default. All the fine-grained controls (engine, model, duration,
              aspect, path, res/audio, captions, improve-hook, Enhance) live here so the bottom row stays
              a clean 3-control action bar and the chat thread keeps the reclaimed height. Expands inline
              above the action row (the composer is pinned at the bottom, so it grows UPWARD into the thread). */}
          {advanced && optionsOpen && (
            <div className="flex items-center gap-1.5 flex-wrap border-t border-border/60 pt-2">
              <EngineSelect value={engine} onChange={setEngine} />
              {/* SEEDANCE MODEL — the Seedance version. Only relevant when the engine is Seedance (or Auto,
                  which usually routes to Seedance), so it's hidden for Veo/Kling/Wan/Imagen/Venice to remove
                  the "two pickers that look like they do the same thing" confusion. */}
              {(engine === 'auto' || engine === 'seedance' || engine === 'fal-seedance') && (
              <div className="flex items-center gap-1.5 pl-1.5 pr-1 py-0.5 rounded-xl border border-border bg-card">
                <span className="text-[11px] text-gray-500">Seedance version</span>
                <select value={seedanceModel} onChange={e => setSeedanceModel(e.target.value)}
                  className="bg-transparent text-[11px] text-gray-200 focus:outline-none cursor-pointer">
                  <option value="">Seedance 2.0 · up to 15s/clip</option>
                  <option value="bytedance/seedance-2-5">Seedance 2.5 · 30s single-shot</option>
                  <option value="google/gemini-omni-flash-1-1">Gemini Omni 1.1 Flash · 10s/clip · native audio</option>
                </select>
              </div>
              )}
              {/* Explicit capability + cost difference for the selected model. */}
              {seedanceModel === 'bytedance/seedance-2-5' && (
                <span className="w-full order-last text-[10px] text-amber-400/90">
                  Seedance 2.5 — one continuous {seconds || 30}s take (no clip seams), up to 1080p · est. ~$
                  {((({ '480p': 0.158, '720p': 0.315, '1080p': 0.570 } as Record<string, number>)[resolution] || 0.315) * (seconds || 30)).toFixed(2)}
                  {' '}at {resolution} (official Kie rate · ≈2.5× Seedance 2.0).
                </span>
              )}
              {seedanceModel === 'google/gemini-omni-flash-1-1' && (
                <span className="w-full order-last text-[10px] text-amber-400/90">
                  Gemini Omni 1.1 Flash — native audio, ~{seconds || 10}s per clip, resolutions 360p / 720p / 1080p / 4k · cost is an estimate.
                </span>
              )}
              {/* Duration + Aspect */}
              <div className="flex items-center gap-1.5 pl-1.5 pr-1 py-0.5 rounded-xl border border-border bg-card">
                <span className="text-[11px] text-gray-500">Duration</span>
                <input type="number" min={4} max={seedanceModel === 'google/gemini-omni-flash-1-1' ? 10 : seedanceModel === 'bytedance/seedance-2-5' ? 30 : (engine === 'seedance' ? 15 : 148)} value={seconds || ''} placeholder="Auto"
                  onChange={e => setSeconds(parseInt(e.target.value) || 0)}
                  title="Leave blank for Auto — the video length is sized from the script. Set a number to cap it."
                  className="w-12 text-sm rounded-lg border border-border bg-card px-2 py-0.5 text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" />
                <span className="text-[11px] text-gray-500">{seconds ? 's' : ''}</span>
              </div>
              <div className="flex items-center gap-1.5 pl-1.5 pr-1 py-0.5 rounded-xl border border-border bg-card">
                <span className="text-[11px] text-gray-500">Aspect</span>
                <select value={aspect} onChange={e => setAspect(e.target.value)} className="text-sm rounded-lg border border-border bg-card px-1.5 py-0.5">
                  {['9:16', '16:9', '1:1', '4:3', '3:4', '21:9'].map(a => <option key={a} value={a}>{a}</option>)}
                </select>
              </div>
              {/* Path: force the generation lane. Auto = reuse a real library clip only when it matches the
                  brief, else generate from scratch. Scratch = always text-to-video. Avatar = always reuse a real clip. */}
              <div className="flex items-center gap-1.5 pl-1.5 pr-1 py-0.5 rounded-xl border border-border bg-card" title="Auto: use a real avatar clip only when it matches your brief, else generate from scratch. From scratch: always text-to-video. Real avatar: always reuse a matching library clip.">
                <span className="text-[11px] text-gray-500">Path</span>
                <select value={genPath} onChange={e => setGenPath(e.target.value)} className="text-sm rounded-lg border border-border bg-card px-1.5 py-0.5">
                  <option value="auto">Auto</option>
                  <option value="scratch">From scratch (prompt → video)</option>
                  <option value="avatar">Real avatar</option>
                </select>
              </div>
              {engine === 'seedance' && (
                <div className="flex items-center gap-1.5 pl-1.5 pr-1 py-0.5 rounded-xl border border-border bg-card">
                  <span className="text-[11px] text-gray-500">Res</span>
                  <select value={effectiveResolution} onChange={e => setResolution(e.target.value)} className="text-sm rounded-lg border border-border bg-card px-1.5 py-0.5">
                    {resChoices.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
              )}
              {engine === 'seedance' && (
                <label className="flex items-center gap-1.5 text-[11px] text-gray-500"><input type="checkbox" checked={audio} onChange={e => setAudio(e.target.checked)} /> audio</label>
              )}
              {/* Captions is an explicit choice for every video — clear intent, no silent guessing */}
              <label className="flex items-center gap-1.5 text-[11px] text-gray-500" title="Burn subtitles onto the video"><input type="checkbox" checked={captions} onChange={e => setCaptions(e.target.checked)} /> captions</label>
              {/* Rewrite toggle: ON = the creative office may sharpen the hook; OFF = speak my script word-for-word */}
              <label className="flex items-center gap-1.5 text-[11px] text-gray-500" title="ON: ORBIT can improve your hook. OFF: your script is spoken exactly as written — no rewriting, no invented names/numbers."><input type="checkbox" checked={improveHook} onChange={e => setImproveHook(e.target.checked)} /> improve hook</label>
              <button onClick={enhance} disabled={enhancing || !prompt.trim()}
                title="Rewrite your message into a richer, engine-optimized prompt"
                className="px-2.5 py-1.5 rounded-xl border border-border text-sm text-gray-600 hover:bg-accent disabled:opacity-40 inline-flex items-center gap-1">
                {enhancing ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Sparkles className="w-4 h-4" /> Enhance</>}
              </button>
            </div>
          )}
          {/* Bottom action row — only THREE always-visible controls (Dry/Real toggle · attach · Send),
              plus the ⚙ Options accordion trigger on the left. */}
          <div className="flex items-center flex-wrap gap-2 border-t border-border/60 pt-2">
            {advanced ? (
              <button type="button" onClick={() => setOptionsOpen(o => !o)}
                title="Model, duration, aspect, path, captions, hook rewrite + Enhance"
                className="inline-flex items-center gap-1 px-1.5 py-1 rounded-lg text-[11px] text-gray-400 hover:text-gray-200 hover:bg-accent">
                <Settings className="w-3.5 h-3.5" /> Options {optionsOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronUp className="w-3.5 h-3.5" />}
              </button>
            ) : (
              <div className="inline-flex items-center rounded-xl border border-border bg-card p-0.5" role="group" aria-label="Video shape">
                {([['9:16', 'Vertical'], ['1:1', 'Square'], ['16:9', 'Wide']] as const).map(([v, label]) => (
                  <button key={v} type="button" onClick={() => setAspect(v)} aria-pressed={aspect === v}
                    className={`rounded-lg px-2 py-1 text-xs font-medium transition ${aspect === v ? 'bg-primary/15 text-primary' : 'text-gray-400 hover:text-gray-200'}`}>
                    {label}
                  </button>
                ))}
              </div>
            )}
            <button type="button" onClick={toggleAdvanced} aria-pressed={advanced}
              title={advanced ? 'Hide the technical controls' : 'Show model, duration, resolution and other technical controls'}
              className="px-1.5 py-1 rounded-lg text-[11px] text-gray-500 hover:text-gray-200 hover:bg-accent">
              {advanced ? 'Simple' : 'Advanced'}
            </button>
            <div className="ml-auto flex items-center gap-2">
              {/* GENERATE / ESTIMATE COST — Generate (default) makes the video. Estimate cost sends the SAME
                  request with dry_run:true → a plan card (plan + exact spoken lines + cost, nothing spent). */}
              <div className="inline-flex items-center rounded-xl border border-border bg-card p-0.5"
                title="Generate makes the video now (the backend still confirms before any large spend). Estimate cost shows the plan + exact spoken lines + price and spends nothing.">
                <button type="button" onClick={() => setDryRun(false)}
                  className={`inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium transition ${!dryRun ? 'bg-primary/15 text-primary' : 'text-gray-400 hover:text-gray-200'}`}>
                  <Play className="w-3 h-3 fill-current" /> Generate
                </button>
                <button type="button" onClick={() => setDryRun(true)}
                  className={`inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium transition ${dryRun ? 'bg-primary/15 text-primary' : 'text-gray-400 hover:text-gray-200'}`}>
                  <FlaskConical className="w-3.5 h-3.5" /> {advanced ? 'Estimate cost' : 'Estimate first (free)'}
                </button>
              </div>
              {/* REVIEW-FIRST — render the clips then pause so you approve/regenerate before paying to combine. */}
              {advanced && <button type="button" onClick={() => setReviewFirst(v => !v)}
                title="Render the clips first and review them — approve to combine, or regenerate just the bad ones (saves credits)"
                className={`px-2.5 py-1.5 rounded-xl border text-sm inline-flex items-center gap-1 ${reviewFirst ? 'border-amber-400/60 text-amber-300 bg-amber-400/10' : 'border-border text-gray-500 hover:bg-accent'}`}>
                <Film className="w-4 h-4" /> Review clips{reviewFirst ? ' ✓' : ''}
              </button>}
              {/* The picker has always accepted video too (same-person video-ref lock, new script),
                  but it was labelled "Image" with an image icon — so nobody knew the reference-video
                  path existed. Label what it actually does. */}
              <button onClick={() => refImageRef.current?.click()}
                title="Attach a reference — an IMAGE (character/style → image-to-video) or a VIDEO (same person, new script)"
                className={`px-2.5 py-1.5 rounded-xl border text-sm inline-flex items-center gap-1 ${refImage ? 'border-primary/50 text-primary bg-primary/10' : 'border-border text-gray-600 hover:bg-accent'}`}>
                <ImageIcon className="w-4 h-4" />
                {refImage
                  ? ((/^video\//i.test(refImage.type) || /\.(mp4|mov|webm|m4v|avi)$/i.test(refImage.name)) ? 'Video ✓' : 'Image ✓')
                  : 'Image / Video'}
              </button>
              <motion.button
                type="button" onClick={send} disabled={sending || !prompt.trim()}
                whileHover={{ scale: prompt.trim() && !sending ? 1.05 : 1 }}
                whileTap={{ scale: prompt.trim() && !sending ? 0.92 : 1 }}
                transition={{ type: 'spring', stiffness: 500, damping: 20 }}
                title={dryRun ? 'Estimate the plan + cost — nothing is spent' : 'Generate the video'}
                className="inline-flex items-center gap-1.5 rounded-xl bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground shadow-lg shadow-primary/25 hover:bg-primary/90 disabled:opacity-40 disabled:shadow-none">
                {sending ? <Loader2 className="w-4 h-4 animate-spin" />
                  : dryRun ? <><FlaskConical className="w-3.5 h-3.5" /> {advanced ? 'Estimate cost' : 'Estimate first (free)'}</>
                    : <><Play className="w-3.5 h-3.5 fill-current" /> Generate video</>}
              </motion.button>
            </div>
          </div>
        </div>
        <div className="text-[11px] text-gray-400 text-center mt-1">{STUDIO_ENGINES.find(e => e.id === engine)?.hint}</div>
      </div>
      </div>
    </div>
  );
}

// ── Create from Assets (scenic images + script → end-to-end video) ────────────
function CreateFromAssets({ onSubmitted }: { onSubmitted: () => void }) {
  const [files, setFiles] = useState<File[]>([]);
  const [script, setScript] = useState('');
  const [vertical, setVertical] = useState('');   // no domain-specific default — user picks (was hardcoded 'home_insurance')
  const [model, setModel] = useState('');
  const [voiceover, setVoiceover] = useState(true);
  const [aspect, setAspect] = useState('9:16');
  const [resolution, setResolution] = useState('480p');
  const [seconds, setSeconds] = useState(0);   // 0 = Auto → runtime sized from the script
  const [busy, setBusy] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState<{ summary?: string; cost_usd?: number } | null>(null);
  const [aiBusy, setAiBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [verticals, setVerticals] = useState<string[]>(['home_insurance', 'bizop', 'refinance']);
  useEffect(() => {
    verticalsApi.getAll().then(r => {
      const list = (r.data.data || r.data || []).map((v: any) => (typeof v === 'string' ? v : v.name || v.vertical || v.slug)).filter(Boolean);
      if (list.length) { setVerticals(list); setVertical(list[0]); }
    }).catch(() => {});
  }, []);

  // Build the shared FormData for both preview (dry run) and the real submit — resolution, aspect and
  // seconds ride along so the backend renders at the chosen settings (Auto seconds = omit the field).
  const buildForm = (dryRun: boolean) => {
    const fd = new FormData();
    files.forEach(f => fd.append('images', f));
    fd.append('script', script.trim());
    fd.append('vertical', vertical);
    fd.append('do_voiceover', String(voiceover));
    fd.append('aspect_ratio', aspect);
    fd.append('resolution', resolution);
    if (seconds > 0) fd.append('seconds', String(seconds));
    if (model) fd.append('model', model);
    if (dryRun) fd.append('dry_run', 'true');
    return fd;
  };

  const runPreview = async () => {
    if (!files.length || !script.trim()) { setMsg({ ok: false, text: 'Add at least one image and a script.' }); return; }
    setPreviewing(true); setMsg(null); setPreview(null);
    try {
      const r = await regenApi.fromAssets(buildForm(true));
      setPreview({ summary: r.data?.summary || r.data?.plan, cost_usd: r.data?.cost_usd ?? r.data?.cost });
    } catch (e: any) {
      setMsg({ ok: false, text: e?.response?.data?.error || 'Could not preview the cost.' });
    } finally { setPreviewing(false); }
  };

  const submit = async () => {
    if (!files.length || !script.trim()) { setMsg({ ok: false, text: 'Add at least one image and a script.' }); return; }
    setBusy(true); setMsg(null);
    try {
      await regenApi.fromAssets(buildForm(false));
      setMsg({ ok: true, text: 'Building your video — track it in Requests + watch the Creative Team Room.' });
      setTimeout(onSubmitted, 1200);
    } catch (e: any) {
      setMsg({ ok: false, text: e?.response?.data?.error || 'Failed to start.' });
    } finally { setBusy(false); }
  };

  // Draft a script with the same script-writer the rest of the app uses
  // (creative-team enhance, mode 'script'). Seeds from the vertical + whatever the
  // user has already typed, then drops the result straight into the textarea.
  const generateScript = async () => {
    setAiBusy(true); setMsg(null);
    try {
      const seed = script.trim()
        ? `Turn this into a short spoken UGC video ad script: ${script.trim()}`
        : `Write a short, punchy UGC-style video ad script for ${vertical.replace(/_/g, ' ')}. Each sentence is one scene beat spoken as the voiceover.`;
      const r = await regenApi.enhancePrompt({ prompt: seed, mode: 'script', vertical });
      const text = r.data?.text?.trim();
      if (text) setScript(text);
      else setMsg({ ok: false, text: 'AI returned an empty script — try again.' });
    } catch {
      setMsg({ ok: false, text: 'Could not generate a script — write one or use the example.' });
    } finally { setAiBusy(false); }
  };

  const ready = files.length > 0 && !!script.trim();
  const scenes = script.split(/[.!?]\s/).filter(s => s.trim()).length;
  const move = (from: number, to: number) => setFiles(f => { const n = [...f]; const [x] = n.splice(from, 1); n.splice(to, 0, x); return n; });
  const Step = ({ n, title, extra }: { n: number; title: string; extra?: React.ReactNode }) => (
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center gap-2">
        <span className="w-6 h-6 rounded-full bg-primary text-white text-xs flex items-center justify-center font-bold">{n}</span>
        <h3 className="font-semibold text-sm text-gray-900 dark:text-white">{title}</h3>
      </div>
      {extra}
    </div>
  );
  return (
    <div className="max-w-2xl mx-auto space-y-4">
      <div className="rounded-xl border border-border bg-card p-4">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white"><Clapperboard className="inline w-3.5 h-3.5 mr-1 align-[-2px]" /> Create a video from your own scenes</h2>
        <p className="text-sm text-gray-500 mt-1">Bring <b>scenic images + a script</b> → ORBIT animates each scene (image→video), narrates it, captions it, and stitches a finished ad. Just follow the 3 steps.</p>
      </div>

      {/* Step 1 — scenes */}
      <div className="rounded-xl border border-border bg-card p-4">
        <Step n={1} title="Add your scenes" extra={<span className="text-[11px] text-gray-400">{files.length}/8 images</span>} />
        <label onDragOver={e => e.preventDefault()}
          onDrop={e => { e.preventDefault(); setFiles(Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/')).slice(0, 8)); }}
          className="flex flex-col items-center justify-center gap-1 border-2 border-dashed border-border rounded-lg p-6 cursor-pointer hover:border-primary/60 hover:bg-accent/40 transition text-center">
          <ImageIcon className="w-6 h-6 text-gray-400" />
          <span className="text-sm text-gray-600 dark:text-gray-300">Drag &amp; drop images here, or <span className="text-primary font-medium">browse</span></span>
          <span className="text-[11px] text-gray-400">JPG/PNG · up to 8 · each image becomes one scene</span>
          <input type="file" accept="image/*" multiple className="hidden" onChange={e => setFiles(Array.from(e.target.files || []).slice(0, 8))} />
        </label>
        {!!files.length && (
          <div className="flex flex-wrap gap-2.5 mt-3">
            {files.map((f, i) => (
              <div key={i} className="relative group">
                <img src={URL.createObjectURL(f)} alt="" className="w-20 h-28 object-cover rounded-xl border border-border" />
                <span className="absolute top-1 left-1 w-5 h-5 rounded-md bg-black/70 text-white text-[10px] font-semibold flex items-center justify-center">{i + 1}</span>
                <button onClick={() => setFiles(files.filter((_, j) => j !== i))} title="Remove scene"
                  className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center"><X className="w-3 h-3" /></button>
                <div className="absolute bottom-1 inset-x-1 flex justify-between opacity-0 group-hover:opacity-100 transition">
                  <button disabled={i === 0} onClick={() => move(i, i - 1)} title="Move earlier"
                    className="w-5 h-5 rounded-md bg-black/70 text-white flex items-center justify-center disabled:opacity-30"><ChevronLeft className="w-3 h-3" /></button>
                  <button disabled={i === files.length - 1} onClick={() => move(i, i + 1)} title="Move later"
                    className="w-5 h-5 rounded-md bg-black/70 text-white flex items-center justify-center disabled:opacity-30"><ChevronRight className="w-3 h-3" /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Step 2 — script */}
      <div className="rounded-xl border border-border bg-card p-4">
        <Step n={2} title="Write the script" extra={
          <div className="flex items-center gap-3">
            <button onClick={generateScript} disabled={aiBusy}
              className="text-[11px] text-primary hover:underline inline-flex items-center gap-1 disabled:opacity-50">
              {aiBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
              {aiBusy ? 'Generating…' : 'Generate with AI'}
            </button>
            <button onClick={() => setScript("If you own a home in Texas, this could save you hundreds. My neighbor just cut her insurance bill in half. Here's how you check your rate in two minutes. Tap below, enter your zip, and see what comes up.")}
              className="text-[11px] text-primary hover:underline">insert example</button>
          </div>} />
        <textarea value={script} onChange={e => setScript(e.target.value)} rows={6}
          placeholder="Each sentence becomes a scene beat, spoken as the voiceover…"
          className="w-full rounded-lg border border-border bg-transparent p-3 text-sm" />
        <div className="flex items-center gap-2 mt-1.5 flex-wrap">
          <span className="text-[11px] px-2 py-0.5 rounded-full bg-primary/15 text-primary font-medium">{scenes} scene{scenes !== 1 ? 's' : ''} detected</span>
          {files.length > 0 && scenes !== files.length && (
            <span className="text-[11px] text-amber-500">· {files.length} image{files.length !== 1 ? 's' : ''} added — one sentence ≈ one scene</span>
          )}
        </div>
      </div>

      {/* Step 3 — options + create */}
      <div className="rounded-xl border border-border bg-card p-4">
        <Step n={3} title="Options & create" />
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Vertical</label>
            <select value={vertical} onChange={e => setVertical(e.target.value)} className="w-full rounded-lg border border-border bg-card p-2 text-sm">
              <option value="">Choose a vertical…</option>
              {verticals.map(v => <option key={v} value={v}>{v.replace(/_/g, ' ')}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Model</label>
            <select value={model} onChange={e => setModel(e.target.value)} className="w-full rounded-lg border border-border bg-card p-2 text-sm">
              {GEN_MODELS.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
            </select>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3 mb-3">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Aspect ratio</label>
            <select value={aspect} onChange={e => { setAspect(e.target.value); setPreview(null); }} className="w-full rounded-lg border border-border bg-card p-2 text-sm">
              {['9:16', '16:9', '1:1', '4:3', '3:4', '21:9'].map(a => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Resolution</label>
            <select value={resolution} onChange={e => { setResolution(e.target.value); setPreview(null); }} className="w-full rounded-lg border border-border bg-card p-2 text-sm">
              {['480p', '720p', '1080p', '4k'].map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Duration</label>
            <input type="number" min={0} value={seconds || ''} placeholder="Auto"
              onChange={e => { setSeconds(parseInt(e.target.value) || 0); setPreview(null); }}
              title="Leave blank for Auto — the length is sized from the script. Set a number of seconds to cap it."
              className="w-full rounded-lg border border-border bg-card p-2 text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" />
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm mb-3">
          <input type="checkbox" checked={voiceover} onChange={e => setVoiceover(e.target.checked)} />
          Add voiceover narration (TTS of the script)
        </label>
        {preview && (
          <div className="mb-3 rounded-lg border border-primary/40 bg-primary/5 p-3 text-sm">
            <div className="flex items-center gap-1.5 font-medium text-primary"><FlaskConical className="w-4 h-4" /> Dry run — nothing spent</div>
            {typeof preview.cost_usd === 'number' && <div className="mt-1 text-gray-300">Estimated cost: <b className="text-emerald-300">${preview.cost_usd.toFixed(2)}</b></div>}
            {preview.summary && <div className="mt-1 text-gray-400 whitespace-pre-wrap">{preview.summary}</div>}
          </div>
        )}
        <div className="flex gap-2">
          <button onClick={runPreview} disabled={previewing || busy || !ready}
            className="flex-1 py-2.5 rounded-lg border border-border text-sm font-semibold disabled:opacity-50 flex items-center justify-center gap-2 hover:bg-accent">
            {previewing ? <><Loader2 className="w-4 h-4 animate-spin" />Previewing…</> : <><FlaskConical className="w-4 h-4" />Preview (dry run)</>}
          </button>
          <button onClick={submit} disabled={busy || previewing || !ready}
            className="flex-1 py-2.5 rounded-lg bg-primary text-white text-sm font-semibold disabled:opacity-50 flex items-center justify-center gap-2">
            {busy ? <><Loader2 className="w-4 h-4 animate-spin" />Starting…</> : <><Wand2 className="w-4 h-4" />Create Video</>}
          </button>
        </div>
        {!ready && <p className="text-[11px] text-gray-400 mt-2 text-center">{!files.length ? 'Add at least one image' : 'Write a script'} to enable Create.</p>}
        {msg && (
          <div className={`mt-3 text-sm flex items-center gap-1 ${msg.ok ? 'text-green-600' : 'text-red-600'}`}>
            {msg.ok ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}{msg.text}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Create Video tab shell — the scene-based flow plus three sibling modes
// (Storyboard, Audio only, Audio lipsync). One place to make any ad, keeps each
// mode a small self-contained component. ────────────────────────────────────────
function CreateVideo({ onSubmitted }: { onSubmitted: () => void }) {
  const [mode, setMode] = useState<'scenes' | 'storyboard' | 'images' | 'audio' | 'lipsync'>('scenes');
  const modes = [
    ['scenes', 'From scenes'], ['storyboard', 'Storyboard'], ['images', 'Storyboard Images'],
    ['audio', 'Audio only'], ['lipsync', 'Audio lipsync'],
  ] as const;
  return (
    <div className="max-w-2xl mx-auto">
      <div className="flex flex-wrap items-center gap-1 mb-4">
        {modes.map(([k, label]) => (
          <button key={k} onClick={() => setMode(k)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
              mode === k ? 'bg-primary/15 text-primary border-primary/40' : 'text-gray-400 border-transparent hover:bg-white/5'}`}
          >{label}</button>
        ))}
      </div>
      {mode === 'scenes' ? <CreateFromAssets onSubmitted={onSubmitted} />
        : mode === 'storyboard' ? <Storyboard onSubmitted={onSubmitted} />
        : mode === 'images' ? <StoryboardImages />
        : mode === 'audio' ? <AudioOnly onSubmitted={onSubmitted} />
        : <AudioLipsyncUpload onSubmitted={onSubmitted} />}
    </div>
  );
}

// Voice picker — populated from GET /regen/voices (same catalog the Avtar Studio uses).
function VoicePicker({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [voices, setVoices] = useState<any[]>([]);
  useEffect(() => { regenApi.listVoices().then(r => setVoices(r.data.voices || [])).catch(() => {}); }, []);
  return (
    <select value={value} onChange={e => onChange(e.target.value)} className="w-full rounded-lg border border-border bg-card p-2 text-sm">
      <option value="">Auto (cast a voice for me)</option>
      {voices.map(v => <option key={v.id} value={v.id}>{v.name} · {v.gender}/{v.age_band} · {v.provider}{v.cloned ? ' · cloned' : ''}</option>)}
    </select>
  );
}

// ── Storyboard — click-based board. Character image + script + scene count → plan + cost preview →
// per-scene stills (checkbox thumbnails) → regenerate only the CHECKED panels (others stay locked) →
// render the video. No typed "go" — every step is a button. ──────────────────────────────────────
function Storyboard({ onSubmitted }: { onSubmitted: () => void }) {
  const [charImg, setCharImg] = useState<File | null>(null);
  const [script, setScript] = useState('');
  const [sceneCount, setSceneCount] = useState(4);
  const [stage, setStage] = useState<'input' | 'planned' | 'stills'>('input');
  const [spec, setSpec] = useState<any>(null);
  const [plan, setPlan] = useState<{ summary?: string; cost_usd?: number } | null>(null);
  const [scenes, setScenes] = useState<any[]>([]);
  const [checked, setChecked] = useState<Record<number, boolean>>({});
  const [busy, setBusy] = useState<'' | 'preview' | 'stills' | 'regen' | 'render'>('');
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const imgRef = useRef<HTMLInputElement>(null);

  const preview = async () => {
    if (!charImg || !script.trim()) { setMsg({ ok: false, text: 'Add a character image and a script.' }); return; }
    setBusy('preview'); setMsg(null);
    try {
      const fd = new FormData();
      fd.append('images', await downscaleImage(charImg));
      fd.append('script', script.trim());
      fd.append('scenes', String(sceneCount));
      const r = await regenApi.storyboardPreview(fd);
      setSpec(r.data?.spec ?? r.data);
      setPlan({ summary: r.data?.summary || r.data?.plan, cost_usd: r.data?.cost_usd ?? r.data?.cost });
      setStage('planned');
    } catch (e: any) { setMsg({ ok: false, text: e?.response?.data?.error || 'Could not build the plan.' }); }
    finally { setBusy(''); }
  };

  const genStills = async () => {
    setBusy('stills'); setMsg(null);
    try {
      const r = await regenApi.storyboardStills({ spec });
      setScenes(r.data?.scenes || []); setChecked({});
      if (r.data?.spec) setSpec(r.data.spec);
      setStage('stills');
    } catch (e: any) { setMsg({ ok: false, text: e?.response?.data?.error || 'Could not generate stills.' }); }
    finally { setBusy(''); }
  };

  const regenSelected = async () => {
    const panels = scenes.map((_, i) => i).filter(i => checked[i]);
    if (!panels.length) { setMsg({ ok: false, text: 'Check the panels you want to regenerate.' }); return; }
    setBusy('regen'); setMsg(null);
    try {
      const r = await regenApi.storyboardRegenerate({ spec, scenes, panels });
      if (r.data?.scenes) setScenes(r.data.scenes);
      if (r.data?.spec) setSpec(r.data.spec);
      setChecked({});
    } catch (e: any) { setMsg({ ok: false, text: e?.response?.data?.error || 'Regenerate failed.' }); }
    finally { setBusy(''); }
  };

  const render = async () => {
    setBusy('render'); setMsg(null);
    try {
      await regenApi.storyboardRender({ spec, scenes });
      setMsg({ ok: true, text: 'Rendering your video — track it in Requests / ORBIT Generated.' });
      setTimeout(onSubmitted, 1400);
    } catch (e: any) { setMsg({ ok: false, text: e?.response?.data?.error || 'Render failed.' }); }
    finally { setBusy(''); }
  };

  // Scene +/- : before stills it sets the plan count; in the stills stage it adds a blank panel
  // (auto-checked so "Regenerate selected" fills it) or drops the last one — others stay locked.
  const addPanel = () => {
    if (stage === 'stills') setScenes(s => { const i = s.length; setChecked(c => ({ ...c, [i]: true })); return [...s, { index: i, placeholder: true }]; });
    else setSceneCount(n => Math.min(n + 1, 12));
  };
  const removePanel = () => {
    if (stage === 'stills') setScenes(s => s.length > 1 ? s.slice(0, -1) : s);
    else setSceneCount(n => Math.max(n - 1, 1));
  };
  const count = stage === 'stills' ? scenes.length : sceneCount;
  const thumbOf = (sc: any) => sc?.thumb_url || sc?.url || sc?.image_url || sc?.play_url;

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-border bg-card p-4">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white"><Film className="inline w-3.5 h-3.5 mr-1 align-[-2px]" /> Storyboard a video, panel by panel</h2>
        <p className="text-sm text-gray-500 mt-1">Give a <b>character image + a script</b>, preview the plan &amp; cost, generate a <b>still per scene</b>, then regenerate only the panels you don't like before rendering the final video.</p>
      </div>

      {/* Step 1 — inputs */}
      <div className="rounded-xl border border-border bg-card p-4 space-y-3">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Character reference</label>
          <input ref={imgRef} type="file" accept="image/*" className="hidden" onChange={e => setCharImg(e.target.files?.[0] || null)} />
          <div className="flex items-center gap-3">
            <button onClick={() => imgRef.current?.click()} disabled={stage !== 'input'}
              className="px-3 py-2 rounded-lg border border-border text-sm hover:bg-accent inline-flex items-center gap-1.5 disabled:opacity-50">
              <ImageIcon className="w-4 h-4" /> {charImg ? 'Change image' : 'Choose image'}
            </button>
            {charImg && <img src={URL.createObjectURL(charImg)} alt="" className="w-12 h-16 object-cover rounded-lg border border-border" />}
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Script</label>
          <textarea value={script} onChange={e => setScript(e.target.value)} rows={5} disabled={stage !== 'input'}
            placeholder="Each sentence becomes a scene beat…"
            className="w-full rounded-lg border border-border bg-transparent p-3 text-sm disabled:opacity-60" />
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs font-medium text-gray-500">Scenes</span>
          <div className="inline-flex items-center rounded-lg border border-border">
            <button onClick={removePanel} disabled={count <= 1 || !!busy} className="px-2.5 py-1 text-sm hover:bg-accent disabled:opacity-40">−</button>
            <span className="px-3 text-sm tabular-nums">{count}</span>
            <button onClick={addPanel} disabled={!!busy || (stage === 'input' && count >= 12)} className="px-2.5 py-1 text-sm hover:bg-accent disabled:opacity-40">+</button>
          </div>
          {stage === 'input' && (
            <button onClick={preview} disabled={busy === 'preview' || !charImg || !script.trim()}
              className="ml-auto px-4 py-2 rounded-lg bg-primary text-white text-sm font-semibold disabled:opacity-50 inline-flex items-center gap-2">
              {busy === 'preview' ? <><Loader2 className="w-4 h-4 animate-spin" />Planning…</> : <><FlaskConical className="w-4 h-4" />Preview plan</>}
            </button>
          )}
        </div>
      </div>

      {/* Step 2 — plan + cost → generate stills */}
      {stage !== 'input' && (
        <div className="rounded-xl border border-primary/40 bg-primary/5 p-4">
          <div className="flex items-center gap-1.5 font-medium text-primary text-sm"><FlaskConical className="w-4 h-4" /> Plan {plan?.cost_usd != null && <>· est. <b className="text-emerald-300">${plan.cost_usd.toFixed(2)}</b></>}</div>
          {plan?.summary && <div className="mt-1 text-sm text-gray-400 whitespace-pre-wrap">{plan.summary}</div>}
          {stage === 'planned' && (
            <button onClick={genStills} disabled={busy === 'stills'}
              className="mt-3 px-4 py-2 rounded-lg bg-primary text-white text-sm font-semibold disabled:opacity-50 inline-flex items-center gap-2">
              {busy === 'stills' ? <><Loader2 className="w-4 h-4 animate-spin" />Generating stills…</> : <><ImageIcon className="w-4 h-4" />Generate stills</>}
            </button>
          )}
        </div>
      )}

      {/* Step 3 — panels + regenerate selected + render */}
      {stage === 'stills' && (
        <div className="rounded-xl border border-border bg-card p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-sm text-gray-900 dark:text-white">Panels — check the ones to change</h3>
            <span className="text-[11px] text-gray-400">{scenes.filter((_, i) => checked[i]).length} selected</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {scenes.map((sc, i) => {
              const url = thumbOf(sc);
              const on = !!checked[i];
              return (
                <button key={i} type="button" onClick={() => setChecked(c => ({ ...c, [i]: !c[i] }))}
                  className={`relative rounded-lg overflow-hidden border text-left ${on ? 'border-primary ring-2 ring-primary' : 'border-border hover:border-primary/50'}`}>
                  <div className="aspect-[9/16] bg-black flex items-center justify-center">
                    {url ? <img src={url} alt="" className="w-full h-full object-contain" loading="lazy" />
                      : <span className="text-[10px] text-gray-600 px-2 text-center">{sc.placeholder ? 'new panel — regenerate to fill' : 'no still'}</span>}
                  </div>
                  <span className={`absolute top-1 left-1 w-4 h-4 rounded border flex items-center justify-center ${on ? 'bg-primary border-primary text-white' : 'bg-black/50 border-white/60 text-transparent'}`}><Check className="w-3 h-3" /></span>
                  <span className="absolute bottom-0 inset-x-0 px-1 py-0.5 bg-black/70 text-white text-[9px] truncate">Scene {i + 1}{sc.caption ? ` · ${sc.caption}` : ''}</span>
                </button>
              );
            })}
          </div>
          <div className="flex flex-wrap items-center gap-2 mt-4">
            <button onClick={regenSelected} disabled={!!busy || !scenes.some((_, i) => checked[i])}
              className="px-4 py-2 rounded-lg border border-border text-sm font-semibold hover:bg-accent disabled:opacity-50 inline-flex items-center gap-2">
              {busy === 'regen' ? <><Loader2 className="w-4 h-4 animate-spin" />Regenerating…</> : <><RefreshCw className="w-4 h-4" />Regenerate selected</>}
            </button>
            <button onClick={render} disabled={!!busy}
              className="ml-auto px-4 py-2 rounded-lg bg-primary text-white text-sm font-semibold disabled:opacity-50 inline-flex items-center gap-2">
              {busy === 'render' ? <><Loader2 className="w-4 h-4 animate-spin" />Rendering…</> : <><Video className="w-4 h-4" />Generate video</>}
            </button>
          </div>
        </div>
      )}

      {msg && (
        <div className={`text-sm flex items-center gap-1 ${msg.ok ? 'text-green-600' : 'text-red-600'}`}>
          {msg.ok ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}{msg.text}
        </div>
      )}
    </div>
  );
}

// ── Audio only — script + chosen voice → a delivered voiceover mp3 (backend `wantsAudio`). ─────────
function AudioOnly({ onSubmitted }: { onSubmitted: () => void }) {
  const [script, setScript] = useState('');
  const [voiceId, setVoiceId] = useState('');
  const [cloneVoice, setCloneVoice] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const submit = async () => {
    if (!script.trim()) { setMsg({ ok: false, text: 'Write a script to narrate.' }); return; }
    setBusy(true); setMsg(null);
    try {
      await regenApi.generateAudio({ script: script.trim(), voice_id: voiceId || undefined, clone_voice: cloneVoice });
      setMsg({ ok: true, text: 'Generating your voiceover — it appears in ORBIT Generated when ready.' });
      setTimeout(onSubmitted, 1200);
    } catch (e: any) { setMsg({ ok: false, text: e?.response?.data?.error || 'Failed to start.' }); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-border bg-card p-4">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white"><Mic className="inline w-3.5 h-3.5 mr-1 align-[-2px]" /> Generate a voiceover</h2>
        <p className="text-sm text-gray-500 mt-1">A <b>script + a voice</b> → a narrated <b>mp3</b>, no video. Play or download it from ORBIT Generated.</p>
      </div>
      <div className="rounded-xl border border-border bg-card p-4 space-y-3">
        <textarea value={script} onChange={e => setScript(e.target.value)} rows={6}
          placeholder="Type the words to be spoken…"
          className="w-full rounded-lg border border-border bg-transparent p-3 text-sm" />
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Voice</label>
          <VoicePicker value={voiceId} onChange={setVoiceId} />
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={cloneVoice} onChange={e => setCloneVoice(e.target.checked)} />
          Clone the chosen voice
        </label>
        <button onClick={submit} disabled={busy || !script.trim()}
          className="w-full py-2.5 rounded-lg bg-primary text-white text-sm font-semibold disabled:opacity-50 flex items-center justify-center gap-2">
          {busy ? <><Loader2 className="w-4 h-4 animate-spin" />Starting…</> : <><Mic className="w-4 h-4" />Generate audio</>}
        </button>
        {msg && (
          <div className={`text-sm flex items-center gap-1 ${msg.ok ? 'text-green-600' : 'text-red-600'}`}>
            {msg.ok ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}{msg.text}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Audio lipsync — upload an audio file + a video OR image → a lipsynced video. ───────────────────
function AudioLipsyncUpload({ onSubmitted }: { onSubmitted: () => void }) {
  const [audio, setAudio] = useState<File | null>(null);
  const [visual, setVisual] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const audioRef = useRef<HTMLInputElement>(null);
  const visualRef = useRef<HTMLInputElement>(null);
  const visualIsVideo = !!visual && (/^video\//i.test(visual.type) || /\.(mp4|mov|webm|m4v|avi)$/i.test(visual.name));

  const submit = async () => {
    if (!audio || !visual) { setMsg({ ok: false, text: 'Add an audio file and a video or image.' }); return; }
    setBusy(true); setMsg(null);
    try {
      const fd = new FormData();
      fd.append('audio', audio);
      fd.append('visual', visual);   // backend detects video vs image from the file
      await regenApi.lipsyncUpload(fd);
      setMsg({ ok: true, text: 'Lipsyncing — track it in Requests / ORBIT Generated.' });
      setTimeout(onSubmitted, 1200);
    } catch (e: any) { setMsg({ ok: false, text: e?.response?.data?.error || 'Failed to start.' }); }
    finally { setBusy(false); }
  };

  const Picker = ({ file, onPick, inputRef, accept, label, hint }:
    { file: File | null; onPick: (f: File | null) => void; inputRef: React.RefObject<HTMLInputElement | null>; accept: string; label: string; hint: string }) => (
    <div>
      <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
      <input ref={inputRef} type="file" accept={accept} className="hidden" onChange={e => onPick(e.target.files?.[0] || null)} />
      <button onClick={() => inputRef.current?.click()}
        className={`w-full flex flex-col items-center justify-center gap-1 border-2 border-dashed rounded-lg p-5 text-center transition ${file ? 'border-primary/50 bg-primary/5' : 'border-border hover:border-primary/60 hover:bg-accent/40'}`}>
        {label.startsWith('Audio') ? <Mic className="w-5 h-5 text-gray-400" /> : <Video className="w-5 h-5 text-gray-400" />}
        <span className="text-sm text-gray-600 dark:text-gray-300">{file ? file.name : <>Click to choose · <span className="text-primary font-medium">browse</span></>}</span>
        <span className="text-[11px] text-gray-400">{hint}</span>
      </button>
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-border bg-card p-4">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white"><Mic className="inline w-3.5 h-3.5 mr-1 align-[-2px]" /> Lipsync your own audio</h2>
        <p className="text-sm text-gray-500 mt-1">Upload an <b>audio track</b> and a <b>video or image</b> of a person — ORBIT syncs their mouth to your audio.</p>
      </div>
      <div className="rounded-xl border border-border bg-card p-4 space-y-3">
        <Picker file={audio} onPick={setAudio} inputRef={audioRef} accept="audio/*" label="Audio file" hint="MP3 / WAV / M4A" />
        <Picker file={visual} onPick={setVisual} inputRef={visualRef} accept="video/*,image/*" label="Video or image" hint="MP4 / MOV · or a JPG / PNG face" />
        {visual && <div className="text-[11px] text-gray-400">{visualIsVideo ? 'Video reference — lipsyncs the existing clip.' : 'Image reference — animates a talking head.'}</div>}
        <button onClick={submit} disabled={busy || !audio || !visual}
          className="w-full py-2.5 rounded-lg bg-primary text-white text-sm font-semibold disabled:opacity-50 flex items-center justify-center gap-2">
          {busy ? <><Loader2 className="w-4 h-4 animate-spin" />Starting…</> : <><Wand2 className="w-4 h-4" />Lipsync</>}
        </button>
        {msg && (
          <div className={`text-sm flex items-center gap-1 ${msg.ok ? 'text-green-600' : 'text-red-600'}`}>
            {msg.ok ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}{msg.text}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Creative Team Room (live "office" of the AI creative team) ─────────────────
// Roster personas arrive from the backend with an emoji string. We map each to a
// lucide icon at render time (purely visual — backend data is untouched) so no
// emoji glyphs show in the office, drawer, or reports.
function personaIcon(role?: string) {
  const k = (role || '').toLowerCase();
  if (k.includes('creative') && k.includes('director')) return Clapperboard;
  if (k.includes('strateg')) return Brain;
  if (k.includes('script')) return PenLine;
  if (k.includes('character')) return UserCircle;
  if (k.includes('shot')) return Camera;
  if (k.includes('prompt')) return FileText;
  if (k.includes('critic')) return Search;
  if (k.includes('learn')) return GraduationCap;
  if (k.includes('director')) return Megaphone;
  return Users;
}
function PersonaIcon({ role, className }: { role?: string; className?: string }) {
  const Icon = personaIcon(role);
  return <Icon className={className || 'w-5 h-5'} />;
}
interface Desk {
  id: string; role: string; emoji: string; seat: string; desc: string;
  status: 'idle' | 'queued' | 'working'; job_id: string | null; task: string | null;
  busy_ms: number; queued: number;
  last: { t: number; ms: number; ok: boolean; revised: boolean; detail: string } | null;
}
interface FeedItem { t: number; persona: string; event: string; task?: string; ms?: number; detail?: string; job_id?: string; }
interface ReportRow {
  id: string; role: string; emoji: string; runs: number; passes: number; revises: number;
  fails: number; accuracy_pct: number; helpfulness_pct: number | null; avg_ms: number;
  accountability_pct: number; attributed_faults: number; coaching: string[];
}

const STATUS_STYLE: Record<string, { dot: string; ring: string; label: string }> = {
  working: { dot: 'bg-green-500 animate-pulse', ring: 'ring-2 ring-green-400 shadow-lg shadow-green-500/20', label: 'Working' },
  queued: { dot: 'bg-amber-500', ring: 'ring-1 ring-amber-300', label: 'Queued' },
  idle: { dot: 'bg-gray-300', ring: 'ring-1 ring-border', label: 'Idle' },
};

function DeskCard({ d, lead }: { d: Desk; lead?: boolean }) {
  const s = STATUS_STYLE[d.status] || STATUS_STYLE.idle;
  return (
    <div className={`relative rounded-xl bg-white dark:bg-gray-800 p-3 ${s.ring} ${lead ? 'border-2 border-primary/40' : ''}`}
         title={d.desc}>
      {lead && <span className="absolute -top-2 left-3 text-[10px] px-2 py-0.5 rounded-full bg-primary text-white font-semibold">TEAM LEAD</span>}
      <div className="flex items-start gap-2">
        <div className="leading-none text-gray-200 dark:text-gray-100"><PersonaIcon role={d.role} className="w-6 h-6" /></div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className={`w-2 h-2 rounded-full ${s.dot}`} />
            <span className="text-sm font-semibold text-gray-900 dark:text-white truncate">{d.role}</span>
          </div>
          <div className="text-[11px] text-gray-500">{s.label}{d.queued ? ` · ${d.queued} queued` : ''}</div>
        </div>
      </div>
      <div className="mt-2 min-h-[34px] text-[11px]">
        {d.status === 'working' ? (
          <div className="text-gray-700 dark:text-gray-200">
            <span className="text-gray-400">on</span> <span className="font-medium">{d.job_id}</span>
            <div className="italic text-gray-500 line-clamp-3 break-words" title={d.task || ''}>{d.task}</div>
          </div>
        ) : d.last ? (
          <div className="text-gray-500 flex items-start gap-1">
            <span className="shrink-0 mt-0.5">{d.last.ok ? (d.last.revised ? <><PencilRuler className="w-3 h-3 inline" /> revised</> : <><Check className="w-3 h-3 inline" /> done</>) : <><X className="w-3 h-3 inline" /> failed</>} ·</span>
            <span className="line-clamp-3 break-words" title={d.last.detail || ''}>{d.last.detail}</span>
          </div>
        ) : <div className="text-gray-400">—</div>}
      </div>
    </div>
  );
}

const fmtEta = (s: number | null | undefined) => {
  if (s == null) return '—';
  if (s <= 0) return 'done';
  const m = Math.floor(s / 60), ss = s % 60;
  return m ? `${m}m ${ss}s` : `${ss}s`;
};

// Engine dial — concurrency cap, monthly budget ceiling, default quality + live spend
function EngineConfig() {
  const [cfg, setCfg] = useState<any>(null);
  const [saving, setSaving] = useState(false);
  const load = useCallback(() => { regenApi.engineConfig().then(r => setCfg(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [load]);
  const save = (patch: any) => { setSaving(true); regenApi.setEngineConfig(patch).then(() => load()).finally(() => setSaving(false)); };
  if (!cfg) return null;
  return (
    <div className="mb-3 p-3 rounded-xl border border-border bg-card flex flex-wrap items-center gap-4 text-xs">
      <span className="font-semibold text-gray-800 dark:text-gray-100 inline-flex items-center gap-1"><Settings className="w-3.5 h-3.5" /> Engine</span>
      <label className="flex items-center gap-1">Parallel lanes
        <input type="number" min={1} max={30} defaultValue={cfg.concurrency_cap} disabled={saving}
          onBlur={e => save({ concurrency_cap: parseInt(e.target.value) || cfg.concurrency_cap })}
          className="w-14 px-1.5 py-0.5 rounded border border-border bg-transparent" />
        <span className="text-gray-400">({cfg.active} active)</span></label>
      <label className="flex items-center gap-1">Monthly budget $
        <input type="number" min={0} step={10} defaultValue={cfg.monthly_budget_usd} disabled={saving}
          onBlur={e => save({ monthly_budget_usd: parseFloat(e.target.value) || 0 })}
          className="w-20 px-1.5 py-0.5 rounded border border-border bg-transparent" />
        <span className="text-gray-400">(0 = off)</span></label>
      <label className="flex items-center gap-1">Quality
        <select defaultValue={cfg.default_quality} disabled={saving} onChange={e => save({ default_quality: e.target.value })}
          className="px-1.5 py-0.5 rounded border border-border bg-transparent">
          <option value="bulk">bulk (cheapest)</option><option value="premium">premium (sync.so)</option>
        </select></label>
      <span className="ml-auto text-gray-600 dark:text-gray-300"><DollarSign className="inline w-3.5 h-3.5 mr-1 align-[-2px]" /> spent this month: <b>${Number(cfg.month_spend_usd || 0).toFixed(2)}</b>
        {cfg.monthly_budget_usd ? ` / $${cfg.monthly_budget_usd} · $${Number(cfg.budget_remaining_usd || 0).toFixed(2)} left` : ''}</span>
    </div>
  );
}

// Full per-job step log — every persona's durable steps for ONE job, in order
// (time · persona · event · ms · detail). Same durable /audit endpoint the drawer uses,
// but with NO persona filter, so it's the whole team's timeline for the job. Works for
// finished jobs too (the rows live in Postgres, not just in-memory). Also surfaces the
// Learner's per-job decision (QC / ROI / human verdict) for that job.
function JobStepLog({ jobId, label }: { jobId: string; label?: string }) {
  const [events, setEvents] = useState<any[] | null>(null);
  const [learn, setLearn] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    setLoading(true); setEvents(null);
    regenApi.teamAudit(jobId).then(r => { if (alive) setEvents(r.data.events || []); })
      .catch(() => { if (alive) setEvents([]); })
      .finally(() => { if (alive) setLoading(false); });
    regenApi.teamDecision(jobId).then(r => { if (alive) setLearn(r.data.decisions || []); })
      .catch(() => { if (alive) setLearn([]); });
    return () => { alive = false; };
  }, [jobId]);

  return (
    <div className="mb-3 rounded-2xl border border-border bg-white dark:bg-gray-800 p-3">
      <div className="flex items-center gap-2 mb-2">
        <FileText className="w-4 h-4 text-primary" />
        <span className="text-sm font-semibold text-gray-900 dark:text-white truncate">Full step log — {label || jobId}</span>
        {events && <span className="text-[11px] text-gray-400 ml-auto tabular-nums">{events.length} step(s)</span>}
      </div>

      {!!learn.length && (
        <div className="mb-3">
          <div className="text-[11px] font-semibold text-primary mb-1.5 flex items-center gap-1"><GraduationCap className="w-3.5 h-3.5" /> What the Learner learned (this job)</div>
          {learn.map((lr, i) => (
            <div key={i} className="rounded-lg border border-border/60 bg-white/5 p-2.5 text-[11px] mb-2 space-y-1">
              <div className="flex items-center gap-2">
                <span className={`px-1.5 py-0.5 rounded-full font-semibold ${lr.outcome === 'win' ? 'bg-green-500/20 text-green-500' : lr.outcome === 'loss' ? 'bg-red-500/20 text-red-500' : 'bg-white/10 text-gray-400'}`}>{lr.outcome}</span>
                <span className={lr.qc_passed ? 'text-emerald-500' : 'text-red-500'}>QC {lr.qc_passed ? 'passed' : 'blocked'}</span>
                {lr.roi != null && <span className="text-gray-300">ROI {Number(lr.roi).toFixed(2)}</span>}
              </div>
              {lr.qc_reasons && <div className="text-gray-400 break-words">QC: {lr.qc_reasons}</div>}
              {lr.human_verdict && <div className="text-gray-300">Human verdict: <b>{lr.human_verdict}</b>{lr.human_reason ? ` — ${lr.human_reason}` : ''}</div>}
            </div>
          ))}
        </div>
      )}

      {loading ? <div className="text-[11px] text-gray-400 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> loading the team's steps…</div>
        : !events?.length ? <div className="text-[11px] text-gray-400">No recorded steps for this job (older jobs pre-date the durable audit log, or this recipe didn't run the team).</div>
        : (
          <ol className="space-y-1 max-h-[440px] overflow-auto pr-1">
            {events.map((e, i) => (
              <li key={i} className="text-[11px] flex gap-2 items-start border-b border-border/40 pb-1">
                <span className="text-gray-400 tabular-nums whitespace-nowrap">{e.at ? new Date(e.at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }) : ''}</span>
                <span className="font-medium text-gray-700 dark:text-gray-200 w-32 shrink-0 truncate">{e.role || e.persona}</span>
                <span className={`w-24 shrink-0 ${e.event === 'done' ? 'text-emerald-600' : e.event === 'fail' || e.event === 'job_fail' ? 'text-red-600' : e.event === 'revise' || e.event === 'coached' ? 'text-amber-600' : 'text-gray-500'}`}>
                  {e.revised ? 'revised' : e.event}{e.ms ? ` · ${(e.ms / 1000).toFixed(1)}s` : ''}
                </span>
                <span className="text-gray-500 flex-1 break-words">{e.detail || e.task || ''}</span>
              </li>
            ))}
          </ol>
        )}
    </div>
  );
}

function CreativeTeamRoom() {
  const [view, setView] = useState<'pixel' | 'office' | 'reports' | 'ledger'>('pixel');
  // "Discuss in team room" from a result card → jump to the room with a meeting already bound
  // to that request + clip, so the meeting is ABOUT something instead of free-floating.
  const [discuss, setDiscuss] = useState<{ creativeRef?: string; nonce: number } | null>(null);
  useEffect(() => {
    const onDiscuss = (e: Event) => {
      const d = (e as CustomEvent).detail || {};
      if (d.jobId) setSelJob(d.jobId);
      setView('pixel');
      setDiscuss({ creativeRef: d.creativeRef, nonce: Date.now() });
      roomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };
    window.addEventListener('orbit:discuss', onDiscuss);
    return () => window.removeEventListener('orbit:discuss', onDiscuss);
  }, []);
  const roomRef = useRef<HTMLDivElement>(null);
  const [desks, setDesks] = useState<Desk[]>([]);
  const [feed, setFeed] = useState<FeedItem[]>([]);
  const [reports, setReports] = useState<ReportRow[]>([]);
  const [jobList, setJobList] = useState<any[]>([]);      // switcher: all recent jobs w/ progress+eta
  const [selJob, setSelJob] = useState<string | null>(null);   // which job the room is showing
  const [cur, setCur] = useState<any>(null);              // the selected job's live snapshot
  const [err, setErr] = useState('');
  const [logPersona, setLogPersona] = useState<string | null>(null);   // work-log drawer
  const [logEvents, setLogEvents] = useState<any[]>([]);
  const [learnRows, setLearnRows] = useState<any[]>([]);   // Learner's per-job signal (C)
  // Actual $ spent per job (AE's reconciled cost ledger) for the shown job. job_id = CL request_id.
  const [jobCost, setJobCost] = useState<Record<string, number | null>>({});
  useEffect(() => {
    const id = selJob || cur?.job_id;
    if (!id || id in jobCost) return;
    regenApi.creationCosts(id)
      .then((r: any) => { const t = r?.data?.total_usd; setJobCost(c => ({ ...c, [id]: typeof t === 'number' ? t : null })); })
      .catch(() => setJobCost(c => ({ ...c, [id]: null })));
  }, [selJob, cur, jobCost]);
  // ── Cost gate: when a job trips the spend-confirmation threshold, the office reports it as a
  // COST_CONFIRM_REQUIRED failure. Show an admin-only Confirm/Cancel control right here. The office
  // keys each job by its CL request_id (AE begin_job(req.request_id)), so cur.job_id is the id the
  // confirm-cost/cancel endpoints take. Both endpoints are admin-gated server-side too.
  const { user } = useAuth();
  const isAdmin = isRegenAdmin(user?.email);
  const [costBusy, setCostBusy] = useState<Record<string, 'confirm' | 'cancel' | undefined>>({});
  const [costDismissed, setCostDismissed] = useState<Record<string, boolean>>({});
  // Cost-gate "Adjust" box: admin types a shorten instruction → re-cost (dry-run) → new script + lower $.
  const [adjustText, setAdjustText] = useState<Record<string, string>>({});
  const [adjustBusy, setAdjustBusy] = useState<Record<string, boolean>>({});
  const [adjustRes, setAdjustRes] = useState<Record<string, { new_script: string; projected_usd: number; detail: string; old_projected_usd: number } | undefined>>({});
  const selRef = React.useRef<string | null>(null);
  selRef.current = selJob;

  useEffect(() => {
    if (!logPersona) { setLogEvents([]); setLearnRows([]); return; }
    const jid = cur?.job_id || selJob;
    if (!jid) { setLogEvents([]); setLearnRows([]); return; }
    regenApi.teamAudit(jid, logPersona).then(r => setLogEvents(r.data.events || [])).catch(() => setLogEvents([]));
    // For the Learner, also pull what it recorded for THIS job (QC / ROI / human verdict).
    if (logPersona === 'learner') regenApi.teamDecision(jid).then(r => setLearnRows(r.data.decisions || [])).catch(() => setLearnRows([]));
    else setLearnRows([]);
  }, [logPersona, cur, selJob]);

  const pull = useCallback(async () => {
    if (typeof document !== 'undefined' && document.hidden) return;   // don't poll a hidden tab
    try {
      const jl = (await regenApi.teamJobs()).data.jobs || [];
      setJobList(jl);
      // default selection: keep current if still present, else first running, else newest
      let sel = selRef.current;
      if (!sel || !jl.some((j: any) => j.job_id === sel)) {
        sel = (jl.find((j: any) => j.status === 'running') || jl[0])?.job_id || null;
        setSelJob(sel);
      }
      const a = await regenApi.teamActivity(sel || undefined);
      setCur(a.data); setDesks(a.data.desks || []); setFeed(a.data.feed || []);
      setErr('');
    } catch (e: any) {
      if (e?.response?.status !== 429) setErr('unreachable');   // 429 = transient
    }
  }, []);
  const pullReports = useCallback(async () => {
    if (typeof document !== 'undefined' && document.hidden) return;
    try { const r = await regenApi.teamReports(); setReports(r.data.rows || []); } catch { /* noop */ }
  }, []);

  useEffect(() => {
    pull(); pullReports();
    const t2 = setInterval(pullReports, 20000);
    return () => { clearInterval(t2); };
  }, [pull, pullReports]);

  // ── LIVE room: SSE pushes state the moment it changes, so walks, handoffs and meetings
  // actually animate instead of jumping every few seconds. The poll stays as a FALLBACK and
  // only runs while the stream is down (blocked proxy, older deploy), so we never double-load.
  const [streamLive, setStreamLive] = useState(false);
  useEffect(() => {
    let es: EventSource | null = null;
    let fallback: ReturnType<typeof setInterval> | null = null;
    let dead = false;

    const startFallback = () => {
      if (fallback || dead) return;
      fallback = setInterval(pull, 6000);
    };
    const stopFallback = () => { if (fallback) { clearInterval(fallback); fallback = null; } };

    try {
      es = new EventSource(regenApi.teamStreamUrl(selJob || undefined));
      es.addEventListener('room', (ev: MessageEvent) => {
        try {
          const d = JSON.parse(ev.data);
          setCur(d); setDesks(d.desks || []); setFeed(d.feed || []);
          if (Array.isArray(d.jobs)) setJobList(d.jobs);
          setErr('');
          if (!dead) { setStreamLive(true); stopFallback(); }
        } catch { /* ignore a malformed frame */ }
      });
      es.onerror = () => { setStreamLive(false); startFallback(); };
    } catch {
      startFallback();
    }

    return () => { dead = true; stopFallback(); try { es?.close(); } catch { /* noop */ } };
  }, [selJob, pull]);
  // when the user picks a different job, refresh its room immediately
  useEffect(() => { if (selJob) regenApi.teamActivity(selJob).then(a => { setCur(a.data); setDesks(a.data.desks || []); setFeed(a.data.feed || []); }).catch(() => {}); }, [selJob]);

  const lead = desks.find(d => d.id === 'director');
  const rest = desks.filter(d => d.id !== 'director');
  const anyBusy = desks.some(d => d.status === 'working');
  const running = jobList.filter(j => j.status === 'running');

  return (
    <div>
      <EngineConfig />
      {logPersona && (() => {
        const d = desks.find(x => x.id === logPersona);
        return (
          <div className="fixed inset-0 z-50 flex justify-end" onClick={() => setLogPersona(null)}>
            <div className="absolute inset-0 bg-black/40" />
            <div className="relative w-full max-w-md h-full bg-card border-l border-border shadow-2xl overflow-auto p-4" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-1">
                <h3 className="font-semibold text-gray-900 dark:text-white inline-flex items-center gap-1.5"><PersonaIcon role={d?.role || String(logPersona)} className="w-5 h-5" /> {(d?.role || logPersona)} — work log</h3>
                <button onClick={() => setLogPersona(null)} className="text-gray-400 hover:text-gray-700"><X className="w-5 h-5" /></button>
              </div>
              {d?.desc && <p className="text-xs text-gray-500 mb-3">{d.desc}</p>}
              {logPersona === 'learner' && (
                <div className="mb-3">
                  <div className="text-[11px] font-semibold text-primary mb-1.5 flex items-center gap-1"><GraduationCap className="w-3.5 h-3.5" /> What the Learner learned (this job)</div>
                  {!learnRows.length ? (
                    <p className="text-[11px] text-gray-400">No learning signal recorded for this job yet — the Learner logs the QC gate at render, then fills in ROI + the human verdict once they arrive.</p>
                  ) : learnRows.map((lr, i) => (
                    <div key={i} className="rounded-lg border border-border/60 bg-white/5 p-2.5 text-[11px] mb-2 space-y-1">
                      <div className="flex items-center gap-2">
                        <span className={`px-1.5 py-0.5 rounded-full font-semibold ${lr.outcome === 'win' ? 'bg-green-500/20 text-green-500' : lr.outcome === 'loss' ? 'bg-red-500/20 text-red-500' : 'bg-white/10 text-gray-400'}`}>{lr.outcome}</span>
                        <span className={lr.qc_passed ? 'text-emerald-500' : 'text-red-500'}>QC {lr.qc_passed ? 'passed' : 'blocked'}</span>
                        {lr.roi != null && <span className="text-gray-300">ROI {Number(lr.roi).toFixed(2)}</span>}
                      </div>
                      {lr.qc_reasons && <div className="text-gray-400 break-words">QC: {lr.qc_reasons}</div>}
                      {lr.human_verdict && <div className="text-gray-300">Human verdict: <b>{lr.human_verdict}</b>{lr.human_reason ? ` — ${lr.human_reason}` : ''}</div>}
                      <div className="text-gray-500 break-words">cast: {lr.character_gender || '—'}/{lr.character_age || '—'} · voice {lr.voice_provider || '—'}{lr.voice_cloned ? ' (cloned)' : ''}{lr.video_model ? ` · model ${lr.video_model}` : ''}</div>
                    </div>
                  ))}
                </div>
              )}
              {!logEvents.length ? <p className="text-xs text-gray-400">No recorded steps for this job yet — run a generation and click a role to see exactly what it did.</p> :
                <ol className="space-y-2">
                  {logEvents.map((e: any, i: number) => (
                    <li key={i} className="text-[11px] border-b border-border/50 pb-1.5">
                      <div className="flex items-center gap-1.5">
                        <span className="text-gray-400 tabular-nums">{e.at ? new Date(e.at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }) : ''}</span>
                        <span className={`font-medium ${e.event === 'done' ? 'text-emerald-600' : e.event === 'fail' || e.event === 'job_fail' ? 'text-red-600' : e.event === 'revise' || e.event === 'coached' ? 'text-amber-600' : 'text-gray-700 dark:text-gray-200'}`}>{e.event}</span>
                        {e.ms ? <span className="text-gray-400">· {(e.ms / 1000).toFixed(1)}s</span> : null}
                      </div>
                      {(e.task || e.detail) && <div className="text-gray-600 dark:text-gray-300 break-words mt-0.5">{e.task || e.detail}</div>}
                    </li>
                  ))}
                </ol>}
            </div>
          </div>
        );
      })()}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <button onClick={() => setView('pixel')}
            className={`px-3 py-1.5 text-sm rounded-lg ${view === 'pixel' ? 'bg-primary text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-600'}`}><Building2 className="inline w-3.5 h-3.5 mr-1 align-[-2px]" /> Office</button>
          <button onClick={() => setView('reports')}
            className={`px-3 py-1.5 text-sm rounded-lg ${view === 'reports' ? 'bg-primary text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-600'}`}><BarChart3 className="inline w-3.5 h-3.5 mr-1 align-[-2px]" /> Reports</button>
          <button onClick={() => setView('ledger')}
            className={`px-3 py-1.5 text-sm rounded-lg ${view === 'ledger' ? 'bg-primary text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-600'}`}><DollarSign className="inline w-3.5 h-3.5 mr-1 align-[-2px]" /> Ledger</button>
        </div>
        <div className="text-xs text-gray-500 flex items-center gap-2">
          <span className={`w-2 h-2 rounded-full ${running.length ? 'bg-green-500 animate-pulse' : 'bg-gray-300'}`} />
          {running.length ? `${running.length} job(s) running` : 'team idle'}
        </div>
      </div>
      {err && !desks.length && <div className="mb-3 text-xs text-amber-600 flex items-center gap-1"><AlertTriangle className="w-3 h-3" />Reconnecting to the creative engine…</div>}

      {/* ── Job switcher: pick which job the room shows (one room, switch jobs) ── */}
      {!!jobList.length && (
        <div className="flex items-center gap-2 mb-3 overflow-x-auto pb-1">
          {jobList.slice(0, 8).map(j => (
            <button key={j.job_id} onClick={() => setSelJob(j.job_id)}
              title={j.label}
              className={`shrink-0 text-[11px] px-2.5 py-1 rounded-lg border flex items-center gap-1.5 ${selJob === j.job_id ? 'border-primary bg-primary/10 text-primary' : 'border-border text-gray-600 hover:bg-accent'}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${j.status === 'running' ? 'bg-green-500 animate-pulse' : j.status === 'failed' ? 'bg-red-500' : 'bg-gray-400'}`} />
              <span className="max-w-[160px] truncate">{j.label}</span>
              <span className="tabular-nums text-gray-400">{j.progress}%</span>
            </button>
          ))}
        </div>
      )}

      {/* ── Headline: which job is showing + live progress bar + ETA ── */}
      {cur && cur.job_id && view !== 'reports' && (
        <div className="mb-3 p-3 rounded-xl border border-border bg-card">
          <div className="flex items-center justify-between mb-1.5">
            <div className="text-sm font-semibold text-gray-900 dark:text-white truncate">
              Showing: {cur.label || cur.job_id}
            </div>
            <div className="text-xs text-gray-500 tabular-nums">
              {cur.status === 'running' ? <>{cur.progress}% · ETA {fmtEta(cur.eta_sec)}</>
                : cur.status === 'failed' ? <span className="text-red-600">failed</span>
                : <span className="text-green-600">done</span>}
              {(cur.job_id in jobCost && jobCost[cur.job_id] != null) && (
                <span className="ml-2 text-gray-400">· 💰 ${jobCost[cur.job_id]!.toFixed(2)} spent</span>
              )}
            </div>
          </div>
          <div className="h-2 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
            <div className={`h-full transition-all duration-700 ${cur.status === 'failed' ? 'bg-red-500' : cur.status === 'done' ? 'bg-green-500' : 'bg-primary'}`}
              style={{ width: `${cur.progress || 0}%` }} />
          </div>
          {cur.status === 'failed' && cur.error && (() => {
            // COST GATE — the engine stopped BEFORE spending because this job is over the confirmation
            // threshold. Show the real number + Confirm/Cancel. Admin-only (buttons + endpoints).
            const cm = String(cur.error).match(/COST_CONFIRM_REQUIRED\|([\d.]+)\|([^\n]*)/);
            const rid = cur.job_id;   // office keys each job by its CL request_id
            if (cm && rid && !costDismissed[rid]) {
              const amount = cm[1];
              const detail = (cm[2] || '').split('—')[0].trim();
              const busy = costBusy[rid];
              // "Adjust" result (if the admin shortened the script + re-costed): the Confirm button then
              // spends the NEW lower amount, and Confirm re-runs the persisted shorter script.
              const adj = adjustRes[rid];
              const abusy = adjustBusy[rid];
              const spendAmt = adj ? adj.projected_usd.toFixed(2) : amount;
              const spendDetail = adj ? adj.detail : detail;
              return (
                <div className="mt-2 rounded-xl border border-amber-500/40 bg-amber-500/10 px-3 py-2.5">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 text-amber-500" />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-amber-200">
                        This generation will cost {adj
                          ? <>only <span className="line-through opacity-60 font-normal">${amount}</span> <span className="text-green-300">${spendAmt}</span></>
                          : <>about ${amount}</>}
                      </div>
                      <div className="text-[11px] text-gray-300 mt-0.5">Nothing has been spent yet{spendDetail ? ` — ${spendDetail}` : ''}.</div>
                      {adj && (
                        <div className="mt-2 rounded-lg border border-border bg-black/20 p-2">
                          <div className="text-[10px] uppercase tracking-wide text-gray-400 mb-1">New (shorter) script</div>
                          <div className="text-[11px] text-gray-200 whitespace-pre-wrap max-h-32 overflow-y-auto">{adj.new_script}</div>
                        </div>
                      )}
                      {isAdmin ? (
                        <>
                        <div className="mt-2 flex items-center gap-2">
                          <button
                            disabled={!!busy || !!abusy}
                            onClick={() => {
                              setCostBusy(b => ({ ...b, [rid]: 'confirm' }));
                              regenApi.confirmCost(rid)
                                .then(() => { setCostDismissed(d => ({ ...d, [rid]: true })); pull(); })
                                .catch(() => window.alert('Could not start it.'))
                                .finally(() => setCostBusy(b => ({ ...b, [rid]: undefined })));
                            }}
                            className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg bg-amber-500 text-black font-medium hover:bg-amber-400 disabled:opacity-50">
                            {busy === 'confirm' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <DollarSign className="w-3.5 h-3.5" />}
                            Confirm &amp; spend ${spendAmt}
                          </button>
                          <button
                            disabled={!!busy || !!abusy}
                            onClick={() => {
                              setCostBusy(b => ({ ...b, [rid]: 'cancel' }));
                              regenApi.cancel(rid).catch(() => {}).finally(() => {
                                setCostDismissed(d => ({ ...d, [rid]: true }));
                                setCostBusy(b => ({ ...b, [rid]: undefined }));
                                pull();
                              });
                            }}
                            className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg border border-border text-gray-300 hover:bg-white/5 disabled:opacity-50">
                            {busy === 'cancel' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <X className="w-3.5 h-3.5" />}
                            Cancel
                          </button>
                        </div>
                        <div className="mt-2 flex items-center gap-2">
                          <input
                            value={adjustText[rid] || ''}
                            onChange={e => setAdjustText(t => ({ ...t, [rid]: e.target.value }))}
                            onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                            placeholder="Adjust — e.g. 'shorten to under 35s, same message'"
                            disabled={!!busy || !!abusy}
                            className="flex-1 min-w-0 text-xs px-2.5 py-1.5 rounded-lg bg-black/30 border border-border text-gray-100 placeholder:text-gray-500 focus:outline-none focus:ring-1 focus:ring-amber-500/50 disabled:opacity-50" />
                          <button
                            disabled={!!busy || !!abusy || !(adjustText[rid] || '').trim()}
                            onClick={() => {
                              const ins = (adjustText[rid] || '').trim();
                              if (!ins) return;
                              setAdjustBusy(b => ({ ...b, [rid]: true }));
                              regenApi.adjustScript(rid, ins)
                                .then(r => setAdjustRes(s => ({ ...s, [rid]: r.data })))
                                .catch(() => window.alert('Could not re-cost that.'))
                                .finally(() => setAdjustBusy(b => ({ ...b, [rid]: false })));
                            }}
                            className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg border border-amber-500/50 text-amber-200 hover:bg-amber-500/10 disabled:opacity-50 shrink-0">
                            {abusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
                            Adjust &amp; re-cost
                          </button>
                        </div>
                        </>
                      ) : (
                        <div className="mt-1.5 text-[11px] text-gray-400">Only an admin can approve this spend.</div>
                      )}
                    </div>
                  </div>
                </div>
              );
            }
            return <div className="mt-1.5 text-[11px] text-red-600 break-words flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> {cur.error}</div>;
          })()}
        </div>
      )}

      {view === 'ledger' ? (
        <LedgerPanel />
      ) : view !== 'reports' ? (
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-4">
          {/* The room */}
          {view === 'pixel' ? (
            !desks.length
              ? <div className="rounded-2xl border border-border p-8"><Centered><Loader2 className="w-4 h-4 animate-spin" />loading…</Centered></div>
              : <div ref={roomRef}>
                  <TeamRoomPanel desks={desks as any} feed={feed} cur={cur} jobId={selJob || undefined}
                    live={streamLive} creativeRef={discuss?.creativeRef}
                    autoMeeting={discuss?.nonce}
                    onSelectPersona={setLogPersona} onRefresh={() => pull()} />
                </div>
          ) : (
          <div className="rounded-2xl bg-gradient-to-b from-slate-50 to-slate-100 dark:from-gray-900 dark:to-gray-950 border border-border p-4">
            {lead && <div className="mb-4 max-w-sm mx-auto"><DeskCard d={lead} lead /></div>}
            <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3">
              {rest.map(d => <DeskCard key={d.id} d={d} />)}
            </div>
            {!desks.length && <Centered><Loader2 className="w-4 h-4 animate-spin" />loading the room…</Centered>}
          </div>
          )}
          {/* Live feed — each entry: time · persona · event on one row, full detail wrapping below */}
          <div className="rounded-2xl border border-border bg-white dark:bg-gray-800 p-3">
            <div className="text-xs font-semibold text-gray-700 dark:text-gray-200 mb-2">Live feed</div>
            <div className="space-y-2 max-h-[560px] overflow-auto">
              {feed.map((f, i) => (
                <div key={i} className="text-[11px] leading-snug border-b border-border/50 pb-1.5">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-gray-400 tabular-nums whitespace-nowrap">{new Date(f.t * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })}</span>
                    <span className="font-semibold text-gray-800 dark:text-gray-100">{f.persona}</span>
                    <span className={f.event === 'done' ? 'text-green-600' : f.event === 'fail' ? 'text-red-600' : f.event === 'revise' || f.event === 'coached' ? 'text-amber-600' : 'text-gray-500'}>
                      {f.event}{f.ms ? ` · ${(f.ms / 1000).toFixed(1)}s` : ''}
                    </span>
                  </div>
                  {(f.task || f.detail) && <div className="text-gray-500 dark:text-gray-400 break-words">{f.task || f.detail}</div>}
                </div>
              ))}
              {!feed.length && <div className="text-[11px] text-gray-400">no activity yet</div>}
            </div>
          </div>
        </div>
      ) : (
        <>
        {/* Per-job step log for the selected job (finished or running) — the full
            chronological record of what every persona did on THIS job. */}
        {selJob && <JobStepLog jobId={selJob} label={jobList.find(j => j.job_id === selJob)?.label} />}
        <div className="text-[11px] text-gray-400 mb-1.5 px-1">All-time persona scorecard (across every job) — click a row to drill into that role's steps.</div>
        <div className="rounded-2xl border border-border bg-white dark:bg-gray-800 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900 text-gray-500 text-[11px] uppercase">
              <tr>
                <th className="text-left px-3 py-2">Persona</th>
                <th className="text-right px-3 py-2">Runs</th>
                <th className="text-left px-3 py-2 w-40">Accountability</th>
                <th className="text-left px-3 py-2 w-36">Helpfulness</th>
                <th className="text-right px-3 py-2">Faults</th>
                <th className="text-left px-3 py-2 w-56">Latest coaching (1:1)</th>
                <th className="text-right px-3 py-2">Avg</th>
              </tr>
            </thead>
            <tbody>
              {reports.map(r => (
                <tr key={r.id} onClick={() => setLogPersona(r.id)}
                  className="border-t border-border align-top cursor-pointer hover:bg-accent/50"
                  title="See this role's step-by-step log for the selected job">
                  <td className="px-3 py-2 font-medium text-gray-900 dark:text-white"><span className="inline-flex items-center gap-1.5"><PersonaIcon role={r.role} className="w-4 h-4 text-gray-400" /> {r.role}</span></td>
                  <td className="px-3 py-2 text-right tabular-nums text-gray-700 dark:text-gray-200">{r.runs}</td>
                  <td className="px-3 py-2"><Bar pct={r.accountability_pct} color={r.accountability_pct >= 80 ? 'bg-green-500' : r.accountability_pct >= 50 ? 'bg-amber-500' : 'bg-red-500'} /></td>
                  <td className="px-3 py-2">{r.helpfulness_pct == null ? <span className="text-gray-400 text-xs">n/a</span> : <Bar pct={r.helpfulness_pct} color="bg-blue-500" />}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-red-600">{r.attributed_faults}</td>
                  <td className="px-3 py-2 text-[11px] text-gray-500">
                    {r.coaching?.length ? <ul className="list-disc pl-4 space-y-0.5">{r.coaching.slice(0, 2).map((c, i) => <li key={i}>{c}</li>)}</ul> : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-gray-500">{r.avg_ms ? `${(r.avg_ms / 1000).toFixed(1)}s` : '—'}</td>
                </tr>
              ))}
              {!reports.length && <tr><td colSpan={7}><Centered>no runs recorded yet — run a regeneration</Centered></td></tr>}
            </tbody>
          </table>
        </div>
        </>
      )}
    </div>
  );
}

function LedgerPanel() {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    try { const r = await regenApi.financeLedger(); setData(r.data); } catch { /* noop */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);
  if (loading && !data) return <div className="rounded-2xl border border-border p-8"><Centered><Loader2 className="w-4 h-4 animate-spin" />loading ledger…</Centered></div>;
  const items: any[] = data?.items || [];
  const byKey: any[] = data?.by_key || [];
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-sm font-semibold text-gray-700 dark:text-gray-200">Finance ledger — spend per model + API key</div>
        <div className="text-sm text-gray-600 dark:text-gray-300">Grand total: <b>${Number(data?.grand_total_usd || 0).toFixed(2)}</b></div>
      </div>
      {/* per-API-key roll-up cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
        {byKey.map((k) => (
          <div key={k.api_key} className="rounded-xl border border-border p-3 bg-white dark:bg-gray-800">
            <div className="text-[11px] text-gray-500 truncate" title={k.api_key}>{k.api_key}</div>
            <div className="text-lg font-semibold text-gray-900 dark:text-white">${Number(k.total_usd || 0).toFixed(2)}</div>
            <div className="text-[11px] text-gray-400 tabular-nums">{k.calls} calls</div>
          </div>
        ))}
      </div>
      {/* per provider+model rows */}
      <div className="rounded-2xl border border-border bg-white dark:bg-gray-800 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-900 text-gray-500 text-[11px] uppercase">
            <tr>
              <th className="text-left px-3 py-2">Provider / model</th>
              <th className="text-left px-3 py-2">API key</th>
              <th className="text-right px-3 py-2">Spend</th>
              <th className="text-right px-3 py-2">Generations</th>
              <th className="text-right px-3 py-2">Calls</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it, i) => (
              <tr key={i} className="border-t border-border">
                <td className="px-3 py-2"><span className="font-medium text-gray-900 dark:text-white">{it.provider}</span>{it.model ? <span className="text-gray-400"> · {it.model}</span> : ''}</td>
                <td className="px-3 py-2 text-[11px] text-gray-500">{it.api_key}</td>
                <td className="px-3 py-2 text-right tabular-nums text-gray-800 dark:text-gray-100">${Number(it.total_usd || 0).toFixed(4)}</td>
                <td className="px-3 py-2 text-right tabular-nums text-gray-700 dark:text-gray-200">{it.generations}</td>
                <td className="px-3 py-2 text-right tabular-nums text-gray-500">{it.calls}</td>
              </tr>
            ))}
            {!items.length && <tr><td colSpan={5}><Centered>no spend recorded yet — run a generation</Centered></td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Bar({ pct, color }: { pct: number; color: string }) {
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-2 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
        <div className={`h-full ${color}`} style={{ width: `${Math.max(0, Math.min(100, pct))}%` }} />
      </div>
      <span className="text-xs tabular-nums text-gray-600 dark:text-gray-300 w-10 text-right">{pct.toFixed(0)}%</span>
    </div>
  );
}
